import React, { Component, useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import { Table } from 'reactstrap';
import TableRows, { getGroupKey } from './TableRows';
import Loader from '../../components/Loader/Loader';
import { withAuthorization } from '../../components/Session';
import { withFirebase } from '../../components/Firebase';
import { EDIT_AUDIO } from '../../constants/routes';
import { getOrgCollectionPath, AUDIOMETAPATH } from '../../constants/paths';
import './trackTable.scss';
import { useTracksStore } from '../../store/tracks.store';
import { usePublicTracksStore } from '../../store/publicTracks.store';
import { useExcludedChipsStore } from '../../store/excludedChips.store';
import Chatbot from '../../components/Chatbot/Chatbot';
import TestMUITags from '../../components/TestMUITags';
import styles from './FindAudio.module.css';
import { APP_CONFIG } from '../../config';
import PlaylistsToggle from '../../components/PlaylistsToggle';
import { usePlaylists } from '../../components/PlaylistContext';
import { getFirestore, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { useFilterStore } from '../../store/filter.store';
import { useOrganizationStore } from '../../store/organization.store';
import { useAuthStore } from '../../store/auth.store';
import ActiveFiltersBar from '../../components/ActiveFiltersBar';
import ColumnVisibilitySelector from '../../components/ColumnVisibilitySelector';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
// Using new toast styling system from _toasts.scss
import { findLicenseFieldInconsistencies } from '../../utils/licenseDebugger';
import { PendingLicensesModal } from '../../components/PendingLicensesModal';
import PendingLicenseRequestsModal from '../../components/PendingLicenseRequestsModal';
import { ExpiredLicensesModal } from '../../components/ExpiredLicensesModal';
import { ExpiringLicensesModal } from '../../components/ExpiringLicensesModal';
import { ExclusiveLicenseNotificationModal } from '../../components/ExclusiveLicenseNotificationModal';
import { LicenseClaimedModal } from '../../components/LicenseClaimedModal';

// Configuration: Mnemonic Exclusion System
// Set to true to automatically filter out Mnemonic tracks unless explicitly selected
// Set to false to include Mnemonic tracks in all searches (default behavior)
// These are fallback defaults — actual values come from user display preferences in auth store
const EXCLUDE_MNEMONICS_BY_DEFAULT = true;
const EXCLUDE_LICENSED_BY_DEFAULT = true;

// Helper to get current user display preferences
const getDisplayPreferences = () => {
  const prefs = useAuthStore.getState().displayPreferences;
  return {
    excludeMnemonics: prefs?.hideMnemonics ?? EXCLUDE_MNEMONICS_BY_DEFAULT,
    excludeLicensed: prefs?.hideLicensed ?? EXCLUDE_LICENSED_BY_DEFAULT,
  };
};

// Configuration: Progressive Filter Relaxation System
// Set to true to enable automatic filter relaxation when results are too limited
// Set to false to disable filter relaxation (keeps original filters unchanged)
const ENABLE_PROGRESSIVE_FILTER_RELAXATION = false;

const DEFAULT_MULTI = {
  active: false,
  searchOperator: 'OR',
  data: [],
  value: 'tags'
};

const DEFAULT_LICENSE = {
  value: undefined,
  active: false
};

const DEFAULT_DATE = {
  type: 'completed',
  to: APP_CONFIG.DATE_TIME.DEFAULT_DATE_RANGE.TO,
  from: APP_CONFIG.DATE_TIME.DEFAULT_DATE_RANGE.FROM,
  active: false,
};
const DEFAULT_SINGLE = {
  filters: [],
  active: false,
};
const DEFAULT_TEXT = {
  searchText: '',
  field: 'flightingCode',
  active: false,
};

const CHUNK_SIZE = 50;

const GROUP_BY_OPTIONS = [
  { value: 'agency', label: 'Agency', adminOnly: true },
  { value: 'album', label: 'Album', adminOnly: true },
  { value: 'artist', label: 'Artist', adminOnly: true },
  { value: 'client', label: 'Client', adminOnly: true },
  { value: 'composer', label: 'Composer', adminOnly: true },
  { value: 'genre', label: 'Genre', adminOnly: false },
  { value: 'jobTitle', label: 'Job Title', adminOnly: false },
  { value: 'licenseStatus', label: 'License Status', adminOnly: true },
  { value: 'publisher', label: 'Publisher', adminOnly: true },
  { value: 'recordLabel', label: 'Record Label', adminOnly: true },
  { value: 'studio', label: 'Studio', adminOnly: true },
];

// MUI imports for GroupBySelector
const { FormControl, Select, MenuItem, makeStyles } = require('@material-ui/core');

const useGroupByStyles = makeStyles(() => ({
  formControl: {
    minWidth: 80,
  },
  select: {
    backgroundColor: 'transparent',
    color: '#fff',
    fontFamily: 'var(--font-family-primary)',
    fontSize: '0.8rem',
    padding: '0',
    '& .MuiSelect-select': {
      padding: '3px 24px 3px 8px',
      minHeight: 'unset',
      lineHeight: '1.4',
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiSelect-icon': {
      color: '#fff',
      fontSize: '1rem',
      top: 'calc(50% - 8px)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
      top: 0,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
      borderWidth: '1px',
    },
  },
  menuPaper: {
    backgroundColor: '#1e1e2f',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: 4,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },
  menuItem: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontFamily: 'var(--font-family-primary)',
    fontSize: '0.8rem',
    padding: '6px 12px',
    minHeight: 'unset',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
}));

const GroupBySelector = ({ viewMode, groupByField, onChange }) => {
  const classes = useGroupByStyles();
  const currentOrgRole = useOrganizationStore((state) => state.currentOrgRole);
  const isSuperAdmin = useOrganizationStore((state) => state.isSuperAdmin);
  const isAdminOrOperator = isSuperAdmin || ['ADMIN', 'OPERATOR'].includes(currentOrgRole);

  const availableOptions = GROUP_BY_OPTIONS.filter(opt =>
    !opt.adminOnly || isAdminOrOperator
  );

  const handleChange = (event) => {
    const val = event.target.value;
    onChange(val);
  };

  return (
    <FormControl variant="outlined" className={classes.formControl} size="small">
      <Select
        value={viewMode === 'grouped' ? groupByField : 'none'}
        onChange={handleChange}
        className={classes.select}
        renderValue={(val) => {
          if (val === 'none') return 'Group By: None';
          const opt = GROUP_BY_OPTIONS.find(o => o.value === val);
          return `Group By: ${opt ? opt.label : val}`;
        }}
        MenuProps={{
          classes: { paper: classes.menuPaper },
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          getContentAnchorEl: null,
        }}
      >
        <MenuItem value="none" className={classes.menuItem}>None</MenuItem>
        {availableOptions.map(opt => (
          <MenuItem key={opt.value} value={opt.value} className={classes.menuItem}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

class FindAudio extends Component {
  listener;
  genreListener;
  searchFilter = {
    multi: DEFAULT_MULTI,
    date: DEFAULT_DATE,
    subFilters: DEFAULT_SINGLE,
    text: DEFAULT_TEXT,
  };
  sortBy = { column: 'trackName', ascending: true };
  prevFilterState = null; // Track previous filter state
  constructor(props) {
    super(props);
    // Determine which store to use based on mode prop
    const isPublicMode = props.mode === 'public';
    const tracksStore = isPublicMode
      ? usePublicTracksStore.getState()
      : useTracksStore.getState();
    const initialTracks = tracksStore.queryTracks();
    
    this.state = {
      tracks: initialTracks,
      allTracks: initialTracks,
      displayedTracks: initialTracks.slice(0, CHUNK_SIZE), // Start with first chunk
      loading: false,
      loadingMore: false,
      hasMore: initialTracks.length > CHUNK_SIZE,
      currentPage: 1,
      orderBy: {
        column: 'trackName',
        ascending: true,
      },
      columnsVisible: null, // null means not loaded yet
      isSidebarOpen: false,
      useSimplePrompts: false, // Toggle between verbose and simple audio analysis prompts
      showMnemonicsOverride: false, // When true, bypasses mnemonic filtering
      hasMnemonicMatches: false, // Set to true when there are mnemonic tracks that match filters but are hidden
      showLicensedOverride: false, // When true, bypasses licensed track filtering
      hasLicensedMatches: false, // Set to true when there are licensed tracks that match filters but are hidden
      viewMode: 'flat', // 'flat' | 'grouped'
      groupByField: 'client',
      filterState: {
        multi: DEFAULT_MULTI,
        date: DEFAULT_DATE,
        subFilters: DEFAULT_SINGLE,
        text: DEFAULT_TEXT,
        license: DEFAULT_LICENSE
      },
      initialFilterState: {
        tags: {
          selected: [],
          logic: 'or'
        },
        tags2: {
          selected: [],
          logic: 'or'
        },
        genres: {
          selected: [],
          logic: 'or'
        },
        keys: {
          selected: [],
          logic: 'or'
        },
        studios: {
          selected: [],
          logic: 'or'
        },
        agencies: {
          selected: [],
          logic: 'or'
        },
        clients: {
          selected: [],
          logic: 'or'
        },
        bpm: {
          filterType: 'exact',
          exact: null,
          range: { min: '', max: '' }
        },
        producers: {
          selected: [],
          logic: 'or'
        },
        composers: {
          selected: [],
          logic: 'or'
        },
        creativeDirectors: {
          selected: [],
          logic: 'or'
        },
        sessionArtists: {
          selected: [],
          logic: 'or'
        }
      },
      playlistFilterTrackIds: null,
      playlistFilterName: '',
      selectedColumns: [], // array of selected column ids
      toast: { show: false, message: '', type: 'success' },
    };

    // Bind new methods
    this.handleScroll = this.handleScroll.bind(this);
    this.loadMoreTracks = this.loadMoreTracks.bind(this);
  }

  // Add new method for handling scroll
  handleScroll = (event) => {
    const { target } = event;
    if (
      !this.state.loadingMore && 
      this.state.hasMore &&
      target.scrollHeight - target.scrollTop <= target.clientHeight * 1.2
    ) {
      this.loadMoreTracks();
    }
  };

  // Add new method for loading more tracks
  loadMoreTracks = () => {
    const { tracks, displayedTracks, loadingMore, hasMore } = this.state;
    if (loadingMore || !hasMore) return;
    const start = displayedTracks.length;
    const end = start + CHUNK_SIZE;
    if (start >= tracks.length) {
      this.setState({ hasMore: false });
      return;
    }
    this.setState({ loadingMore: true }, () => {
      const newTracks = tracks.slice(start, end);
      this.setState(prevState => ({
        displayedTracks: [...prevState.displayedTracks, ...newTracks],
        loadingMore: false,
        hasMore: end < tracks.length
      }), () => {
        this.forceUpdate();
      });
    });
  };

  toggleSidebar = () => {
    this.setState((prevState) => ({
      isSidebarOpen: !prevState.isSidebarOpen,
    }));
  };

  showMnemonics = () => {
    this.setState({ showMnemonicsOverride: true }, () => {
      // Re-run the current filters with mnemonic filtering bypassed
      this.loadData();
    });
  };

  resetMnemonicsOverride = () => {
    this.setState({ showMnemonicsOverride: false });
  };

  showLicensed = () => {
    this.setState({ showLicensedOverride: true }, () => {
      this.loadData();
    });
  };

  resetLicensedOverride = () => {
    this.setState({ showLicensedOverride: false });
  };

  // Check if there are licensed tracks that match the current filters but are hidden
  hasLicensedMatches = () => {
    if (!getDisplayPreferences().excludeLicensed) return false;
    // Check if license filter is explicitly set to "licensed"
    const { filterState } = this.state;
    if (filterState?.license?.active && filterState.license.value === true) return false;
    if (this.state.showLicensedOverride) return false;
    return this.state.hasLicensedMatches || false;
  };

  // Check if there are mnemonic tracks that match the current filters
  hasMnemonicMatches = () => {
    if (!getDisplayPreferences().excludeMnemonics) {
      return false;
    }
    
    const { filterState } = this.state;
    
    // Check if 'Mnemonic' is explicitly selected (check the original filterState format)
    const mnemonicExplicitlySelected = 
      // Check multi filter (tags/tag1)
      (filterState.multi?.active && filterState.multi.data?.some(item => 
        item.label?.toLowerCase() === 'mnemonic'
      )) ||
      // Check tags2 filter
      (filterState.tags2?.active && filterState.tags2.data?.some(item => 
        item.label?.toLowerCase() === 'mnemonic'
      ));
    
    // If mnemonic is explicitly selected, don't show the button (user already chose to see mnemonics)
    if (mnemonicExplicitlySelected) {
      return false;
    }
    
    // If we're already showing mnemonics override, don't show the button
    if (this.state.showMnemonicsOverride) {
      return false;
    }
    
    // Use the stored hasMnemonicMatches value that was computed during filtering
    // This tells us if there are actually mnemonic tracks that would match the current filters
    return this.state.hasMnemonicMatches || false;
  };
  
  // Helper method to count mnemonic tracks
  countMnemonicTracks = (tracks) => {
    if (!tracks || !Array.isArray(tracks)) {
      return 0;
    }
    
    return tracks.filter(track => {
      if (!track.tags) return false;
      
      const trackLabels = Array.isArray(track.tags)
        ? track.tags.map(d => d?.name || d?.label || d).filter(Boolean)
        : [(track.tags?.name || track.tags?.label || track.tags)].filter(Boolean);
      
      return trackLabels.some(tagLabel => 
        tagLabel?.toLowerCase() === 'mnemonic'
      );
    }).length;
  };
  
  // Helper method to check if there are any active filters
  hasAnyActiveFilters = (filterState) => {
    return (
      (filterState.composers?.active && filterState.composers.data?.length > 0) ||
      (filterState.multi?.active && filterState.multi.data?.length > 0) ||
      (filterState.tags2?.active && filterState.tags2.data?.length > 0) ||
      (filterState.genres?.active && filterState.genres.data?.length > 0) ||
      (filterState.clients?.active && filterState.clients.data?.length > 0) ||
      (filterState.agencies?.active && filterState.agencies.data?.length > 0) ||
      (filterState.studios?.active && filterState.studios.data?.length > 0) ||
      (filterState.producers?.active && filterState.producers.data?.length > 0) ||
      (filterState.creativeDirectors?.active && filterState.creativeDirectors.data?.length > 0) ||
      (filterState.sessionArtists?.active && filterState.sessionArtists.data?.length > 0) ||
      (filterState.keys?.active && filterState.keys.data?.length > 0) ||
      (filterState.text?.active && filterState.text.value)
    );
  };

  orderBy = (data) => {
    // console.log("Ordering by:", data);
    const isSameColumn = this.state.orderBy.column === data.column;
    const newDirection = isSameColumn ? !this.state.orderBy.ascending : true;
    
    this.setState(
      {
        orderBy: {
          column: data.column,
          ascending: newDirection,
        },
      },
      () => {
        this.sortBy = { ...this.state.orderBy };
        this.loadData();
      }
    );
  };

  columnFilterChanged = (filter) => {
    this.setState({
      columnsVisible: filter,
      selectedColumns: this.columnsVisibleToArray(filter)
    });
  };

  loadData = async () => {
    // console.log('[FIND_AUDIO] Loading data');
    try {
      this.setState({ loading: true });

      // Get the current filter state and user display preferences
      const { filterState } = this.state;
      const { excludeMnemonics, excludeLicensed } = getDisplayPreferences();

      // All tracks including licensed tracks (licensed tracks are now in audiometa collection)
      // Licensed tracks have isLicensedTrack: true flag
      let filteredTracks = [...this.state.allTracks];
      // console.log('3. Initial tracks count:', filteredTracks.length);
      
      // Filter by playlist if playlistFilterTrackIds is set
      if (this.state.playlistFilterTrackIds) {
        if (this.state.playlistFilterTrackIds.length === 0) {
          // Playlist is selected but has no tracks: show no tracks
          filteredTracks = [];
          // console.log('Filtered by playlist, no tracks in playlist, tracks count: 0');
        } else {
          filteredTracks = filteredTracks.filter(track => this.state.playlistFilterTrackIds.includes(track.id));
          // console.log('Filtered by playlist, tracks count:', filteredTracks.length);
        }
      }
      
      // Role-based filtering for licensed tracks
      // Use organization-level role instead of global auth role
      const { currentOrgRole, isSuperAdmin } = useOrganizationStore.getState();
      const role = isSuperAdmin ? 'ADMIN' : (currentOrgRole || 'USER');

      if (role === 'USER') {
        // Run comprehensive license field debugging
        if (filteredTracks.length > 0) {
          
          // Check for field inconsistencies
          const inconsistencies = findLicenseFieldInconsistencies(filteredTracks.slice(0, 10));
          if (inconsistencies.length > 0) {
          }
          
        }
        const licensedTracks = []; // Track which tracks are considered licensed
        
        filteredTracks = filteredTracks.filter(track => {
          // Client-copied tracks (licensed IN from another org) are this org's
          // own library entries — they should never be treated as "licensed
          // out" by this filter.
          const isClientCopy = track.isLicensedTrack === true;

          // Check both licenseStatus.isLicensed and dateExpires field
          const hasExplicitLicense = !isClientCopy && track.licenseStatus?.isLicensed &&
                      (track.licenseStatus.durationType === 'blanket' ||
                       (track.licenseStatus.expiryDate && track.licenseStatus.expiryDate > Date.now()));

          const hasDateExpires = !isClientCopy && track.dateExpires && track.dateExpires > Date.now();

          const isLicensed = hasExplicitLicense || hasDateExpires;

          // Log details for first few tracks that are considered licensed
          if (isLicensed && licensedTracks.length < 3) {
            licensedTracks.push({
              name: track.trackName,
              licenseStatus: track.licenseStatus,
              dateExpires: track.dateExpires,
              hasExplicitLicense,
              hasDateExpires
            });
          }

          // Check if track is flagged as needs attention
          const needsAttention = track.needsAttention === true;

          // For USER role, filter OUT licensed tracks AND flagged tracks
          return !isLicensed && !needsAttention;
        });
        
        if (licensedTracks.length > 0) {
        }
      }
      
      
      // Apply text search filter if active
      if (filterState.text?.active && filterState.text?.value) {
        const searchValue = filterState.text.value.toLowerCase();
        filteredTracks = filteredTracks.filter(track => {
          const trackName = track.trackName?.toLowerCase() || '';
          return trackName.includes(searchValue);
        });
      }
      
      // Apply date filter if active
      if (filterState.date?.active) {
        // console.log('5a. Applying date filter:', JSON.stringify(filterState.date, null, 2));
        //console.log('5a. Applying date filter:', JSON.stringify(filterState.date, null, 2));
        filteredTracks = filteredTracks.filter(track => {
          const trackDate = filterState.date.type === 'completed' ? track.dateCompleted : track.dateExpires;
          if (!trackDate) return false;
          
          const date = new Date(trackDate);
          const from = filterState.date.from ? new Date(filterState.date.from) : null;
          const to = filterState.date.to ? new Date(filterState.date.to) : null;
          
          if (from && to) {
            return date >= from && date <= to;
          } else if (from) {
            return date >= from;
          } else if (to) {
            return date <= to;
          }
          return true;
        });
        // console.log('5b. Tracks after date filter:', filteredTracks.length);
      }
      
      // Apply license filter if active
      if (filterState.license?.active) {
        // console.log('6a. Applying license filter:', JSON.stringify(filterState.license, null, 2));
        // console.log('6a. Applying license filter:', JSON.stringify(filterState.license, null, 2));
        filteredTracks = filteredTracks.filter(track => {
          // Check if track has a valid licenseStatus
          if (track.licenseStatus) {
            const isLicensed = track.licenseStatus.isLicensed;
            const isBlanket = track.licenseStatus.durationType === 'blanket';
            const hasValidExpiry = track.licenseStatus.expiryDate && track.licenseStatus.expiryDate > Date.now();
            
            const isTrackLicensed = isLicensed && (isBlanket || hasValidExpiry);
            return filterState.license.value ? isTrackLicensed : !isTrackLicensed;
          }
          
          // If no licenseStatus, check dateExpires
          if (track.dateExpires) {
            const isTrackLicensed = track.dateExpires > Date.now();
            return filterState.license.value ? isTrackLicensed : !isTrackLicensed;
          }
          
          // If no license info at all, consider it unlicensed
          return !filterState.license.value;
        });
        // console.log('6b. Tracks after license filter:', filteredTracks.length);
      }
      
      // Apply multi filter if active
      if (filterState.multi?.active) {
        // console.log('7a. Applying multi filter:', JSON.stringify(filterState.multi, null, 2));
        // console.log('7a. Applying multi filter:', JSON.stringify(filterState.multi, null, 2));
        const labels = filterState.multi.data?.map((d) => d.label);
        const searchKey = filterState.multi.value;
        
        // Debug: Log the first few tracks' tags to see what we're working with
        // console.log('7a1. Sample of track tags:');
        // filteredTracks.slice(0, 5).forEach((track, index) => {
        //   console.log(`   Track ${index + 1} tags:`, track[searchKey]);
        // });
        
        if (filterState.multi.searchOperator === 'AND') {
          if (filterState.multi.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              // Extract tag names from the tag objects
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.name || d?.label || d)
                : [track[searchKey]?.name || track[searchKey]?.label || track[searchKey]];
              
              // Debug: Log the comparison for the first few tracks
              // console.log('7a2. First track comparison:');
              // console.log('   Track labels:', trackLabels);
              // console.log('   Looking for labels:', labels);
              // console.log('   Result:', labels?.every((label) => 
              //   trackLabels?.some(trackLabel => 
              //     trackLabel?.toLowerCase() === label?.toLowerCase()
              //   )
              // ));
              
              return labels?.every((label) => 
                trackLabels?.some(trackLabel => 
                  trackLabel?.toLowerCase() === label?.toLowerCase()
                )
              );
            });
          }
        } else if (filterState.multi.searchOperator === 'OR') {
          if (filterState.multi.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              // Extract tag names from the tag objects
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.name || d?.label || d)
                : [track[searchKey]?.name || track[searchKey]?.label || track[searchKey]];
              
              // Debug: Log the comparison for the first few tracks
              // console.log('7a3. First track comparison (OR):');
              // console.log('   Track labels:', trackLabels);
              // console.log('   Looking for labels:', labels);
              // console.log('   Result:', labels?.some((label) => 
              //   trackLabels?.some(trackLabel => 
              //     trackLabel?.toLowerCase() === label?.toLowerCase()
              //   )
              // ));
              
              return labels?.some((label) => 
                trackLabels?.some(trackLabel => 
                  trackLabel?.toLowerCase() === label?.toLowerCase()
                )
              );
            });
          }
        }
        // console.log('7b. Tracks after multi filter:', filteredTracks.length);
      } else {
        // console.log('Multi filter is not active');
      }

      // Handle tags2 filter (Required Elements - configurable AND/OR logic)
      if (filterState.tags2?.active) {
        // console.log('7c. Applying tags2 filter:', JSON.stringify(filterState.tags2, null, 2));
        const labels = filterState.tags2.data?.map((d) => d.label);
        const searchKey = 'tags'; // tags2 uses the same 'tags' field in tracks data
        
        if (filterState.tags2.searchOperator === 'AND') {
          if (filterState.tags2.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              // Extract tag names from the tag objects
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.name || d?.label || d)
                : [track[searchKey]?.name || track[searchKey]?.label || track[searchKey]];
              
              // AND logic: track must have ALL tags from tags2
              return labels?.every((label) => 
                trackLabels?.some(trackLabel => 
                  trackLabel?.toLowerCase() === label?.toLowerCase()
                )
              );
            });
          }
        } else if (filterState.tags2.searchOperator === 'OR') {
          if (filterState.tags2.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              // Extract tag names from the tag objects
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.name || d?.label || d)
                : [track[searchKey]?.name || track[searchKey]?.label || track[searchKey]];
              
              // OR logic: track must have ANY tag from tags2
              return labels?.some((label) => 
                trackLabels?.some(trackLabel => 
                  trackLabel?.toLowerCase() === label?.toLowerCase()
                )
              );
            });
          }
        }
        // console.log('7d. Tracks after tags2 filter:', filteredTracks.length);
      } else {
        // console.log('Tags2 filter is not active');
      }

      // Handle creative director filter
      if (filterState.creativeDirectors?.active) {
        // console.log('8a. Applying creative director filter:', JSON.stringify(filterState.creativeDirectors, null, 2));
        const labels = filterState.creativeDirectors.data?.map((d) => d.label);
        const searchKey = 'creativedirector';
        
        // Debug: Log the first few tracks' creative directors to see what we're working with
        // console.log('Sample of track creative directors:');
        // filteredTracks.slice(0, 5).forEach((track, index) => {
        //   console.log(`   Track ${index + 1} creative directors:`, track[searchKey]);
        // });
        
        if (filterState.creativeDirectors.searchOperator === 'AND') {
          if (filterState.creativeDirectors.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              
              // Debug: Log the comparison for the first few tracks
              // console.log('First track creative directors comparison:');
              // console.log('   Track creative director labels:', trackLabels);
              // console.log('   Looking for creative director labels:', labels);
              
              return labels?.every((label) => trackLabels?.includes(label));
            });
          }
        } else if (filterState.creativeDirectors.searchOperator === 'OR') {
          if (filterState.creativeDirectors.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              
              // Debug: Log the comparison for the first few tracks
              // console.log('First track creative directors comparison (OR):');
              // console.log('   Track creative director labels:', trackLabels);
              // console.log('   Looking for creative director labels:', labels);
              
              return labels?.some((label) => trackLabels?.includes(label));
            });
          }
        }
        // console.log('8b. Tracks after creative director filter:', filteredTracks.length);
      } else {
        // console.log('Creative directors filter is not active');
      }

      // Apply session artists filter
      if (filterState.sessionArtists?.active && filterState.sessionArtists?.data?.length > 0) {
        // console.log('401a. Applying session artists filter:', JSON.stringify(filterState.sessionArtists, null, 2));
        const sessionArtistLabels = filterState.sessionArtists.data.map(item => item.label);
        // console.log('Session artist labels to match:', sessionArtistLabels);
        
        // Debug: Log the first few tracks' session artists to see what we're working with
        // console.log('Sample of track session artists:');
        // filteredTracks.slice(0, 5).forEach((track, index) => {
        //   console.log(`   Track ${index + 1} session artists:`, track.sessionArtists);
        // });
        
        filteredTracks = filteredTracks.filter(track => {
          const trackSessionArtists = track.sessionArtists || [];
          const trackLabels = trackSessionArtists.map(artist => artist.label || artist.name || artist);
          
          // Debug: Log the comparison for the first few tracks
          // console.log('First track session artists comparison:');
          // console.log('   Track session artist labels:', trackLabels);
          // console.log('   Looking for session artist labels:', sessionArtistLabels);
          
          const hasSessionArtists = sessionArtistLabels.some(label => 
            trackLabels.some(trackLabel => 
              trackLabel?.toLowerCase() === label?.toLowerCase()
            )
          );
          
          // console.log('First track has matching session artists:', hasSessionArtists);
          
          return filterState.sessionArtists.searchOperator === 'AND' 
            ? sessionArtistLabels.every(label => 
                trackLabels.some(trackLabel => 
                  trackLabel?.toLowerCase() === label?.toLowerCase()
                )
              )
            : hasSessionArtists;
        });
        // console.log('9b. Tracks after session artists filter:', filteredTracks.length);
      } else {
        // console.log('Session artists filter is not active or has no data');
      }

      // Handle genres filter
      if (filterState.genres?.active) {
        // console.log('10a. Applying genres filter:', JSON.stringify(filterState.genres, null, 2));
        const labels = filterState.genres.data?.map((d) => d.label);
        const searchKey = 'genre';
        
        // Debug: Log the first few tracks' genres to see what we're working with
        // console.log('Sample of track genres:');
        // filteredTracks.slice(0, 5).forEach((track, index) => {
        //   console.log(`   Track ${index + 1} genres:`, track[searchKey]);
        // });
        
        if (filterState.genres.searchOperator === 'AND') {
          if (filterState.genres.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              
              // Debug: Log the comparison for the first few tracks
              // console.log('First track genres comparison:');
              // console.log('   Track genre labels:', trackLabels);
              // console.log('   Looking for genre labels:', labels);
              
              return labels?.every((label) => trackLabels?.includes(label));
            });
          }
        } else if (filterState.genres.searchOperator === 'OR') {
          if (filterState.genres.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              
              // Debug: Log the comparison for the first few tracks
              // console.log('First track genres comparison (OR):');
              // console.log('   Track genre labels:', trackLabels);
              // console.log('   Looking for genre labels:', labels);
              
              return labels?.some((label) => trackLabels?.includes(label));
            });
          }
        }
        // console.log('10b. Tracks after genres filter:', filteredTracks.length);
      } else {
        // console.log('Genres filter is not active');
      }

      // Handle agencies filter
      if (filterState.agencies?.active) {
        // console.log('10c. Applying agencies filter:', JSON.stringify(filterState.agencies, null, 2));
        const labels = filterState.agencies.data?.map((d) => d.label);
        const searchKey = 'agency';  // This matches the collection name in the database
        
        if (filterState.agencies.searchOperator === 'AND') {
          if (filterState.agencies.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              return labels?.every((label) => trackLabels?.includes(label));
            });
          }
        } else if (filterState.agencies.searchOperator === 'OR') {
          if (filterState.agencies.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              return labels?.some((label) => trackLabels?.includes(label));
            });
          }
        }
        // console.log('10d. Tracks after agencies filter:', filteredTracks.length);
      }

      // Handle clients filter
      if (filterState.clients?.active) {
        // console.log('10e. Applying clients filter:', JSON.stringify(filterState.clients, null, 2));
        const labels = filterState.clients.data?.map((d) => d.label);
        const searchKey = 'client';  // This matches the collection name in the database
        
        if (filterState.clients.searchOperator === 'AND') {
          if (filterState.clients.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              return labels?.every((label) => trackLabels?.includes(label));
            });
          }
        } else if (filterState.clients.searchOperator === 'OR') {
          if (filterState.clients.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              return labels?.some((label) => trackLabels?.includes(label));
            });
          }
        }
        // console.log('10f. Tracks after clients filter:', filteredTracks.length);
      }

      // Handle keys filter
      if (filterState.keys?.active) {
        // console.log('10g. Applying keys filter:', JSON.stringify(filterState.keys, null, 2));
        const labels = filterState.keys.data?.map((d) => d.label);
        const searchKey = 'key';  // This matches the collection name in the database
        
        if (filterState.keys.searchOperator === 'AND') {
          if (filterState.keys.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              return labels?.every((label) => trackLabels?.includes(label));
            });
          }
        } else if (filterState.keys.searchOperator === 'OR') {
          if (filterState.keys.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              return labels?.some((label) => trackLabels?.includes(label));
            });
          }
        }
        // console.log('10h. Tracks after keys filter:', filteredTracks.length);
      }

      // Handle studios filter
      if (filterState.studios?.active) {
        // console.log('13a. Applying studios filter:', JSON.stringify(filterState.studios, null, 2));
        const labels = filterState.studios.data?.map((d) => d.label);
        const searchKey = 'studio';
        
        if (filterState.studios.searchOperator === 'AND') {
          if (filterState.studios.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabel = track[searchKey]?.label || '';
              return labels?.every((label) => trackLabel.toLowerCase() === label.toLowerCase());
            });
          }
        } else if (filterState.studios.searchOperator === 'OR') {
          if (filterState.studios.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabel = track[searchKey]?.label || '';
              return labels?.some((label) => trackLabel.toLowerCase() === label.toLowerCase());
            });
          }
        }
        // console.log('13b. Tracks after studios filter:', filteredTracks.length);
      }

      // Handle producers filter
      if (filterState.producers?.active) {
        // console.log('14a. Applying producers filter:', JSON.stringify(filterState.producers, null, 2));
        const labels = filterState.producers.data?.map((d) => d.label);
        const searchKey = 'producer';
        
        if (filterState.producers.searchOperator === 'AND') {
          if (filterState.producers.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              return labels?.every((label) => trackLabels?.includes(label));
            });
          }
        } else if (filterState.producers.searchOperator === 'OR') {
          if (filterState.producers.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              return labels?.some((label) => trackLabels?.includes(label));
            });
          }
        }
        // console.log('14b. Tracks after producers filter:', filteredTracks.length);
      }

      // Handle composers filter
      if (filterState.composers?.active) {
        // console.log('15a. Applying composers filter:', JSON.stringify(filterState.composers, null, 2));
        const labels = filterState.composers.data?.map((d) => d.label);
        const searchKey = 'composer';
        
        if (filterState.composers.searchOperator === 'AND') {
          if (filterState.composers.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              return labels?.every((label) => trackLabels?.includes(label));
            });
          }
        } else if (filterState.composers.searchOperator === 'OR') {
          if (filterState.composers.data?.length > 0) {
            filteredTracks = filteredTracks?.filter((track) => {
              if (!track[searchKey]) return false;
              
              const trackLabels = Array.isArray(track[searchKey])
                ? track[searchKey]?.map((d) => d?.label || '')
                : [track[searchKey]?.label || ''];
              return labels?.some((label) => trackLabels?.includes(label));
            });
          }
        }
        // console.log('15b. Tracks after composers filter:', filteredTracks.length);
      }

      // Handle the subFilters
      if (filterState.subFilters?.active) {
        // console.log('16a. Applying subFilters:', JSON.stringify(filterState.subFilters, null, 2));
        
        filterState.subFilters.filters.forEach((subFilter) => {
          if (subFilter.key === 'bpm') {
            // Get the target BPM value from the label
            const targetBpm = Number(subFilter.value.label);
            // console.log('16b. Filtering by BPM:', targetBpm);
            
            filteredTracks = filteredTracks.filter((track) => {
              // Skip filtering if track has no BPM
              if (!track.bpm) return true;
              const trackBpm = Number(track.bpm?.label);
              return !isNaN(trackBpm) && trackBpm === targetBpm;
            });
            // console.log('16c. Tracks after BPM subFilter:', filteredTracks.length);
          } else if (subFilter.key === 'key') {
            // Handle key filter
            const valueLabel = subFilter.value.label;
            // console.log('16d. Filtering by key:', valueLabel);
            
            filteredTracks = filteredTracks.filter((track) => {
              // Skip filtering if track has no key
              if (!track[subFilter.key]) return true;
              return track[subFilter.key]?.label === valueLabel;
            });
            // console.log('16e. Tracks after key subFilter:', filteredTracks.length);
          } else {
            // Handle other filters
            const valueLabel = subFilter.value.label;
            // console.log('16f. Filtering by', subFilter.key + ':', valueLabel);
            
            filteredTracks = filteredTracks.filter((track) => {
              // Skip filtering if track has no value for this field
              if (!track[subFilter.key]) return true;
              return track[subFilter.key]?.label === valueLabel;
            });
            // console.log('16g. Tracks after', subFilter.key, 'subFilter:', filteredTracks.length);
          }
        });
      } else {
        // console.log('No subFilters active');
      }
      
      // Handle BPM filter
      if (filterState.bpm?.active) {
        // console.log('17a. Applying BPM filter:', JSON.stringify(filterState.bpm, null, 2));
        
        if (filterState.bpm.filterType === 'exact' && filterState.bpm.exact) {
          // console.log('17b. Filtering by exact BPM:', filterState.bpm.exact.value);
          filteredTracks = filteredTracks.filter(track => {
            if (!track.bpm) {
              return false;
            }
            
            // Handle different BPM data structures
            const trackBPM = typeof track.bpm === 'number' 
              ? track.bpm 
              : typeof track.bpm === 'object' && track.bpm !== null
                ? parseInt(track.bpm.label || track.bpm.value)
                : parseInt(track.bpm);

            return trackBPM === parseInt(filterState.bpm.exact.value);
          });
          // console.log('17c. Tracks after exact BPM filter:', filteredTracks.length);
        } else if (filterState.bpm.filterType === 'range') {
          const { min, max } = filterState.bpm.range;
          // console.log('17d. Filtering by BPM range:', min, '-', max);
          
          if (min || max) {
            filteredTracks = filteredTracks.filter(track => {
              if (!track.bpm) {
                return false;
              }
              
              // Handle different BPM data structures
              const trackBPM = typeof track.bpm === 'number' 
                ? track.bpm 
                : typeof track.bpm === 'object' && track.bpm !== null
                  ? parseInt(track.bpm.label || track.bpm.value)
                  : parseInt(track.bpm);
              
              if (min && max) {
                return trackBPM >= parseInt(min) && trackBPM <= parseInt(max);
              } else if (min) {
                return trackBPM >= parseInt(min);
              } else if (max) {
                return trackBPM <= parseInt(max);
              }
              return true;
            });
            // console.log('17e. Tracks after BPM range filter:', filteredTracks.length);
          }
        }
      } else {
        // console.log('BPM filter is not active');
      }

      // === EXCLUSION FILTERS ===
      // Apply exclusion filters after all inclusion filters
      
      // Handle excluded tags filter
      if (filterState.excludedTags?.active && filterState.excludedTags?.data?.length > 0) {
        const excludedLabels = filterState.excludedTags.data.map(item => item.label);
        filteredTracks = filteredTracks.filter(track => {
          if (!track.tags) return true; // Keep tracks with no tags
          
          const trackLabels = Array.isArray(track.tags)
            ? track.tags.map(d => d?.name || d?.label || d)
            : [track.tags?.name || track.tags?.label || track.tags];
          
          // Exclude if track has ANY of the excluded tags (OR logic)
          // or exclude if track has ALL of the excluded tags (AND logic)
          return filterState.excludedTags.searchOperator === 'AND'
            ? !excludedLabels.every(label => 
                trackLabels.some(trackLabel => 
                  trackLabel?.toLowerCase() === label?.toLowerCase()
                )
              )
            : !excludedLabels.some(label => 
                trackLabels.some(trackLabel => 
                  trackLabel?.toLowerCase() === label?.toLowerCase()
                )
              );
        });
      }

      // Handle excluded tags2 filter
      if (filterState.excludedTags2?.active && filterState.excludedTags2?.data?.length > 0) {
        const excludedLabels = filterState.excludedTags2.data.map(item => item.label);
        filteredTracks = filteredTracks.filter(track => {
          if (!track.tags) return true; // Keep tracks with no tags
          
          const trackLabels = Array.isArray(track.tags)
            ? track.tags.map(d => d?.name || d?.label || d)
            : [track.tags?.name || track.tags?.label || track.tags];
          
          return filterState.excludedTags2.searchOperator === 'AND'
            ? !excludedLabels.every(label => 
                trackLabels.some(trackLabel => 
                  trackLabel?.toLowerCase() === label?.toLowerCase()
                )
              )
            : !excludedLabels.some(label => 
                trackLabels.some(trackLabel => 
                  trackLabel?.toLowerCase() === label?.toLowerCase()
                )
              );
        });
      }

      // Handle excluded genres filter
      if (filterState.excludedGenres?.active && filterState.excludedGenres?.data?.length > 0) {
        const excludedLabels = filterState.excludedGenres.data.map(item => item.label);
        filteredTracks = filteredTracks.filter(track => {
          if (!track.genre) return true; // Keep tracks with no genre
          
          const trackLabels = Array.isArray(track.genre)
            ? track.genre.map(d => d?.label || '')
            : [track.genre?.label || ''];
          
          return filterState.excludedGenres.searchOperator === 'AND'
            ? !excludedLabels.every(label => trackLabels.includes(label))
            : !excludedLabels.some(label => trackLabels.includes(label));
        });
      }

      // Handle excluded creative directors filter
      if (filterState.excludedCreativeDirectors?.active && filterState.excludedCreativeDirectors?.data?.length > 0) {
        const excludedLabels = filterState.excludedCreativeDirectors.data.map(item => item.label);
        filteredTracks = filteredTracks.filter(track => {
          if (!track.creativedirector) return true;
          
          const trackLabels = Array.isArray(track.creativedirector)
            ? track.creativedirector.map(d => d?.label || '')
            : [track.creativedirector?.label || ''];
          
          return filterState.excludedCreativeDirectors.searchOperator === 'AND'
            ? !excludedLabels.every(label => trackLabels.includes(label))
            : !excludedLabels.some(label => trackLabels.includes(label));
        });
      }

      // Handle excluded session artists filter
      if (filterState.excludedSessionArtists?.active && filterState.excludedSessionArtists?.data?.length > 0) {
        const excludedLabels = filterState.excludedSessionArtists.data.map(item => item.label);
        filteredTracks = filteredTracks.filter(track => {
          if (!track.sessionartists) return true;
          
          const trackLabels = track.sessionartists.map(artist => artist.label || artist.name || artist);
          
          return filterState.excludedSessionArtists.searchOperator === 'AND'
            ? !excludedLabels.every(label => 
                trackLabels.some(trackLabel => 
                  trackLabel?.toLowerCase() === label?.toLowerCase()
                )
              )
            : !excludedLabels.some(label => 
                trackLabels.some(trackLabel => 
                  trackLabel?.toLowerCase() === label?.toLowerCase()
                )
              );
        });
      }

      // Handle other excluded filters (agencies, clients, keys, studios, producers, composers)
      const excludedFilterConfigs = [
        { stateKey: 'excludedAgencies', trackKey: 'agency' },
        { stateKey: 'excludedClients', trackKey: 'client' },
        { stateKey: 'excludedKeys', trackKey: 'key' },
        { stateKey: 'excludedStudios', trackKey: 'studio' },
        { stateKey: 'excludedProducers', trackKey: 'producer' },
        { stateKey: 'excludedComposers', trackKey: 'composer' }
      ];

      excludedFilterConfigs.forEach(config => {
        if (filterState[config.stateKey]?.active && filterState[config.stateKey]?.data?.length > 0) {
          const excludedLabels = filterState[config.stateKey].data.map(item => item.label);
          filteredTracks = filteredTracks.filter(track => {
            if (!track[config.trackKey]) return true;
            
            const trackLabels = Array.isArray(track[config.trackKey])
              ? track[config.trackKey].map(d => d?.label || '')
              : [track[config.trackKey]?.label || ''];
            
            return filterState[config.stateKey].searchOperator === 'AND'
              ? !excludedLabels.every(label => trackLabels.includes(label))
              : !excludedLabels.some(label => trackLabels.includes(label));
          });
        }
      });
      
      // Apply secondary layer of excluded chips filtering
      const excludedChipsBySection = useExcludedChipsStore.getState().getExcludedChipsBySection();
      
      if (Object.keys(excludedChipsBySection).length > 0) {
        // Process each section that has excluded chips
        Object.entries(excludedChipsBySection).forEach(([section, excludedIds]) => {
          if (excludedIds.length > 0) {
            let trackKey;
            
            // Map section names to track field names
            switch (section) {
              case 'tags':
                trackKey = 'tags';
                break;
              case 'tags2':
                trackKey = 'requiredElements';
                break;
              case 'genres':
                trackKey = 'genre';
                break;
              case 'keys':
                trackKey = 'key';
                break;
              case 'studios':
                trackKey = 'studio';
                break;
              case 'agencies':
                trackKey = 'agency';
                break;
              case 'clients':
                trackKey = 'client';
                break;
              case 'producers':
                trackKey = 'producer';
                break;
              case 'composers':
                trackKey = 'composer';
                break;
              case 'creativeDirectors':
                trackKey = 'creativedirector';
                break;
              case 'sessionArtists':
                trackKey = 'sessionartists';
                break;
              default:
                return;
            }
            
            // Filter out tracks that contain any of the excluded values
            filteredTracks = filteredTracks.filter(track => {
              if (!track[trackKey]) return true;
              
              // Get track labels
              const trackLabels = Array.isArray(track[trackKey])
                ? track[trackKey].map(item => 
                    typeof item === 'string' ? item : (item?.label || item?.name || '')
                  )
                : [typeof track[trackKey] === 'string' 
                    ? track[trackKey] 
                    : (track[trackKey]?.label || track[trackKey]?.name || '')
                  ];
              
              // Check if any excluded ID matches any track label
              return !excludedIds.some(excludedId => 
                trackLabels.some(trackLabel => 
                  trackLabel?.toLowerCase() === excludedId?.toLowerCase()
                )
              );
            });
          }
        });
      }
      
      //license status expiration check
      //////////////
      // Check for expired licenses
      const orgId = useOrganizationStore.getState().currentOrgId;
      const firebase = this.props.firebase;
      filteredTracks.forEach(track => {
        if (track.licenseStatus &&
            track.licenseStatus.isLicensed &&
            track.licenseStatus.durationType !== 'blanket' &&
            track.licenseStatus.expiryDate &&
            track.licenseStatus.expiryDate < Date.now()) {

          // Update locally first for immediate UI feedback
          track.licenseStatus.isLicensed = false;

          // Then update in Firestore and check for exclusive license notifications
          if (orgId) {
            const trackRef = doc(getFirestore(), getOrgCollectionPath(orgId, AUDIOMETAPATH), track.id);
            updateDoc(trackRef, { 'licenseStatus.isLicensed': false })
              .catch(err => console.error('Error updating expired license:', err));

            // Check if any expired exclusive licenses need a notification
            if (firebase) {
              firebase.getLicensesForTrack(orgId, track.id).then(licenses => {
                const now = Date.now();
                const expiredExclusives = licenses.filter(l =>
                  l.isExclusive === true &&
                  l.status !== 'inactive' &&
                  l.dateExpires && l.dateExpires < now
                );
                if (expiredExclusives.length > 0) {
                  // Mark expired exclusive licenses as inactive so they don't trigger again
                  expiredExclusives.forEach(l => {
                    firebase.updateLicense(orgId, track.id, l.id, {
                      status: 'inactive',
                      deactivatedAt: now,
                      deactivatedBy: 'system-expiry',
                    }).catch(err => console.error('Error deactivating expired license:', err));
                  });

                  // Check if there are still active (non-expired) exclusive licenses
                  firebase.getActiveExclusiveLicense(orgId, track.id).then(activeExclusive => {
                    if (!activeExclusive) {
                      firebase.createExclusiveLicenseEndedNotification(orgId, track.id, {
                        ...expiredExclusives[0],
                        trackName: track.trackName,
                      }, 'expired');
                    }
                  });
                }
              }).catch(err => console.error('Error checking exclusive licenses:', err));
            }
          }
        }
      });
      //////////////
      
      // Special handling for license status sorting
      if (this.state.orderBy.column === 'licenseStatus') {
        // console.log('19c. Sorting by license status');
        filteredTracks.sort((a, b) => {
          // Check if items are licensed
          const aLicensed = (a.licenseStatus?.isLicensed && 
                          (a.licenseStatus.durationType === 'blanket' || 
                          (a.licenseStatus.expiryDate && a.licenseStatus.expiryDate > Date.now()))) ||
                          (a.dateExpires && a.dateExpires > Date.now());
                          
          const bLicensed = (b.licenseStatus?.isLicensed && 
                          (b.licenseStatus.durationType === 'blanket' || 
                          (b.licenseStatus.expiryDate && b.licenseStatus.expiryDate > Date.now()))) ||
                          (b.dateExpires && b.dateExpires > Date.now());
          
          const x = aLicensed ? 1 : 0;
          const y = bLicensed ? 1 : 0;
          
          if (this.state.orderBy.ascending) {
            return x - y;
          } else {
            return y - x;
          }
        });
        // console.log('19d. Tracks sorted by license status');
      } else {
        // Apply general sorting for other columns
        // console.log('19e. Applying general sorting by:', this.state.orderBy.column);
        const { column, ascending } = this.state.orderBy;
        
        filteredTracks.sort((a, b) => {
          let aValue = a[column];
          let bValue = b[column];
          
          // Special case: BPM column should be sorted numerically, treat N/A as 0
          if (column === 'bpm') {
            const getBpmNumber = (val) => {
              if (typeof val === 'number' && !isNaN(val)) return val;
              if (val && typeof val === 'object' && val.label && !isNaN(parseFloat(val.label))) return parseFloat(val.label);
              if (val && typeof val === 'object' && val.value && !isNaN(parseFloat(val.value))) return parseFloat(val.value);
              if (typeof val === 'string' && !isNaN(parseFloat(val))) return parseFloat(val);
              return 0; // Treat N/A as 0
            };
            const aBpm = getBpmNumber(aValue);
            const bBpm = getBpmNumber(bValue);
            return ascending ? aBpm - bBpm : bBpm - aBpm;
          }
          // Special case: dateCompleted and dateExpires columns should be sorted chronologically, treat N/A as 0
          if (column === 'dateCompleted' || column === 'dateExpires') {
            const getDateValue = (val) => {
              if (!val) return 0;
              const date = new Date(val);
              return isNaN(date.getTime()) ? 0 : date.getTime();
            };
            const aDate = getDateValue(aValue);
            const bDate = getDateValue(bValue);
            return ascending ? aDate - bDate : bDate - aDate;
          }
          
          // Handle nested object properties (like licenseStatus.isLicensed)
          if (column.includes('.')) {
            const parts = column.split('.');
            aValue = parts.reduce((obj, key) => obj && obj[key], a);
            bValue = parts.reduce((obj, key) => obj && obj[key], b);
          }
          
          // Handle array values (like composer, tags, etc.)
          if (Array.isArray(aValue) && Array.isArray(bValue)) {
            aValue = aValue.length > 0 ? aValue[0]?.label || aValue[0] : '';
            bValue = bValue.length > 0 ? bValue[0]?.label || bValue[0] : '';
          }
          
          // Handle object values (like genre, key, etc.)
          if (aValue && typeof aValue === 'object' && !Array.isArray(aValue)) {
            aValue = aValue.label || '';
          }
          if (bValue && typeof bValue === 'object' && !Array.isArray(bValue)) {
            bValue = bValue.label || '';
          }
          
          // Convert to strings for comparison
          aValue = String(aValue || '').toLowerCase();
          bValue = String(bValue || '').toLowerCase();
          
          if (ascending) {
            return aValue.localeCompare(bValue);
          } else {
            return bValue.localeCompare(aValue);
          }
        });
        // console.log('19f. General sorting applied');
      }
      
      // === MNEMONIC EXCLUSION SYSTEM ===
      // Hard-coded system to exclude Mnemonic tracks unless explicitly selected
      // This happens AFTER all other filters have been applied
      let hasMnemonicMatches = false;

      if (excludeMnemonics) {
        // Check if 'Mnemonic' is explicitly selected in tags (multi) or tags2
        const mnemonicExplicitlySelected = 
          // Check multi filter (tags/tag1)
          (filterState.multi?.active && filterState.multi.data?.some(item => 
            item.label?.toLowerCase() === 'mnemonic'
          )) ||
          // Check tags2 filter
          (filterState.tags2?.active && filterState.tags2.data?.some(item => 
            item.label?.toLowerCase() === 'mnemonic'
          ));

        // Before applying mnemonic exclusion, check if there are mnemonic tracks in the final filtered results
        if (!mnemonicExplicitlySelected && !this.state.showMnemonicsOverride) {
          // Client-copied tracks (licensed IN from another org) are this org's own
          // library entries — never treat them as mnemonics to hide here, even
          // if the source track was tagged mnemonic in the artist's catalogue.
          const isHiddenMnemonic = (track) => {
            if (track.isLicensedTrack === true) return false; // client copy
            if (!track.tags) return false;
            const trackLabels = Array.isArray(track.tags)
              ? track.tags.map(d => d?.name || d?.label || d)
              : [track.tags?.name || track.tags?.label || track.tags];
            return trackLabels.some(tagLabel =>
              tagLabel?.toLowerCase() === 'mnemonic'
            );
          };

          hasMnemonicMatches = filteredTracks.some(isHiddenMnemonic);

          const beforeMnemonicFilter = filteredTracks.length;
          filteredTracks = filteredTracks.filter(track => !isHiddenMnemonic(track));

          const excludedMnemonicCount = beforeMnemonicFilter - filteredTracks.length;
          if (excludedMnemonicCount > 0) {
            // console.log(`[MnemonicFilter] Excluded ${excludedMnemonicCount} Mnemonic tracks`);
          }
        }
        // If 'Mnemonic' IS explicitly selected, allow all Mnemonic tracks through
        // (they will be further filtered by the normal tag filtering logic)
      }
      
      // === LICENSED TRACK EXCLUSION SYSTEM ===
      // Hide licensed tracks unless explicitly selected via license filter or override
      let hasLicensedMatches = false;

      if (excludeLicensed) {
        // Check if the license filter is explicitly set to "licensed"
        const licensedExplicitlySelected = filterState?.license?.active && filterState.license.value === true;

        if (!licensedExplicitlySelected && !this.state.showLicensedOverride) {
          // Client-copied tracks (licensed IN from another org) are this org's
          // own library entries — never treat them as "licensed out" here.
          const isLicensedOut = (track) => {
            if (track.isLicensedTrack === true) return false; // client copy
            const ls = track.licenseStatus;
            if (ls && ls.isLicensed) {
              const isBlanket = ls.durationType === 'blanket';
              const hasValidExpiry = ls.expiryDate && ls.expiryDate > Date.now();
              if (isBlanket || hasValidExpiry) return true;
            }
            if (track.dateExpires && track.dateExpires > Date.now()) return true;
            return false;
          };

          hasLicensedMatches = filteredTracks.some(isLicensedOut);
          filteredTracks = filteredTracks.filter((track) => !isLicensedOut(track));
        }
      }

      // Check if filters have actually changed (criteria, not just results)
      const filterCriteriaChanged = JSON.stringify(this.prevFilterState) !== JSON.stringify(filterState);
      const hasFilterChanged = JSON.stringify(this.state.tracks) !== JSON.stringify(filteredTracks);


      // Reset to first page when applying new filters
      this.setState(prevState => {
        // Update displayedTracks if filters changed OR if displayedTracks is empty (initial load race condition fix)
        const shouldUpdateDisplayed = hasFilterChanged || prevState.displayedTracks.length === 0;
        const newDisplayedTracks = shouldUpdateDisplayed ? filteredTracks.slice(0, CHUNK_SIZE) : prevState.displayedTracks;

        return {
          loading: false,
          tracks: filteredTracks,
          displayedTracks: newDisplayedTracks,
          hasMore: filteredTracks.length > newDisplayedTracks.length,
          currentPage: 1,
          loadingMore: false,
          hasMnemonicMatches: hasMnemonicMatches,
          hasLicensedMatches: hasLicensedMatches
        };
      }, () => {
        // Only scroll back to top if filters have actually changed
        if (filterCriteriaChanged) {
          const scrollContainer = document.querySelector('.table-scroll');
          if (scrollContainer) {
            scrollContainer.scrollTop = 0;
          }
        }
        // Update prevFilterState after each loadData call
        this.prevFilterState = JSON.parse(JSON.stringify(filterState));
      });
      
      // console.log('21. UI updated with filtered tracks');
      
      // Save current filters and filterState to global store
      const simpleFilterList = extractSimpleFilterList(this.state.filterState);
      useFilterStore.getState().setFilters(simpleFilterList, this.state.filterState);
      // console.log('[VAULT_FILTER_SYNC] Current filters list:', simpleFilterList);
    } catch (error) {
      console.error('Error loading data:', error);
      this.setState({ loading: false, loadingMore: false });
    }
  };

  handlePromptToggle = () => {
    this.setState(prevState => ({
      useSimplePrompts: !prevState.useSimplePrompts
    }));
  };

  componentDidMount() {
    // Debug role on component mount
    const isPublicMode = this.props.mode === 'public';

    // Subscribe to the appropriate tracks store based on mode
    // The TrackListener/PublicTracksListener component handles initialization
    const tracksStore = isPublicMode ? usePublicTracksStore : useTracksStore;
    this.unsubscribe = tracksStore.subscribe(
      (state, prevState) => {
        const allTracks = state.queryTracks();

        // Update whenever the org changes or tracks change
        const orgChanged = !isPublicMode && prevState && state.currentOrgId !== prevState.currentOrgId;

        // Check if track IDs or count changed
        const idsChanged = allTracks.length !== this.state.allTracks.length ||
          JSON.stringify(allTracks.map(t => t.id).sort()) !== JSON.stringify(this.state.allTracks.map(t => t.id).sort());

        // Check if track data changed (specifically isPublic for public toggle sync)
        const dataChanged = !idsChanged && allTracks.some(newTrack => {
          const oldTrack = this.state.allTracks.find(t => t.id === newTrack.id);
          return oldTrack && oldTrack.isPublic !== newTrack.isPublic;
        });

        if (orgChanged || idsChanged || dataChanged) {
          this.setState({
            allTracks: allTracks,
            tracks: allTracks,
            displayedTracks: allTracks.slice(0, CHUNK_SIZE),
            hasMore: allTracks.length > CHUNK_SIZE,
            currentPage: 1,
          }, () => {
            this.loadData();
          });
        }
      }
    );

    // Subscribe to organization store for org changes (only in library mode)
    if (!isPublicMode) {
      this.orgUnsubscribe = useOrganizationStore.subscribe(
        (state, prevState) => {
          if (prevState && state.currentOrgId !== prevState.currentOrgId) {
            // Org changed, reload data (licensed tracks are now in audiometa collection)
            this.loadData();
          }
        }
      );
    }

    // Listen for auth state changes
    this.authUnsubscribe = onAuthStateChanged(getAuth(), async (user) => {
      if (user) {
        // Force refresh the ID token to ensure Firestore has valid auth
        await user.getIdToken(true);
        this.loadUserColumnSelection();
      }
    });
    // Also try to load columns immediately in case user is already available
    this.loadUserColumnSelection();
  }

  async loadUserColumnSelection() {
    const isPublicMode = this.props.mode === 'public';
    const PUBLIC_ALLOWED_COLUMNS = ['trackName', 'length', 'genre', 'bpm', 'key', 'tags', 'audioFiles'];

    try {
      const auth = getAuth();
      const db = getFirestore();
      const user = auth.currentUser;
      if (!user) {
        this.setState({ columnsVisible: this.getDefaultColumnsVisible() });
        return;
      }
      const ref = doc(db, 'users', user.uid, 'columnPreferences', 'columnSelection');
      const snap = await getDoc(ref);
      if (snap.exists()) {
        let selection = snap.data().selection;
        if (Array.isArray(selection) && selection.length > 0) {
          // In public mode, filter to only allowed columns
          if (isPublicMode) {
            selection = selection.filter(col => PUBLIC_ALLOWED_COLUMNS.includes(col));
            if (selection.length === 0) selection = [...PUBLIC_ALLOWED_COLUMNS];
          }
          this.setState({
            columnsVisible: this.columnsArrayToVisible(selection),
            selectedColumns: selection
          });
          return;
        }
      }
      const defaultCols = this.getDefaultColumnsVisible();
      this.setState({
        columnsVisible: defaultCols,
        selectedColumns: this.columnsVisibleToArray(defaultCols)
      });
    } catch (err) {
      this.setState({ columnsVisible: this.getDefaultColumnsVisible() });
    }
  }

  getDefaultColumnsVisible() {
    // Return the default columnsVisible object based on user role
    const role = this.props.role;
    const isPublicMode = this.props.mode === 'public';

    // Default columns for USER role and public mode (exclude restricted columns)
    const USER_DEFAULT_COLUMNS = [
      'trackName', 'length', 'genre', 'bpm', 'key', 'audioFiles'
    ];

    // Default columns for ADMIN/OPERATOR roles
    const ADMIN_DEFAULT_COLUMNS = [
      'trackName', 'length', 'composers', 'genre', 'bpm', 'key', 'audioFiles', 'licenseStatus', 'isPublic',
    ];

    // Public mode always uses USER-level columns regardless of role
    const DEFAULT_COLUMNS = isPublicMode
      ? USER_DEFAULT_COLUMNS
      : ['ADMIN', 'OPERATOR'].includes(role)
        ? ADMIN_DEFAULT_COLUMNS
        : USER_DEFAULT_COLUMNS;

    const allColumns = [
      'trackName', 'length', 'licenseStatus', 'isPublic', 'composers', 'genre', 'bpm', 'key', 'tags', 'trackRating', 'audioFiles', 'flag', 'jobTitle', 'producer', 'artist', 'album', 'recordLabel', 'publisher', 'agency', 'client', 'creativeDirector', 'dateCompleted', 'dateExpires', 'flightingPlatform', 'flightingCode', 'studio', 'backupLocation', 'sessionArtists'
    ];
    const visible = {};
    allColumns.forEach(col => {
      visible[col] = DEFAULT_COLUMNS.includes(col);
    });
    visible.colsSelected = DEFAULT_COLUMNS.length;
    return visible;
  }

  columnsArrayToVisible(arr) {
    // Convert array of column ids to columnsVisible object
    const allColumns = [
      'trackName', 'length', 'licenseStatus', 'isPublic', 'composers', 'genre', 'bpm', 'key', 'tags', 'trackRating', 'audioFiles', 'flag', 'jobTitle', 'producer', 'artist', 'album', 'recordLabel', 'publisher', 'agency', 'client', 'creativeDirector', 'dateCompleted', 'dateExpires', 'flightingPlatform', 'flightingCode', 'studio', 'backupLocation', 'sessionArtists'
    ];
    const visible = {};
    allColumns.forEach(col => {
      visible[col] = arr.includes(col);
    });
    visible.colsSelected = arr.length;
    return visible;
  }

  columnsVisibleToArray(obj) {
    // Convert columnsVisible object to array of column ids
    return Object.keys(obj).filter(k => obj[k] && k !== 'colsSelected');
  }

  showToast = (message, type = 'success') => {
    this.setState({ toast: { show: true, message, type } });
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    this.toastTimeout = setTimeout(() => {
      this.setState({ toast: { show: false, message: '', type } });
    }, 3000);
  };

  handleSaveColumnSelection = async () => {
    try {
      const auth = getAuth();
      const db = getFirestore();
      const user = auth.currentUser;
      if (!user) throw new Error('Not logged in');
      const ref = doc(db, 'users', user.uid, 'columnPreferences', 'columnSelection');
      const arr = this.state.selectedColumns;
      await setDoc(ref, { selection: arr });
      this.showToast('Column selection saved!');
    } catch (err) {
      this.showToast('Error saving column selection: ' + err.message, 'error');
    }
  };

  handleRecallColumnSelection = async () => {
    try {
      const auth = getAuth();
      const db = getFirestore();
      const user = auth.currentUser;
      if (!user) throw new Error('Not logged in');
      const ref = doc(db, 'users', user.uid, 'columnPreferences', 'columnSelection');
      const snap = await getDoc(ref);
      if (snap.exists()) {
        let selection = snap.data().selection;
        if (Array.isArray(selection) && selection.length > 0) {
          // In public mode, filter to only allowed columns
          const isPublicMode = this.props.mode === 'public';
          if (isPublicMode) {
            const PUBLIC_ALLOWED = ['trackName', 'length', 'genre', 'bpm', 'key', 'tags', 'audioFiles'];
            selection = selection.filter(col => PUBLIC_ALLOWED.includes(col));
            if (selection.length === 0) selection = [...PUBLIC_ALLOWED];
          }
          this.setState({
            columnsVisible: this.columnsArrayToVisible(selection),
            selectedColumns: selection
          });
          this.showToast('Column selection recalled!');
        }
      } else {
        this.showToast('No saved column selection found.', 'error');
      }
    } catch (err) {
      this.showToast('Error recalling column selection: ' + err.message, 'error');
    }
  };

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.orgUnsubscribe) {
      this.orgUnsubscribe();
    }
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
    }
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
  }

  // Helper method to check if a filter object represents an empty/clear state
  checkIfEmptyFilter = (filter) => {
    if (!filter.filters) return false;
    
    // Handle the case where filter.filters is an array (chatbot clear sends empty arrays)
    if (Array.isArray(filter.filters)) {
      const filtersEmpty = filter.filters.length === 0;
      const filtersObjEmpty = (!filter.filtersObj || (Array.isArray(filter.filtersObj) && filter.filtersObj.length === 0));
      const keywordsEmpty = (!filter.keywords || (Array.isArray(filter.keywords) && filter.keywords.length === 0));
      
      return filtersEmpty && filtersObjEmpty && keywordsEmpty;
    }
    
    // Handle the case where filter.filters is an object (original logic)
    // Check if all array fields are empty
    const arrayFieldsEmpty = (
      (!filter.filters.tags || (Array.isArray(filter.filters.tags) && filter.filters.tags.length === 0)) &&
      (!filter.filters.tags2 || (Array.isArray(filter.filters.tags2) && filter.filters.tags2.length === 0)) &&
      (!filter.filters.genres || (Array.isArray(filter.filters.genres) && filter.filters.genres.length === 0)) &&
      (!filter.filters.keys || (Array.isArray(filter.filters.keys) && filter.filters.keys.length === 0)) &&
      (!filter.filters.agencies || (Array.isArray(filter.filters.agencies) && filter.filters.agencies.length === 0)) &&
      (!filter.filters.clients || (Array.isArray(filter.filters.clients) && filter.filters.clients.length === 0)) &&
      (!filter.filters.producers || (Array.isArray(filter.filters.producers) && filter.filters.producers.length === 0)) &&
      (!filter.filters.studios || (Array.isArray(filter.filters.studios) && filter.filters.studios.length === 0)) &&
      (!filter.filters.composers || (Array.isArray(filter.filters.composers) && filter.filters.composers.length === 0)) &&
      (!filter.filters.creativeDirectors || (Array.isArray(filter.filters.creativeDirectors) && filter.filters.creativeDirectors.length === 0)) &&
      (!filter.filters.sessionArtists || (Array.isArray(filter.filters.sessionArtists) && filter.filters.sessionArtists.length === 0)) &&
      (!filter.filters.date || (Array.isArray(filter.filters.date) && filter.filters.date.length === 0))
    );
    
    // Check if all string fields are empty
    const stringFieldsEmpty = (
      (!filter.filters.bpm || filter.filters.bpm === '') &&
      (!filter.filters.license || filter.filters.license === '') &&
      (!filter.filters.key || filter.filters.key === '') &&
      (!filter.filters.client || filter.filters.client === '') &&
      (!filter.filters.agency || filter.filters.agency === '') &&
      (!filter.filters.producer || filter.filters.producer === '') &&
      (!filter.filters.studio || filter.filters.studio === '')
    );
    
    // Check if keywords array is empty
    const keywordsEmpty = (!filter.keywords || (Array.isArray(filter.keywords) && filter.keywords.length === 0));
    
    // Check if filtersObj array is empty (for array-based processing)
    const filtersObjEmpty = (!filter.filtersObj || (Array.isArray(filter.filtersObj) && filter.filtersObj.length === 0));
    
    return arrayFieldsEmpty && stringFieldsEmpty && keywordsEmpty && filtersObjEmpty;
  };

  // Helper method to clear all filters
  clearAllFilters = () => {
    const emptyFilters = {
      tags: { selected: [], logic: 'or', data: [] },
      multi: { active: false, data: [] },
      genres: { selected: [], logic: 'or', data: [] },
      keys: { selected: [], logic: 'or', data: [] },
      bpm: { filterType: 'exact', exact: null, range: { min: '', max: '' }, active: false },
      date: { filterType: 'completed', range: { from: null, to: null }, active: false },
      composers: { selected: [], logic: 'or', data: [] },
      creativeDirectors: { selected: [], logic: 'or', data: [] },
      sessionArtists: { selected: [], logic: 'or', data: [] },
      agencies: { selected: [], logic: 'or', data: [] },
      clients: { selected: [], logic: 'or', data: [] },
      studios: { selected: [], logic: 'or', data: [] },
      producers: { selected: [], logic: 'or', data: [] },
      license: { value: undefined, active: false },
      text: { value: '', active: false },
      expandedSections: {
        trackName: false,
        license: false,
        tags: false,
        composers: false,
        creativeDirectors: false,
        sessionArtists: false,
        genres: false,
        agencies: false,
        clients: false,
        keys: false,
        studios: false,
        producers: false,
        bpm: false,
        date: false
      }
    };
    
    // Reset mnemonic override when clearing all filters
    //console.log('🎵 Clearing all filters - resetting mnemonic override to default state');
    
    this.setState({ 
      filterState: emptyFilters, 
      initialFilterState: emptyFilters,
      showMnemonicsOverride: false, // Reset to default filtering behavior
      hasMnemonicMatches: false, // Reset mnemonic matches state
      showLicensedOverride: false, // Reset to default filtering behavior
      hasLicensedMatches: false // Reset licensed matches state
    }, () => {
      // Update the searchFilter and reload data
      this.searchFilter = { ...emptyFilters };
      this.loadData();
    });
  };

  filterChanged = (filter) => {
    
    // Convert Gemini logic AND/OR logic to filter state logic properties (do this once, early)
    if (filter.logic) {
      if (filter.logic.genre && (filter.logic.genre === 'and' || filter.logic.genre === 'or')) {
        if (!filter.genres) filter.genres = {};
        filter.genres.logic = filter.logic.genre.toUpperCase();
      }
      if (filter.logic.tag && (filter.logic.tag === 'and' || filter.logic.tag === 'or')) {
        if (!filter.tags) filter.tags = {};
        filter.tags.logic = filter.logic.tag; // Keep lowercase for consistency
      }
      if (filter.logic.tag2 && (filter.logic.tag2 === 'and' || filter.logic.tag2 === 'or')) {
        if (!filter.tags2) filter.tags2 = {};
        filter.tags2.logic = filter.logic.tag2; // Keep lowercase for consistency
      }
    }
    
    try {
      // If this is a chatbot filter, convert it to the new multifilter format
      if (filter.filters) {
        // Check if this is an empty filter (all fields are empty/default values)
        const isEmptyFilter = this.checkIfEmptyFilter(filter);
        
        if (isEmptyFilter) {
          //console.log('🎵 Detected empty filter from chatbot - calling clearAllFilters()');
          this.clearAllFilters();
          return;
        }
        
        // Convert chatbot filters to the new format
        const newFilterState = {
          tags: {
            selected: [],
            logic: filter.tags?.logic || 'or'
          },
          tags2: {
            selected: [],
            logic: filter.tags2?.logic || 'or'
          },
          genres: {
            selected: [],
            logic: 'or'
          },
          bpm: {
            filterType: 'exact',
            exact: null,
            range: { min: '', max: '' }
          },
          creativeDirectors: {
            selected: [],
            logic: 'or'
          },
          sessionArtists: {
            selected: [],
            logic: 'or'
          },
          agencies: {
            selected: [],
            logic: 'or'
          },
          clients: {
            selected: [],
            logic: 'or'
          },
          keys: {
            selected: [],
            logic: 'or'
          },
          studios: {
            selected: [],
            logic: 'or'
          },
          producers: {
            selected: [],
            logic: 'or'
          },
          composers: {
            selected: [],
            logic: 'or'
          },
          date: {
            filterType: 'completed',
            range: {
              from: null,
              to: null
            }
          },
          expandedSections: {
            trackName: false,
            license: false,
            tags: false,
            tags2: false,
            composers: false,
            creativeDirectors: false,
            sessionArtists: false,
            genres: false,
            agencies: false,
            clients: false,
            keys: false,
            studios: false,
            producers: false,
            bpm: false,
            date: false
          }
        };

        // NEW: Process direct filter fields (filters.tags, filters.tags2, filters.genres, etc.)
        // This supports the new pattern where Gemini outputs direct filter values
        let directTagsProcessed = false;
        let directTags2Processed = false;
        let directGenresProcessed = false;
        let directKeysProcessed = false;
        let directAgenciesProcessed = false;
        let directClientsProcessed = false;
        let directProducersProcessed = false;
        let directStudiosProcessed = false;
        let directComposersProcessed = false;
        let directCreativeDirectorsProcessed = false;
        let directSessionArtistsProcessed = false;
        let directBpmProcessed = false;
        let directLicenseProcessed = false;
        let directDateProcessed = false;
        
        if (filter.filters.tags && Array.isArray(filter.filters.tags) && filter.filters.tags.length > 0) {
          newFilterState.tags.selected = filter.filters.tags.map(tag => ({
            id: tag,
            name: tag
          }));
          newFilterState.tags.logic = (filter.logic && filter.logic.tag) ? filter.logic.tag : 'or';
          newFilterState.expandedSections.tags = true;
          directTagsProcessed = true;
        }

        if (filter.filters.tags2 && Array.isArray(filter.filters.tags2) && filter.filters.tags2.length > 0) {
          newFilterState.tags2.selected = filter.filters.tags2.map(tag => ({
            id: tag,
            name: tag
          }));
          newFilterState.tags2.logic = (filter.logic && filter.logic.tag2) ? filter.logic.tag2 : 'or';
          newFilterState.expandedSections.tags2 = true;
          directTags2Processed = true;
        }

        if (filter.filters.genres && Array.isArray(filter.filters.genres) && filter.filters.genres.length > 0) {
          newFilterState.genres.selected = filter.filters.genres.map(genre => ({
            id: genre,
            name: genre
          }));
          newFilterState.genres.logic = (filter.logic && filter.logic.genre) ? filter.logic.genre : 'or';
          newFilterState.expandedSections.genres = true;
          directGenresProcessed = true;
        }

        if (filter.filters.keys && Array.isArray(filter.filters.keys) && filter.filters.keys.length > 0) {
          newFilterState.keys.selected = filter.filters.keys.map(key => ({
            id: key,
            name: key
          }));
          newFilterState.keys.logic = (filter.logic && filter.logic.key) ? filter.logic.key : 'or';
          newFilterState.expandedSections.keys = true;
          directKeysProcessed = true;
        }

        if (filter.filters.agencies && Array.isArray(filter.filters.agencies) && filter.filters.agencies.length > 0) {
          newFilterState.agencies.selected = filter.filters.agencies.map(agency => ({
            id: agency,
            name: agency
          }));
          newFilterState.agencies.logic = (filter.logic && filter.logic.agency) ? filter.logic.agency : 'or';
          newFilterState.expandedSections.agencies = true;
          directAgenciesProcessed = true;
        }

        if (filter.filters.clients && Array.isArray(filter.filters.clients) && filter.filters.clients.length > 0) {
          newFilterState.clients.selected = filter.filters.clients.map(client => ({
            id: client,
            name: client
          }));
          newFilterState.clients.logic = (filter.logic && filter.logic.client) ? filter.logic.client : 'or';
          newFilterState.expandedSections.clients = true;
          directClientsProcessed = true;
        }

        if (filter.filters.producers && Array.isArray(filter.filters.producers) && filter.filters.producers.length > 0) {
          newFilterState.producers.selected = filter.filters.producers.map(producer => ({
            id: producer,
            name: producer
          }));
          newFilterState.producers.logic = (filter.logic && filter.logic.producer) ? filter.logic.producer : 'or';
          newFilterState.expandedSections.producers = true;
          directProducersProcessed = true;
        }

        if (filter.filters.studios && Array.isArray(filter.filters.studios) && filter.filters.studios.length > 0) {
          newFilterState.studios.selected = filter.filters.studios.map(studio => ({
            id: studio,
            name: studio
          }));
          newFilterState.studios.logic = (filter.logic && filter.logic.studio) ? filter.logic.studio : 'or';
          newFilterState.expandedSections.studios = true;
          directStudiosProcessed = true;
        }

        if (filter.filters.composers && Array.isArray(filter.filters.composers) && filter.filters.composers.length > 0) {
          newFilterState.composers.selected = filter.filters.composers.map(composer => ({
            id: composer,
            name: composer
          }));
          newFilterState.composers.logic = (filter.logic && filter.logic.composer) ? filter.logic.composer : 'or';
          newFilterState.expandedSections.composers = true;
          directComposersProcessed = true;
        }

        if (filter.filters.creativeDirectors && Array.isArray(filter.filters.creativeDirectors) && filter.filters.creativeDirectors.length > 0) {
          newFilterState.creativeDirectors.selected = filter.filters.creativeDirectors.map(director => ({
            id: director,
            name: director
          }));
          newFilterState.creativeDirectors.logic = (filter.logic && filter.logic.creativedirector) ? filter.logic.creativedirector : 'or';
          newFilterState.expandedSections.creativeDirectors = true;
          directCreativeDirectorsProcessed = true;
        }

        if (filter.filters.sessionArtists && Array.isArray(filter.filters.sessionArtists) && filter.filters.sessionArtists.length > 0) {
          newFilterState.sessionArtists.selected = filter.filters.sessionArtists.map(artist => ({
            id: artist,
            name: artist
          }));
          newFilterState.sessionArtists.logic = (filter.logic && filter.logic.sessionartist) ? filter.logic.sessionartist : 'or';
          newFilterState.expandedSections.sessionArtists = true;
          directSessionArtistsProcessed = true;
        }

        // Handle direct BPM processing
        if (filter.filters.bpm && typeof filter.filters.bpm === 'string') {
          if (filter.filters.bpm.includes('-')) {
            // BPM range (including partial ranges like "140-" or "-80")
            const [minStr, maxStr] = filter.filters.bpm.split('-');
            const min = minStr && minStr.trim() !== '' ? Number(minStr) : null;
            const max = maxStr && maxStr.trim() !== '' ? Number(maxStr) : null;
            
            // Handle complete range (min-max)
            if (min !== null && max !== null && !isNaN(min) && !isNaN(max)) {
              newFilterState.bpm.filterType = 'range';
              newFilterState.bpm.range = { min: min.toString(), max: max.toString() };
              newFilterState.bpm.exact = null;
              newFilterState.expandedSections.bpm = true;
              directBpmProcessed = true;
            }
            // Handle minimum-only range (min-)
            else if (min !== null && !isNaN(min) && (maxStr === '' || maxStr === undefined)) {
              newFilterState.bpm.filterType = 'range';
              newFilterState.bpm.range = { min: min.toString(), max: '' };
              newFilterState.bpm.exact = null;
              newFilterState.expandedSections.bpm = true;
              directBpmProcessed = true;
            }
            // Handle maximum-only range (-max)
            else if (max !== null && !isNaN(max) && (minStr === '' || minStr === undefined)) {
              newFilterState.bpm.filterType = 'range';
              newFilterState.bpm.range = { min: '', max: max.toString() };
              newFilterState.bpm.exact = null;
              newFilterState.expandedSections.bpm = true;
              directBpmProcessed = true;
            }
          } else {
            // Single BPM
            const bpmValue = Number(filter.filters.bpm);
            if (!isNaN(bpmValue)) {
              newFilterState.bpm.filterType = 'exact';
              newFilterState.bpm.exact = { id: bpmValue.toString(), label: bpmValue.toString(), value: bpmValue };
              newFilterState.bpm.range = { min: '', max: '' };
              newFilterState.expandedSections.bpm = true;
              directBpmProcessed = true;
            }
          }
        }

        // Handle direct license processing
        if (filter.filters.license && typeof filter.filters.license === 'string') {
          newFilterState.license = { value: filter.filters.license === 'licensed', active: true };
          newFilterState.expandedSections.license = true;
          directLicenseProcessed = true;
        }

        // Handle direct date processing
        if (filter.filters.date && Array.isArray(filter.filters.date) && filter.filters.date.length >= 1) {
          if (filter.filters.date.length >= 2) {
            // Date range
            const fromDate = new Date(filter.filters.date[0]);
            const toDate = new Date(filter.filters.date[1]);
            if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
              // Set to start and end of day
              fromDate.setHours(0, 0, 0, 0);
              toDate.setHours(23, 59, 59, 999);
              
              newFilterState.date.range = {
                from: fromDate.getTime(),
                to: toDate.getTime()
              };
              newFilterState.date.filterType = 'completed';
              newFilterState.expandedSections.date = true;
              directDateProcessed = true;
            }
          } else {
            // Single date - from that date to now
            const fromDate = new Date(filter.filters.date[0]);
            if (!isNaN(fromDate.getTime())) {
              fromDate.setHours(0, 0, 0, 0);
              newFilterState.date.range = {
                from: fromDate.getTime(),
                to: Date.now()
              };
              newFilterState.date.filterType = 'completed';
              newFilterState.expandedSections.date = true;
              directDateProcessed = true;
            }
          }
        }

        // Helper function to add filter without duplicates
        const addUniqueFilter = (filterState, filterType, value) => {
          const existing = filterState.selected.find(item => item.id === value);
          if (!existing) {
            if (filterState.selected.length > 0) {
              filterState.selected.push({ id: value, name: value });
            } else {
              filterState.selected = [{ id: value, name: value }];
            }
            return true; // Added new item
          }
          return false; // Duplicate found, not added
        };

        // Helper function to normalize filter types for consistency
        const normalizeFilterType = (type) => {
          const typeMap = {
            'creativedirector': 'creativeDirector',
            'sessionartist': 'sessionArtist',
            'sessionartists': 'sessionArtist', // Handle plural form too
            'agencie': 'agency' // Fix the typo too
          };
          return typeMap[type] || type;
        };

        // NEW: Handle array format filters from chatbot (e.g., [{type: 'genre', value: 'pop'}, {type: 'composer', value: 'Paul Norwood'}])
        let arrayFormatProcessed = false;
        if (Array.isArray(filter.filters)) {
          
          arrayFormatProcessed = true;
          filter.filters.forEach((f, index) => {
            
            // Normalize the filter type
            const normalizedType = normalizeFilterType(f.type);
            
            if (normalizedType === 'genre') {
              if (addUniqueFilter(newFilterState.genres, 'genre', f.value)) {
                newFilterState.genres.logic = 'or';
                newFilterState.expandedSections.genres = true;
              }
              directGenresProcessed = true;
            } else if (normalizedType === 'tag') {
              if (addUniqueFilter(newFilterState.tags, 'tag', f.value)) {
                newFilterState.tags.logic = 'or';
                newFilterState.expandedSections.tags = true;
              }
              directTagsProcessed = true;
            } else if (normalizedType === 'tags2') {
              
              // DON'T check directTags2Processed here - we want to process ALL tags2 filters in the array
              // Handle array values properly - extract individual items
              const values = Array.isArray(f.value) ? f.value : [f.value];
              
              values.forEach((value, valueIndex) => {
                
                if (addUniqueFilter(newFilterState.tags2, 'tags2', value)) {
                  newFilterState.tags2.logic = (filter.logic && filter.logic.tag2) ? filter.logic.tag2 : 'or';
                  newFilterState.expandedSections.tags2 = true;
                } else {
                }
              });
              
              // Set this to indicate we've processed at least one tags2 filter (for other logic)
              directTags2Processed = true;
            } else if (normalizedType === 'composer') {
              if (addUniqueFilter(newFilterState.composers, 'composer', f.value)) {
                newFilterState.composers.logic = 'or';
                newFilterState.expandedSections.composers = true;
              }
            } else if (normalizedType === 'producer') {
              if (addUniqueFilter(newFilterState.producers, 'producer', f.value)) {
                newFilterState.producers.logic = 'or';
                newFilterState.expandedSections.producers = true;
              }
            } else if (normalizedType === 'agency') {
              if (addUniqueFilter(newFilterState.agencies, 'agency', f.value)) {
                newFilterState.agencies.logic = 'or';
                newFilterState.expandedSections.agencies = true;
              }
            } else if (normalizedType === 'client') {
              if (addUniqueFilter(newFilterState.clients, 'client', f.value)) {
                newFilterState.clients.logic = 'or';
                newFilterState.expandedSections.clients = true;
              }
            } else if (normalizedType === 'key') {
              if (addUniqueFilter(newFilterState.keys, 'key', f.value)) {
                newFilterState.keys.logic = 'or';
                newFilterState.expandedSections.keys = true;
              }
            } else if (normalizedType === 'studio') {
              if (addUniqueFilter(newFilterState.studios, 'studio', f.value)) {
                newFilterState.studios.logic = 'or';
                newFilterState.expandedSections.studios = true;
              }
            } else if (normalizedType === 'creativeDirector') {
              if (addUniqueFilter(newFilterState.creativeDirectors, 'creativeDirector', f.value)) {
                newFilterState.creativeDirectors.logic = 'or';
                newFilterState.expandedSections.creativeDirectors = true;
              }
            } else if (normalizedType === 'sessionArtist') {
              if (addUniqueFilter(newFilterState.sessionArtists, 'sessionArtist', f.value)) {
                newFilterState.sessionArtists.logic = 'or';
                newFilterState.expandedSections.sessionArtists = true;
              } else {
              }
            } else {
            }
            
          });
          
        }

        // Process each filter (existing keyword-based approach)
        // If array format was processed, only handle non-duplicate filter types (bpm, date, license)
        if (Array.isArray(filter.filtersObj)) {
          filter.filtersObj.forEach(f => {
          
          // Skip filter types that were already processed in array format
          const skipInArrayFormat = arrayFormatProcessed && (
            f.type === 'tags' || f.type === 'tags2' || f.type === 'genre' || f.type === 'composer' || 
            f.type === 'producer' || f.type === 'agency' || f.type === 'client' || 
            f.type === 'key' || f.type === 'studio' || f.type === 'creativedirector' || 
            f.type === 'sessionartist'
          );
          
          if (skipInArrayFormat) {
            return; // Skip this iteration
          }
          
          if (f.type === 'tags' && !directTagsProcessed) {
            // Handle tags filter from keyword processing
            newFilterState.tags.selected = Array.isArray(f.value) 
              ? f.value.map(v => ({ id: v, name: v }))
              : [{ id: f.value, name: f.value }];
            newFilterState.tags.logic = (filter.tags && filter.tags.logic) ? filter.tags.logic : 'or';
            newFilterState.expandedSections.tags = true;
          } else if (f.type === 'tags2' && !directTags2Processed) {
            // Handle tags2 filter from keyword processing
            newFilterState.tags2.selected = Array.isArray(f.value) 
              ? f.value.map(v => ({ id: v, name: v }))
              : [{ id: f.value, name: f.value }];
            newFilterState.tags2.logic = (filter.logic && filter.logic.tag2) ? filter.logic.tag2 : 'or';
            newFilterState.expandedSections.tags2 = true;
            directTags2Processed = true;
          } else if (f.type === 'genre' && !directGenresProcessed) {
            // Handle genre filter from keyword processing
            // Check if we already have genres selected
            if (newFilterState.genres.selected.length > 0) {
              // Add to existing genres instead of replacing
              newFilterState.genres.selected.push({
                id: f.value,
                name: f.value
              });
            } else {
              // Initialize with first genre
              newFilterState.genres.selected = [{
                id: f.value,
                name: f.value
              }];
            }
            newFilterState.genres.logic = 'or';
            newFilterState.expandedSections.genres = true;
          } else if (f.type === 'bpm' && !directBpmProcessed) {
            // console.log('   - Processing BPM filter with value:', f.value);
            // Handle BPM filter
            if (f.value) {
              const bpmValue = typeof f.value === 'string' ? f.value : f.value.value;
              if (bpmValue) {
                if (bpmValue.includes('-')) {
                  // Handle BPM range
                  const [minStr, maxStr] = bpmValue.split('-');
                  const min = minStr && minStr.trim() !== '' ? Number(minStr) : null;
                  const max = maxStr && maxStr.trim() !== '' ? Number(maxStr) : null;
                  
                  // Handle complete range (min-max)
                  if (min !== null && max !== null && !isNaN(min) && !isNaN(max)) {
                    newFilterState.bpm = {
                      filterType: 'range',
                      exact: null,
                      range: {
                        min: min.toString(),
                        max: max.toString()
                      }
                    };
                    newFilterState.expandedSections.bpm = true;
                    // console.log('   - Updated BPM filter with range:', JSON.stringify(newFilterState.bpm, null, 2));
                  }
                  // Handle minimum-only range (min-)
                  else if (min !== null && !isNaN(min) && (maxStr === '' || maxStr === undefined)) {
                    newFilterState.bpm = {
                      filterType: 'range',
                      exact: null,
                      range: {
                        min: min.toString(),
                        max: ''
                      }
                    };
                    newFilterState.expandedSections.bpm = true;
                  }
                  // Handle maximum-only range (-max)
                  else if (max !== null && !isNaN(max) && (minStr === '' || minStr === undefined)) {
                    newFilterState.bpm = {
                      filterType: 'range',
                      exact: null,
                      range: {
                        min: '',
                        max: max.toString()
                      }
                    };
                    newFilterState.expandedSections.bpm = true;
                  } else {
                    // console.log('   - Invalid BPM range values:', min, max);
                  }
                } else {
                  // Handle single BPM
                  const bpm = Number(bpmValue);
                  if (!isNaN(bpm)) {
                    newFilterState.bpm = {
                      filterType: 'exact',
                      exact: {
                        id: bpmValue,
                        label: bpmValue,
                        value: bpm
                      },
                      range: {
                        min: '',
                        max: ''
                      }
                    };
                    newFilterState.expandedSections.bpm = true;
                    // console.log('   - Updated BPM filter with exact value:', JSON.stringify(newFilterState.bpm, null, 2));
                  } else {
                    // console.log('   - Invalid BPM value:', bpmValue);
                  }
                }
              } else {
                // console.log('   - No BPM value provided');
              }
            } else {
              // console.log('   - No BPM value object provided');
            }
          } else if (f.type === 'license' && !directLicenseProcessed) {
            // console.log('   - Processing License filter with value:', f.value);
            // Handle license filter
            newFilterState.license = {
              active: true,
              value: f.value === 'licensed'
            };
            newFilterState.expandedSections.license = true;
            // console.log('   - Updated License filter:', JSON.stringify(newFilterState.license, null, 2));
          } else if (f.type === 'creativedirector' && !directCreativeDirectorsProcessed) {
            // console.log('   - Processing Creative Director filter with value:', f.value);
            // Handle creative director filter
            newFilterState.creativeDirectors.selected = Array.isArray(f.value) 
              ? f.value.map(v => ({ id: v, name: v }))
              : [{ id: f.value, name: f.value }];
            newFilterState.creativeDirectors.logic = 'or';
            newFilterState.expandedSections.creativeDirectors = true;
            // console.log('   - Updated Creative Director filter:', JSON.stringify(newFilterState.creativeDirectors, null, 2));
            // console.log('Updated Creative Director filter:', JSON.stringify(newFilterState.creativeDirectors, null, 2));
          } else if (f.type === 'sessionartists' && !directSessionArtistsProcessed) {
            // console.log('Processing session artists filter:', f);
            // Update session artists filter
            if (Array.isArray(f.value)) {
              if (newFilterState.sessionArtists.selected.length > 0) {
                // Add to existing session artists instead of replacing
                newFilterState.sessionArtists.selected.push(...f.value.map(v => ({ id: v, name: v })));
                // console.log('Added to existing session artists. New session artists:', JSON.stringify(newFilterState.sessionArtists.selected));
              } else {
                // Initialize with first session artist
                newFilterState.sessionArtists.selected = f.value.map(v => ({ id: v, name: v }));
                // console.log('Initialized session artists. New session artists:', JSON.stringify(newFilterState.sessionArtists.selected));
              }
            } else {
              if (newFilterState.sessionArtists.selected.length > 0) {
                // Add to existing session artists instead of replacing
                newFilterState.sessionArtists.selected.push({ id: f.value, name: f.value });
                // console.log('Added to existing session artists. New session artists:', JSON.stringify(newFilterState.sessionArtists.selected));
              } else {
                // Initialize with first session artist
                newFilterState.sessionArtists.selected = [{ id: f.value, name: f.value }];
                // console.log('Initialized session artists. New session artists:', JSON.stringify(newFilterState.sessionArtists.selected));
              }
            }
            // console.log('Updated session artists filter:', newFilterState.sessionArtists);
            // console.log('Updated session artists filter:', JSON.stringify(newFilterState.sessionArtists, null, 2));
          } else if (f.type === 'agency' && !directAgenciesProcessed) {
            // console.log(`   - Processing Agency filter with value:`, f.value);
            // Handle agency filter
            // Check if we already have agencies selected
            if (newFilterState.agencies.selected.length > 0) {
              // Add to existing agencies instead of replacing
              newFilterState.agencies.selected.push({
                id: f.value,
                name: f.value
              });
            } else {
              // Initialize with first agency
              newFilterState.agencies.selected = [{
                id: f.value,
                name: f.value
              }];
            }
            newFilterState.agencies.logic = 'or';
            newFilterState.expandedSections.agencies = true;
            // console.log(`   - Updated Agency filter:`, JSON.stringify(newFilterState.agencies, null, 2));
            // console.log(`Updated Agency filter:`, JSON.stringify(newFilterState.agencies, null, 2));
          } else if (f.type === 'client' && !directClientsProcessed) {
            // console.log(`   - Processing Client filter with value:`, f.value);
            // Handle client filter
            // Check if we already have clients selected
            if (newFilterState.clients.selected.length > 0) {
              // Add to existing clients instead of replacing
              newFilterState.clients.selected.push({
                id: f.value,
                name: f.value
              });
            } else {
              // Initialize with first client
              newFilterState.clients.selected = [{
                id: f.value,
                name: f.value
              }];
            }
            newFilterState.clients.logic = 'or';
            newFilterState.expandedSections.clients = true;
            // console.log(`   - Updated Client filter:`, JSON.stringify(newFilterState.clients, null, 2));
            // console.log(`Updated Client filter:`, JSON.stringify(newFilterState.clients, null, 2));
          } else if (f.type === 'key' && !directKeysProcessed) {
            // console.log(`   - Processing Key filter with value:`, f.value);
            // Handle key filter
            // Check if we already have keys selected
            if (newFilterState.keys.selected.length > 0) {
              // Add to existing keys instead of replacing
              newFilterState.keys.selected.push({
                id: f.value,
                name: f.value
              });
            } else {
              // Initialize with first key
              newFilterState.keys.selected = [{
                id: f.value,
                name: f.value
              }];
            }
            newFilterState.keys.logic = 'or';
            newFilterState.expandedSections.keys = true;
            // console.log(`   - Updated Key filter:`, JSON.stringify(newFilterState.keys, null, 2));
            // console.log(`Updated Key filter:`, JSON.stringify(newFilterState.keys, null, 2));
          } else if (f.type === 'studio' && !directStudiosProcessed) {
            // console.log(`   - Processing Studio filter with value:`, f.value);
            // Handle studio filter
            // Check if we already have studios selected
            if (newFilterState.studios.selected.length > 0) {
              // Add to existing studios instead of replacing
              newFilterState.studios.selected.push({
                id: f.value,
                name: f.value
              });
            } else {
              // Initialize with first studio
              newFilterState.studios.selected = [{
                id: f.value,
                name: f.value
              }];
            }
            newFilterState.studios.logic = 'or';
            newFilterState.expandedSections.studios = true;
            // console.log(`   - Updated Studio filter:`, JSON.stringify(newFilterState.studios, null, 2));
            // console.log(`Updated Studio filter:`, JSON.stringify(newFilterState.studios, null, 2));
          } else if (f.type === 'producer' && !directProducersProcessed) {
            // console.log(`   - Processing Producer filter with value:`, f.value);
            // Handle producer filter
            // Check if we already have producers selected
            if (newFilterState.producers.selected.length > 0) {
              // Add to existing producers instead of replacing
              newFilterState.producers.selected.push({
                id: f.value,
                name: f.value
              });
            } else {
              // Initialize with first producer
              newFilterState.producers.selected = [{
                id: f.value,
                name: f.value
              }];
            }
            newFilterState.producers.logic = 'or';
            newFilterState.expandedSections.producers = true;
            // console.log(`   - Updated Producer filter:`, JSON.stringify(newFilterState.producers, null, 2));
            // console.log(`Updated Producer filter:`, JSON.stringify(newFilterState.producers, null, 2));
          } else if (f.type === 'composer' && !directComposersProcessed) {
            // console.log(`   - Processing Composer filter with value:`, f.value);
            // Handle composer filter
            // Check if we already have composers selected
            if (newFilterState.composers.selected.length > 0) {
              // Add to existing composers instead of replacing
              newFilterState.composers.selected.push({
                id: f.value,
                name: f.value
              });
            } else {
              // Initialize with first composer
              newFilterState.composers.selected = [{
                id: f.value,
                name: f.value
              }];
            }
            newFilterState.composers.logic = 'or';
            newFilterState.expandedSections.composers = true;
            // console.log(`   - Updated Composer filter:`, JSON.stringify(newFilterState.composers, null, 2));
            // console.log(`Updated Composer filter:`, JSON.stringify(newFilterState.composers, null, 2));
          } else if (f.type === 'date' && !directDateProcessed) {
            // console.log('   - Processing Date filter with value:', f.value);
            // Handle date filter
            if (f.value && (f.value.range?.from || f.value.range?.to)) {
              newFilterState.date = {
                filterType: f.value.filterType || 'completed',
                range: {
                  from: f.value.range.from || null,
                  to: f.value.range.to || null
                }
              };
              newFilterState.expandedSections.date = true;
              // console.log('   - Updated Date filter:', JSON.stringify(newFilterState.date, null, 2));
            } else {
              // Reset date filter if no valid range is provided
              newFilterState.date = {
                filterType: 'completed',
                range: {
                  from: null,
                  to: null
                }
              };
              // console.log('   - Reset Date filter to default');
            }
          } else {
            // console.log(`   - Unknown filter type: ${f.type}`);
          }
        });
        }

        // console.log('4. Final converted filter state:', JSON.stringify(newFilterState, null, 2));

        // === Overwrite tags logic/searchOperator from Gemini logic if present ===
        if (filter.logic && filter.logic.tag && (filter.logic.tag === 'and' || filter.logic.tag === 'or')) {
          if (newFilterState.tags) {
            newFilterState.tags.logic = filter.logic.tag;
            newFilterState.tags.searchOperator = filter.logic.tag.toUpperCase();
          }
        }
        // Ensure the logic property is always set for handleMultiFilterChange
        if (newFilterState.tags) {
          newFilterState.tags.logic = (filter.logic && filter.logic.tag) ? filter.logic.tag : (newFilterState.tags.logic || 'or');
        }
        
        // === Overwrite tags2 logic/searchOperator from Gemini logic if present ===
        if (filter.logic && filter.logic.tag2 && (filter.logic.tag2 === 'and' || filter.logic.tag2 === 'or')) {
          if (newFilterState.tags2) {
            newFilterState.tags2.logic = filter.logic.tag2;
            newFilterState.tags2.searchOperator = filter.logic.tag2.toUpperCase();
          }
        }
        // Ensure the logic property is always set for handleMultiFilterChange
        if (newFilterState.tags2) {
          newFilterState.tags2.logic = (filter.logic && filter.logic.tag2) ? filter.logic.tag2 : (newFilterState.tags2.logic || 'or');
        }
        
        // === Overwrite genres logic/searchOperator from Gemini logic if present ===
        if (filter.logic && filter.logic.genre && (filter.logic.genre === 'and' || filter.logic.genre === 'or')) {
          if (newFilterState.genres) {
            newFilterState.genres.logic = filter.logic.genre;
            newFilterState.genres.searchOperator = filter.logic.genre.toUpperCase();
          }
        }
        // Ensure the logic property is always set for handleMultiFilterChange
        if (newFilterState.genres) {
          newFilterState.genres.logic = (filter.logic && filter.logic.genre) ? filter.logic.genre : (newFilterState.genres.logic || 'or');
        }
        // Preserve excluded chips from the store when updating filters
        const excludedChipDataBySection = useExcludedChipsStore.getState().getExcludedChipDataBySection();
        
        // Add excluded items back to the filter state
        Object.entries(excludedChipDataBySection).forEach(([section, excludedItems]) => {
          if (excludedItems.length === 0) return;
          
          // Map section names to filter state keys
          let filterKey;
          let excludedKey;
          switch (section) {
            case 'tags':
              filterKey = 'tags';
              excludedKey = 'excludedTags';
              break;
            case 'tags2':
              filterKey = 'tags2';
              excludedKey = 'excludedTags2';
              break;
            case 'genres':
              filterKey = 'genres';
              excludedKey = 'excludedGenres';
              break;
            case 'keys':
              filterKey = 'keys';
              excludedKey = 'excludedKeys';
              break;
            case 'studios':
              filterKey = 'studios';
              excludedKey = 'excludedStudios';
              break;
            case 'agencies':
              filterKey = 'agencies';
              excludedKey = 'excludedAgencies';
              break;
            case 'clients':
              filterKey = 'clients';
              excludedKey = 'excludedClients';
              break;
            case 'producers':
              filterKey = 'producers';
              excludedKey = 'excludedProducers';
              break;
            case 'composers':
              filterKey = 'composers';
              excludedKey = 'excludedComposers';
              break;
            case 'creativeDirectors':
              filterKey = 'creativeDirectors';
              excludedKey = 'excludedCreativeDirectors';
              break;
            case 'sessionArtists':
              filterKey = 'sessionArtists';
              excludedKey = 'excludedSessionArtists';
              break;
            default:
              return;
          }
          
          // Add excluded items to both selected array AND excluded array
          if (newFilterState[filterKey]) {
            // Initialize excluded array if it doesn't exist
            if (!newFilterState[filterKey].excluded) {
              newFilterState[filterKey].excluded = [];
            }
            
            excludedItems.forEach(chipData => {
              // Add to selected array if not already there
              const alreadyInSelected = newFilterState[filterKey].selected.some(
                item => item.id === chipData.id || item.name === chipData.name
              );
              
              if (!alreadyInSelected) {
                newFilterState[filterKey].selected.push({
                  id: chipData.id,
                  name: chipData.name,
                  label: chipData.label || chipData.name,
                  ...chipData
                });
              }
              
              // Add to excluded array
              const alreadyInExcluded = newFilterState[filterKey].excluded.some(
                item => item.id === chipData.id || item.name === chipData.name
              );
              
              if (!alreadyInExcluded) {
                newFilterState[filterKey].excluded.push({
                  id: chipData.id,
                  name: chipData.name,
                  label: chipData.label || chipData.name,
                  ...chipData
                });
              }
            });
            
            // Also create the excludedXXX structure for filter processing
            if (!newFilterState[excludedKey]) {
              newFilterState[excludedKey] = {
                active: true,
                searchOperator: 'OR',
                data: []
              };
            }
            
            newFilterState[excludedKey].data = excludedItems.map(chipData => ({
              label: chipData.label || chipData.name,
              value: chipData.name
            }));
            
            // Ensure the section is expanded if it has excluded items
            if (newFilterState.expandedSections) {
              newFilterState.expandedSections[filterKey] = true;
            }
          }
        });

        // Update the TestMUITags component's state through initialFilterState
        this.setState({
          initialFilterState: newFilterState
        }, async () => {
          // Call handleMultiFilterChange with the converted filter
          this.handleMultiFilterChange(newFilterState);
          
          // After initial filter application, check if results are too restrictive
          // and apply progressive relaxation if needed
          if (ENABLE_PROGRESSIVE_FILTER_RELAXATION && this.shouldAutoRelaxFilters(filter)) {
            await this.applyProgressiveFilterRelaxation(newFilterState);
          }
        });
        
        return;
      }

      // Handle regular filter changes
      // console.log('6. Processing regular filter change');
      this.searchFilter = filter;
      
      // Only call loadData if the filter has actually changed
      const hasFilterChanged = JSON.stringify(this.searchFilter) !== JSON.stringify(filter);
      if (hasFilterChanged) {
        this.loadData();
      }
    } catch (error) {
      console.error('Error in filterChanged:', error);
    }
  };

  /**
   * Determines if auto-relaxation should be applied based on the filter source
   * @param {Object} filter - The original filter object from chatbot
   * @returns {boolean} - Whether to apply auto-relaxation
   */
  shouldAutoRelaxFilters = (filter) => {
    // Only apply auto-relaxation for chatbot filters (especially audio analysis)
    // Check if this came from chatbot by looking for the filters structure
    return filter && filter.filters && (
      // Has tags2 with multiple items (typical for audio analysis)
      (Array.isArray(filter.filters.tags2) && filter.filters.tags2.length > 2) ||
      // Or has both tags2 and other restrictive filters
      (filter.filters.tags2 && (filter.filters.tags || filter.filters.genres))
    );
  };

  /**
   * Progressively relaxes filters when results are too restrictive
   * @param {Object} currentFilterState - The current filter state
   * @returns {Promise<void>}
   */
  applyProgressiveFilterRelaxation = async (currentFilterState) => {
    const MIN_RESULTS_THRESHOLD = 10;
    let relaxedFilterState = JSON.parse(JSON.stringify(currentFilterState));
    let trackCount = await this.getFilteredTrackCount(relaxedFilterState);
    
    console.log('[FilterRelaxation] Initial track count:', trackCount);
    
    // If we already have enough results, no need to relax
    if (trackCount >= MIN_RESULTS_THRESHOLD) {
      return;
    }

    // Track what we've tried
    let relaxationSteps = [];

    // Step 1: Progressively remove tags2 items based on priority (skip logic change)
    if (relaxedFilterState.tags2?.selected?.length > 0) {
      const removalPriority = this.getTag2RemovalPriority(relaxedFilterState.tags2.selected);
      
      for (const tagToRemove of removalPriority) {
        // Remove the tag
        relaxedFilterState.tags2.selected = relaxedFilterState.tags2.selected.filter(
          tag => tag.id !== tagToRemove.id
        );
        relaxationSteps.push(`Removed tags2: ${tagToRemove.name}`);
        
        trackCount = await this.getFilteredTrackCount(relaxedFilterState);
        console.log(`[FilterRelaxation] After removing ${tagToRemove.name}:`, trackCount);
        
        if (trackCount >= MIN_RESULTS_THRESHOLD) {
          this.applyRelaxedFilters(relaxedFilterState, relaxationSteps);
          return;
        }
        
        // Stop if we've removed all but one tag
        if (relaxedFilterState.tags2.selected.length <= 1) {
          break;
        }
      }
    }

    // Step 2: Remove tags (mood/style) filters if still too restrictive
    if (relaxedFilterState.tags?.selected?.length > 0) {
      relaxedFilterState.tags.selected = [];
      relaxationSteps.push('Removed all mood/style tags');
      
      trackCount = await this.getFilteredTrackCount(relaxedFilterState);
      console.log('[FilterRelaxation] After removing tags:', trackCount);
      
      if (trackCount >= MIN_RESULTS_THRESHOLD) {
        this.applyRelaxedFilters(relaxedFilterState, relaxationSteps);
        return;
      }
    }

    // Step 3: If still too restrictive, remove genres
    if (relaxedFilterState.genres?.selected?.length > 0) {
      relaxedFilterState.genres.selected = [];
      relaxationSteps.push('Removed all genres');
      
      trackCount = await this.getFilteredTrackCount(relaxedFilterState);
      console.log('[FilterRelaxation] After removing genres:', trackCount);
    }

    // Apply whatever we ended up with
    if (relaxationSteps.length > 0) {
      this.applyRelaxedFilters(relaxedFilterState, relaxationSteps);
    }
  };

  /**
   * Determines the priority order for removing tags2 items
   * @param {Array} tags2Selected - Array of selected tags2 items
   * @returns {Array} - Ordered array of tags to remove (first = remove first)
   */
  getTag2RemovalPriority = (tags2Selected) => {
    const priorityCategories = {
      structural: ['Electronic', 'Acoustic', 'Orchestral', 'Hybrid'],
      synthFirst: ['Synth'], // Remove synth first among instruments
      instruments: ['Guitar', 'Piano', 'Drums', 'Bass', 'Strings'],
      vocalsLast: ['Vocals', 'Male Vocals', 'Female Vocals'], // Remove vocals last among instruments
      temporal: ["80's", "90's", "70's", "60's"],
      descriptive: ['Cool', 'Contemporary', 'Modern', 'Vintage', 'Retro'],
      contentType: ['Sting', 'Mnemonic']
    };

    // Sort tags by priority (remove structural first, keep descriptive last)
    // New order: structural -> synth -> instruments -> vocals -> temporal -> descriptive
    const sorted = [...tags2Selected].sort((a, b) => {
      const getPriority = (tag) => {
        const name = tag.name || tag.id;
        if (priorityCategories.structural.includes(name)) return 0;
        if (priorityCategories.synthFirst.includes(name)) return 1; // Remove synth first among instruments
        if (priorityCategories.instruments.includes(name)) return 2;
        if (priorityCategories.vocalsLast.includes(name)) return 3; // Remove vocals last among instruments
        if (priorityCategories.temporal.includes(name)) return 4;
        if (priorityCategories.descriptive.includes(name)) return 5;
        if (priorityCategories.contentType.includes(name)) return 6;
        return 2.5; // Unknown tags in the middle
      };
      
      return getPriority(a) - getPriority(b);
    });

    return sorted;
  };

  /**
   * Gets the count of tracks that would be returned with given filters
   * @param {Object} filterState - The filter state to test
   * @returns {Promise<number>} - Number of tracks matching the filters
   */
  getFilteredTrackCount = async (filterState) => {
    // Apply the same filtering logic as loadData but just return the count
    let filteredTracks = [...this.state.allTracks];
    
    // Convert filterState to the format needed for filtering
    const convertedFilterState = this.convertFilterStateForFiltering(filterState);
    
    // Apply all filters
    filteredTracks = this.applyAllFilters(filteredTracks, convertedFilterState, this.state.showMnemonicsOverride);
    
    return filteredTracks.length;
  };

  /**
   * Converts the UI filter state to the format needed for filtering
   * @param {Object} uiFilterState - The filter state from the UI
   * @returns {Object} - Converted filter state for filtering
   */
  convertFilterStateForFiltering = (uiFilterState) => {
    const filterState = {
      multi: { active: false, data: [], searchOperator: 'OR', value: 'tags' },
      tags2: { active: false, data: [], searchOperator: 'AND', value: 'tags' },
      genres: { active: false, data: [], searchOperator: 'OR' },
      clients: { active: false, data: [], searchOperator: 'OR' },
      agencies: { active: false, data: [], searchOperator: 'OR' },
      studios: { active: false, data: [], searchOperator: 'OR' },
      producers: { active: false, data: [], searchOperator: 'OR' },
      composers: { active: false, data: [], searchOperator: 'OR' },
      creativeDirectors: { active: false, data: [], searchOperator: 'OR' },
      sessionArtists: { active: false, data: [], searchOperator: 'OR' },
      keys: { active: false, data: [], searchOperator: 'OR' },
      text: { active: false, value: '', field: 'trackName' },
      bpm: { active: false, exact: null, range: { min: '', max: '' }, filterType: 'exact' },
      date: { active: false, range: { from: null, to: null }, filterType: 'completed' },
      license: { active: false, value: false },
    };

    // Helper function to convert a filter section
    const convertSection = (sectionName, fieldName = null) => {
      const section = uiFilterState[sectionName];
      
      // Check if section is active and has data
      if (section?.active && section?.data?.length > 0) {
        filterState[sectionName] = {
          active: true,
          data: section.data.map(item => ({
            label: item.label || item.name || item.value,
            value: item.value || item.id || item.name
          })),
          searchOperator: (section.searchOperator || section.logic || 'OR').toUpperCase()
        };
        
        // Add value field for tag-based filters
        if (fieldName) {
          filterState[sectionName].value = fieldName;
        }
      }
      // Also check for the old selected format for backward compatibility
      else if (section?.selected?.length > 0) {
        filterState[sectionName] = {
          active: true,
          data: section.selected.map(item => ({
            label: item.name || item.id,
            value: item.id
          })),
          searchOperator: (section.logic || 'or').toUpperCase()
        };
        
        // Add value field for tag-based filters
        if (fieldName) {
          filterState[sectionName].value = fieldName;
        }
      }
    };

    // Convert all filter sections
    convertSection('tags', 'tags'); // maps to multi
    convertSection('tags2', 'tags'); // maps to tags2
    convertSection('genres');
    convertSection('clients');
    convertSection('agencies');
    convertSection('studios');
    convertSection('producers');
    convertSection('composers');
    convertSection('creativeDirectors');
    convertSection('sessionArtists');
    convertSection('keys');

    // Special handling for tags -> multi mapping
    const tagsSection = uiFilterState.tags;
    if (tagsSection?.active && tagsSection?.data?.length > 0) {
      filterState.multi = {
        active: true,
        data: tagsSection.data.map(tag => ({
          label: tag.label || tag.name || tag.value,
          value: tag.value || tag.id || tag.name
        })),
        searchOperator: (tagsSection.searchOperator || tagsSection.logic || 'OR').toUpperCase(),
        value: 'tags'
      };
    }
    // Backward compatibility with selected format
    else if (tagsSection?.selected?.length > 0) {
      filterState.multi = {
        active: true,
        data: tagsSection.selected.map(tag => ({
          label: tag.name || tag.id,
          value: tag.id
        })),
        searchOperator: (tagsSection.logic || 'or').toUpperCase(),
        value: 'tags'
      };
    }

    // Handle text search filter
    const textSection = uiFilterState.text;
    if (textSection?.active && textSection?.value?.trim()) {
      filterState.text = {
        active: true,
        value: textSection.value.trim(),
        field: textSection.field || 'trackName'
      };
    }

    // Handle BPM filter
    const bpmSection = uiFilterState.bpm;
    if (bpmSection) {
      // Check if BPM filter is active based on having values
      const hasExactBPM = bpmSection.exact !== null && bpmSection.exact !== undefined;
      const hasRangeBPM = bpmSection.range && (bpmSection.range.min || bpmSection.range.max);
      
      if (hasExactBPM || hasRangeBPM) {
        filterState.bpm = {
          active: true,
          filterType: bpmSection.filterType || 'exact',
          exact: bpmSection.exact,
          range: bpmSection.range || { min: '', max: '' }
        };
      }
    }

    // Handle date filter
    const dateSection = uiFilterState.date;
    if (dateSection?.active) {
      filterState.date = {
        active: true,
        filterType: dateSection.type || dateSection.filterType || 'completed',
        range: dateSection.range || { from: dateSection.from, to: dateSection.to }
      };
    }

    // Handle license filter
    const licenseSection = uiFilterState.license;
    if (licenseSection?.active) {
      filterState.license = {
        active: true,
        value: licenseSection.value
      };
    }

    return filterState;
  };

  /**
   * Applies all filters to a track list (extracted from loadData method)
   * @param {Array} tracks - The tracks to filter
   * @param {Object} filterState - The filter state
   * @param {boolean} showMnemonicsOverride - Whether to bypass mnemonic filtering
   * @returns {Array} - Filtered tracks
   */
  applyAllFilters = (tracks, filterState, showMnemonicsOverride = false) => {
    let filteredTracks = [...tracks];

    // === MNEMONIC EXCLUSION SYSTEM (for filter relaxation testing) ===
    const { excludeMnemonics: excludeMnemonicsInRelax } = getDisplayPreferences();
    if (excludeMnemonicsInRelax) {
      // Check if 'Mnemonic' is explicitly selected in tags (multi) or tags2
      const mnemonicExplicitlySelected = 
        // Check multi filter (tags/tag1)
        (filterState.multi?.active && filterState.multi.data?.some(item => 
          item.label?.toLowerCase() === 'mnemonic'
        )) ||
        // Check tags2 filter
        (filterState.tags2?.active && filterState.tags2.data?.some(item => 
          item.label?.toLowerCase() === 'mnemonic'
        ));

      // If 'Mnemonic' is NOT explicitly selected AND not overridden, filter out all Mnemonic tracks
      if (!mnemonicExplicitlySelected && !showMnemonicsOverride) {
        filteredTracks = filteredTracks.filter(track => {
          if (!track.tags) return true; // Keep tracks with no tags
          
          const trackLabels = Array.isArray(track.tags)
            ? track.tags.map(d => d?.name || d?.label || d).filter(Boolean)
            : [(track.tags?.name || track.tags?.label || track.tags)].filter(Boolean);
          
          // Exclude tracks that have 'Mnemonic' as a tag
          return !trackLabels.some(tagLabel => 
            tagLabel?.toLowerCase() === 'mnemonic'
          );
        });
      }
      // If 'Mnemonic' IS explicitly selected, allow all Mnemonic tracks through
    }

    // Apply multi filter (tags) if active
    if (filterState.multi?.active) {
      const labels = filterState.multi.data?.map((d) => d?.label).filter(Boolean);
      const searchKey = filterState.multi.value;
      
      if (filterState.multi.searchOperator === 'AND') {
        if (filterState.multi.data?.length > 0) {
          filteredTracks = filteredTracks?.filter((track) => {
            if (!track || !track[searchKey]) return false;
            
            const trackLabels = Array.isArray(track[searchKey])
              ? track[searchKey]?.filter(d => d != null).map((d) => d?.name || d?.label || d).filter(Boolean)
              : [track[searchKey]?.name || track[searchKey]?.label || track[searchKey]].filter(Boolean);
            
            return labels?.every((label) => 
              trackLabels?.some(trackLabel => 
                trackLabel?.toLowerCase() === label?.toLowerCase()
              )
            );
          });
        }
      } else if (filterState.multi.searchOperator === 'OR') {
        if (filterState.multi.data?.length > 0) {
          filteredTracks = filteredTracks?.filter((track) => {
            if (!track || !track[searchKey]) return false;
            
            const trackLabels = Array.isArray(track[searchKey])
              ? track[searchKey]?.filter(d => d != null).map((d) => d?.name || d?.label || d).filter(Boolean)
              : [track[searchKey]?.name || track[searchKey]?.label || track[searchKey]].filter(Boolean);
            
            return labels?.some((label) => 
              trackLabels?.some(trackLabel => 
                trackLabel?.toLowerCase() === label?.toLowerCase()
              )
            );
          });
        }
      }
    }

    // Handle tags2 filter (Required Elements - configurable AND/OR logic)
    if (filterState.tags2?.active) {
      const labels = filterState.tags2.data?.map((d) => d.label);
      const searchKey = 'tags'; // tags2 uses the same 'tags' field in tracks data
      
      if (filterState.tags2.searchOperator === 'AND') {
        if (filterState.tags2.data?.length > 0) {
          filteredTracks = filteredTracks?.filter((track) => {
            if (!track[searchKey]) return false;
            
            const trackLabels = Array.isArray(track[searchKey])
              ? track[searchKey]?.map((d) => d?.name || d?.label || d)
              : [track[searchKey]?.name || track[searchKey]?.label || track[searchKey]];
            
            // AND logic: track must have ALL tags from tags2
            return labels?.every((label) => 
              trackLabels?.some(trackLabel => 
                trackLabel?.toLowerCase() === label?.toLowerCase()
              )
            );
          });
        }
      } else if (filterState.tags2.searchOperator === 'OR') {
        if (filterState.tags2.data?.length > 0) {
          filteredTracks = filteredTracks?.filter((track) => {
            if (!track[searchKey]) return false;
            
            const trackLabels = Array.isArray(track[searchKey])
              ? track[searchKey]?.map((d) => d?.name || d?.label || d)
              : [track[searchKey]?.name || track[searchKey]?.label || track[searchKey]];
            
            // OR logic: track must have ANY tag from tags2
            return labels?.some((label) => 
              trackLabels?.some(trackLabel => 
                trackLabel?.toLowerCase() === label?.toLowerCase()
              )
            );
          });
        }
      }
    }

    // Handle genres filter
    if (filterState.genres?.active) {
      const labels = filterState.genres.data?.map((d) => d.label);
      const searchKey = 'genre';
      
      if (filterState.genres.searchOperator === 'AND') {
        if (filterState.genres.data?.length > 0) {
          filteredTracks = filteredTracks?.filter((track) => {
            if (!track[searchKey]) return false;
            
            const trackLabels = Array.isArray(track[searchKey])
              ? track[searchKey]?.map((d) => d?.label || '')
              : [track[searchKey]?.label || ''];
            
            return labels?.every((label) => trackLabels?.includes(label));
          });
        }
      } else if (filterState.genres.searchOperator === 'OR') {
        if (filterState.genres.data?.length > 0) {
          filteredTracks = filteredTracks?.filter((track) => {
            if (!track[searchKey]) return false;
            
            const trackLabels = Array.isArray(track[searchKey])
              ? track[searchKey]?.map((d) => d?.label || '')
              : [track[searchKey]?.label || ''];
            
            return labels?.some((label) => trackLabels?.includes(label));
          });
        }
      }
    }

    // Apply text search filter if active
    if (filterState.text?.active && filterState.text?.value) {
      const searchValue = filterState.text.value.toLowerCase();
      filteredTracks = filteredTracks.filter(track => {
        const trackName = track.trackName?.toLowerCase() || '';
        return trackName.includes(searchValue);
      });
    }

    // Apply tags2 filter if active
    if (filterState.tags2?.active && filterState.tags2.data?.length > 0) {
      const labels = filterState.tags2.data.map(d => d.label).filter(Boolean);
      const searchKey = filterState.tags2.value || 'tags';
      
      if (filterState.tags2.searchOperator === 'AND') {
        filteredTracks = filteredTracks.filter(track => {
          if (!track[searchKey]) return false;
          
          const trackLabels = Array.isArray(track[searchKey])
            ? track[searchKey].map(d => d?.name || d?.label || d).filter(Boolean)
            : [(track[searchKey]?.name || track[searchKey]?.label || track[searchKey])].filter(Boolean);
          
          return labels.every(label => 
            trackLabels.some(trackLabel => trackLabel === label)
          );
        });
      } else { // OR logic
        filteredTracks = filteredTracks.filter(track => {
          if (!track[searchKey]) return false;
          
          const trackLabels = Array.isArray(track[searchKey])
            ? track[searchKey].map(d => d?.name || d?.label || d).filter(Boolean)
            : [(track[searchKey]?.name || track[searchKey]?.label || track[searchKey])].filter(Boolean);
          
          return labels.some(label => 
            trackLabels.some(trackLabel => trackLabel === label)
          );
        });
      }
    }

    // Apply genres filter if active
    if (filterState.genres?.active && filterState.genres.data?.length > 0) {
      const genreLabels = filterState.genres.data.map(d => d.label);
      filteredTracks = filteredTracks.filter(track => {
        if (!track.genre) return false;
        
        const trackGenre = typeof track.genre === 'object' ? 
          (track.genre.name || track.genre.label || track.genre) : 
          track.genre;
        
        return genreLabels.some(genreLabel => 
          trackGenre === genreLabel || trackGenre?.toLowerCase() === genreLabel?.toLowerCase()
        );
      });
    }

    // Apply keys filter if active
    if (filterState.keys?.active && filterState.keys.data?.length > 0) {
      const keyLabels = filterState.keys.data.map(d => d.label);
      filteredTracks = filteredTracks.filter(track => {
        if (!track.key) return false;
        
        const trackKey = typeof track.key === 'object' ? 
          (track.key.name || track.key.label || track.key) : 
          track.key;
        
        return keyLabels.some(keyLabel => 
          trackKey === keyLabel || trackKey?.toLowerCase() === keyLabel?.toLowerCase()
        );
      });
    }

    // Apply clients filter if active
    if (filterState.clients?.active && filterState.clients.data?.length > 0) {
      const clientLabels = filterState.clients.data.map(d => d.label);
      filteredTracks = filteredTracks.filter(track => {
        if (!track.client) return false;
        
        const trackClient = typeof track.client === 'object' ? 
          (track.client.name || track.client.label || track.client) : 
          track.client;
        
        return clientLabels.some(clientLabel => 
          trackClient === clientLabel || trackClient?.toLowerCase() === clientLabel?.toLowerCase()
        );
      });
    }

    // Apply agencies filter if active
    if (filterState.agencies?.active && filterState.agencies.data?.length > 0) {
      const agencyLabels = filterState.agencies.data.map(d => d.label);
      filteredTracks = filteredTracks.filter(track => {
        if (!track.agency) return false;
        
        const trackAgency = typeof track.agency === 'object' ? 
          (track.agency.name || track.agency.label || track.agency) : 
          track.agency;
        
        return agencyLabels.some(agencyLabel => 
          trackAgency === agencyLabel || trackAgency?.toLowerCase() === agencyLabel?.toLowerCase()
        );
      });
    }

    // Apply studios filter if active
    if (filterState.studios?.active && filterState.studios.data?.length > 0) {
      const studioLabels = filterState.studios.data.map(d => d.label);
      filteredTracks = filteredTracks.filter(track => {
        if (!track.studio) return false;
        
        const trackStudio = typeof track.studio === 'object' ? 
          (track.studio.name || track.studio.label || track.studio) : 
          track.studio;
        
        return studioLabels.some(studioLabel => 
          trackStudio === studioLabel || trackStudio?.toLowerCase() === studioLabel?.toLowerCase()
        );
      });
    }

    // Apply producers filter if active
    if (filterState.producers?.active && filterState.producers.data?.length > 0) {
      const producerLabels = filterState.producers.data.map(d => d.label);
      filteredTracks = filteredTracks.filter(track => {
        if (!track.producer) return false;
        
        const trackProducer = typeof track.producer === 'object' ? 
          (track.producer.name || track.producer.label || track.producer) : 
          track.producer;
        
        return producerLabels.some(producerLabel => 
          trackProducer === producerLabel || trackProducer?.toLowerCase() === producerLabel?.toLowerCase()
        );
      });
    }

    // Apply composers filter if active
    if (filterState.composers?.active && filterState.composers.data?.length > 0) {
      const composerLabels = filterState.composers.data.map(d => d.label);
      filteredTracks = filteredTracks.filter(track => {
        if (!track.composers) return false;
        
        // Composers can be an array
        const trackComposers = Array.isArray(track.composers) 
          ? track.composers.map(c => typeof c === 'object' ? (c.name || c.label || c) : c)
          : [typeof track.composers === 'object' ? (track.composers.name || track.composers.label || track.composers) : track.composers];
        
        return composerLabels.some(composerLabel => 
          trackComposers.some(trackComposer => 
            trackComposer === composerLabel || trackComposer?.toLowerCase() === composerLabel?.toLowerCase()
          )
        );
      });
    }

    // Apply creativeDirectors filter if active
    if (filterState.creativeDirectors?.active && filterState.creativeDirectors.data?.length > 0) {
      const cdLabels = filterState.creativeDirectors.data.map(d => d.label);
      filteredTracks = filteredTracks.filter(track => {
        if (!track.creativedirector) return false;
        
        // Creative directors can be an array
        const trackCDs = Array.isArray(track.creativedirector) 
          ? track.creativedirector.map(cd => typeof cd === 'object' ? (cd.name || cd.label || cd) : cd)
          : [typeof track.creativedirector === 'object' ? (track.creativedirector.name || track.creativedirector.label || track.creativedirector) : track.creativedirector];
        
        return cdLabels.some(cdLabel => 
          trackCDs.some(trackCD => 
            trackCD === cdLabel || trackCD?.toLowerCase() === cdLabel?.toLowerCase()
          )
        );
      });
    }

    // Apply sessionArtists filter if active
    if (filterState.sessionArtists?.active && filterState.sessionArtists.data?.length > 0) {
      const saLabels = filterState.sessionArtists.data.map(d => d.label);
      filteredTracks = filteredTracks.filter(track => {
        if (!track.sessionartists) return false;
        
        // Session artists can be an array
        const trackSAs = Array.isArray(track.sessionartists) 
          ? track.sessionartists.map(sa => typeof sa === 'object' ? (sa.name || sa.label || sa) : sa)
          : [typeof track.sessionartists === 'object' ? (track.sessionartists.name || track.sessionartists.label || track.sessionartists) : track.sessionartists];
        
        return saLabels.some(saLabel => 
          trackSAs.some(trackSA => 
            trackSA === saLabel || trackSA?.toLowerCase() === saLabel?.toLowerCase()
          )
        );
      });
    }

    // Apply BPM filter if active
    if (filterState.bpm?.active) {
      console.log('🔍 Debug - BPM filter active:', JSON.stringify(filterState.bpm, null, 2));
      console.log('🔍 Debug - Tracks before BPM filter:', filteredTracks.length);
      
      if (filterState.bpm.exact !== null && filterState.bpm.exact !== undefined) {
        // Exact BPM match - handle both object and string formats
        const filterBPMValue = typeof filterState.bpm.exact === 'object' ? 
          (filterState.bpm.exact.label || filterState.bpm.exact.name || filterState.bpm.exact) : 
          filterState.bpm.exact;
          
        filteredTracks = filteredTracks.filter(track => {
          if (!track || track.bpm == null) return false; // Skip tracks with no BPM
          
          const trackBPM = typeof track.bpm === 'object' && track.bpm ? 
            (track.bpm.name || track.bpm.label || track.bpm) : 
            track.bpm;
          const match = parseInt(trackBPM) === parseInt(filterBPMValue);
          if (!match && track.trackName?.toLowerCase().includes('mnemonic')) {
            console.log('🔍 Debug - Mnemonic track filtered out by BPM:', {
              trackName: track.trackName,
              trackBPM: trackBPM,
              filterBPM: filterBPMValue
            });
          }
          return match;
        });
      } else if (filterState.bpm.range?.min || filterState.bpm.range?.max) {
        // BPM range
        const minBPM = filterState.bpm.range.min ? parseInt(filterState.bpm.range.min) : 0;
        const maxBPM = filterState.bpm.range.max ? parseInt(filterState.bpm.range.max) : 999;
        
        filteredTracks = filteredTracks.filter(track => {
          if (!track || track.bpm == null) return false; // Skip tracks with no BPM
          
          const trackBPM = typeof track.bpm === 'object' && track.bpm ? 
            (track.bpm.name || track.bpm.label || track.bpm) : 
            track.bpm;
          const bpmValue = parseInt(trackBPM);
          const match = bpmValue >= minBPM && bpmValue <= maxBPM;
          if (!match && track.trackName?.toLowerCase().includes('mnemonic')) {
            console.log('🔍 Debug - Mnemonic track filtered out by BPM range:', {
              trackName: track.trackName,
              trackBPM: trackBPM,
              minBPM: minBPM,
              maxBPM: maxBPM
            });
          }
          return match;
        });
      }
      
      console.log('🔍 Debug - Tracks after BPM filter:', filteredTracks.length);
    }

    // Apply date filter if active
    if (filterState.date?.active && (filterState.date.range?.from || filterState.date.range?.to)) {
      filteredTracks = filteredTracks.filter(track => {
        const trackDate = filterState.date.filterType === 'completed' ? track.dateCompleted : track.dateExpires;
        if (!trackDate) return false;
        
        const dateValue = new Date(trackDate).getTime();
        const fromDate = filterState.date.range.from;
        const toDate = filterState.date.range.to;
        
        if (fromDate && toDate) {
          return dateValue >= fromDate && dateValue <= toDate;
        } else if (fromDate) {
          return dateValue >= fromDate;
        } else if (toDate) {
          return dateValue <= toDate;
        }
        return true;
      });
    }

    // Apply license filter if active
    if (filterState.license?.active) {
      filteredTracks = filteredTracks.filter(track => {
        return track.isLicensed === filterState.license.value;
      });
    }

    return filteredTracks;
  };

  /**
   * Applies the relaxed filters to the UI
   * @param {Object} relaxedFilterState - The relaxed filter state
   * @param {Array} relaxationSteps - Description of what was relaxed
   */
  applyRelaxedFilters = (relaxedFilterState, relaxationSteps) => {
    console.log('[FilterRelaxation] Applying relaxed filters:', relaxationSteps);
    
    // Show a notification to the user about the relaxation
    const message = `Filters were automatically adjusted for better results:\n${relaxationSteps.join('\n')}`;
    
    // Update the UI with relaxed filters
    this.setState({
      initialFilterState: relaxedFilterState,
      filterRelaxationMessage: message
    }, () => {
      this.handleMultiFilterChange(relaxedFilterState);
      
      // Clear the message after a delay
      setTimeout(() => {
        this.setState({ filterRelaxationMessage: null });
      }, 5000);
    });
  };

  deleteTrack = (id) => {
    const currentOrgId = useOrganizationStore.getState().currentOrgId;

    if (currentOrgId) {
      // Use org-scoped delete
      this.props.firebase
        .deleteOrgAudioMetaData(currentOrgId, id)
        .then((result) => {
          // console.log('Success', result);
        })
        .catch((err) => {
          // console.log('Error', err);
        });
    } else {
      // Fallback to legacy delete
      this.props.firebase
        .deleteAudioMetaData(id)
        .then((result) => {
          // console.log('Success', result);
        })
        .catch((err) => {
          // console.log('Error', err);
        });
    }
  };

//license status update method - improved
//////////////
updateTrackLicense = (trackId, updates) => {
  const { firebase } = this.props;
  const currentOrgId = useOrganizationStore.getState().currentOrgId;

  // Use org-scoped path if org is available, otherwise fall back to legacy path
  const trackPath = currentOrgId
    ? getOrgCollectionPath(currentOrgId, AUDIOMETAPATH)
    : 'audiometa';
  const trackRef = doc(firebase.db, trackPath, trackId);

  updateDoc(trackRef, updates)
    .then(() => {
      // Helper to update track in an array
      const updateTrackInArray = (arr) => arr.map(track => {
        if (track.id === trackId) {
          return { ...track, ...updates };
        }
        return track;
      });

      // Update all track arrays including displayedTracks
      this.setState(prevState => ({
        tracks: updateTrackInArray(prevState.tracks),
        allTracks: updateTrackInArray(prevState.allTracks),
        displayedTracks: updateTrackInArray(prevState.displayedTracks)
      }));
    })
    .catch(err => {
      console.error('Error updating track license:', err);
    });
};
//////////////

// Generic in-place track field update — writes to Firestore and refreshes
// local state (tracks, allTracks, displayedTracks) so the UI reflects
// changes without a page reload. Used by inline editors in the table (e.g.
// the tag adder / remover in the Tags column).
updateTrackFields = (trackId, updates) => {
  const { firebase } = this.props;
  const currentOrgId = useOrganizationStore.getState().currentOrgId;
  if (!currentOrgId) {
    return Promise.reject(new Error('No current org — cannot update track'));
  }

  const trackPath = getOrgCollectionPath(currentOrgId, AUDIOMETAPATH);
  const trackRef = doc(firebase.db, trackPath, trackId);

  return updateDoc(trackRef, updates)
    .then(() => {
      const updateTrackInArray = (arr) => arr.map(track => (
        track.id === trackId ? { ...track, ...updates } : track
      ));
      this.setState(prevState => ({
        tracks: updateTrackInArray(prevState.tracks),
        allTracks: updateTrackInArray(prevState.allTracks),
        displayedTracks: updateTrackInArray(prevState.displayedTracks),
      }));
    })
    .catch(err => {
      console.error('Error updating track fields:', err);
      throw err;
    });
};

  editTrack = (id) => {
    this.props.history.push(`${EDIT_AUDIO}/${id}`);
  };

  pagingChanged = (newPageNumber) => {
    this.setState({ pageNumber: newPageNumber });
  };

  handleMultiFilterChange = (filters) => {
    // console.log('2. Current filter state before update:', JSON.stringify(this.state.filterState, null, 2));
    
    // Create a new filter state based on the selected filters
    const newFilterState = {
      ...this.state.filterState,
      // Reset all filters to their default states
      multi: DEFAULT_MULTI,
      text: DEFAULT_TEXT,
      creativeDirectors: DEFAULT_SINGLE,
      sessionArtists: DEFAULT_SINGLE,
      genres: DEFAULT_SINGLE,
      agencies: DEFAULT_SINGLE,
      clients: DEFAULT_SINGLE,
      keys: DEFAULT_SINGLE,
      studios: DEFAULT_SINGLE,
      producers: DEFAULT_SINGLE,
      composers: DEFAULT_SINGLE,
      bpm: {
        active: false,
        filterType: 'exact',
        exact: null,
        range: { min: '', max: '' }
      },
      date: DEFAULT_DATE
    };

    // Only update filters that are present in the incoming filters object
    if (filters.tags?.selected?.length > 0) {
      // console.log('Setting tags filter with selected items:', JSON.stringify(filters.tags.selected));
      newFilterState.multi = {
        ...DEFAULT_MULTI,
        active: true,
        searchOperator: filters.tags?.logic?.toLowerCase() === 'and' ? 'AND' : 'OR',
        data: filters.tags?.selected?.map(item => ({
          label: item.name || item.label || item,
          value: item.id || item.value || item
        })) || [],
        value: 'tags'
      };
      // console.log('Updated multi filter for tags:', JSON.stringify(newFilterState.multi));
    } else {
      // console.log('No tags selected in filters');
    }

    // Handle excluded tags filter
    if (filters.tags?.excluded?.length > 0) {
      newFilterState.excludedTags = {
        active: true,
        searchOperator: filters.tags?.logic?.toLowerCase() === 'and' ? 'AND' : 'OR',
        data: filters.tags?.excluded?.map(item => ({
          label: item.name || item.label || item,
          value: item.id || item.value || item
        })) || [],
        value: 'tags'
      };
    } else {
      newFilterState.excludedTags = {
        active: false,
        searchOperator: 'OR',
        data: [],
        value: 'tags'
      };
    }

    // Handle tags2 filter (Required Elements - configurable AND/OR logic)
    if (filters.tags2?.selected?.length > 0) {
      // Handle both lowercase (from chatbot) and uppercase (from UI) logic values
      const tags2Logic = filters.tags2?.logic ? filters.tags2.logic.toLowerCase() : 'or';
      newFilterState.tags2 = {
        active: true,
        searchOperator: tags2Logic === 'or' ? 'OR' : 'AND', // Default to OR for tags2
        data: filters.tags2?.selected?.map(item => ({
          label: item.name || item.label || item,
          value: item.id || item.value || item
        })) || [],
        value: 'tags' // tags2 uses the same 'tags' field in tracks data
      };
    } else {
      // Reset tags2 filter when no items selected
      newFilterState.tags2 = {
        active: false,
        searchOperator: 'AND',
        data: [],
        value: 'tags'
      };
    }

    // Handle excluded tags2 filter
    if (filters.tags2?.excluded?.length > 0) {
      const tags2Logic = filters.tags2?.logic ? filters.tags2.logic.toLowerCase() : 'or';
      newFilterState.excludedTags2 = {
        active: true,
        searchOperator: tags2Logic === 'or' ? 'OR' : 'AND',
        data: filters.tags2?.excluded?.map(item => ({
          label: item.name || item.label || item,
          value: item.id || item.value || item
        })) || [],
        value: 'tags'
      };
    } else {
      newFilterState.excludedTags2 = {
        active: false,
        searchOperator: 'OR',
        data: [],
        value: 'tags'
      };
    }

    // Only set license filter if it's explicitly provided with an active property
    if (filters.license?.value !== undefined) {
      newFilterState.license = {
        value: filters.license.value,
        active: true
      };
    } else if (filters.license === null) {
      // Reset license filter when explicitly set to null
      newFilterState.license = {
        value: false,
        active: false
      };
    } else {
      // Keep the existing license filter state if not explicitly provided
      newFilterState.license = this.state.filterState.license;
    }

    if (filters.text?.value) {
      newFilterState.text = {
        active: true,
        value: filters.text.value,
        field: filters.text.field || 'trackName'
      };
    }

    // Handle date filter - only update if explicitly provided
    if (filters.date?.range) {
      const hasDateRange = filters.date.range.from || filters.date.range.to;
      newFilterState.date = {
        active: hasDateRange,
        type: filters.date.filterType || 'completed',
        from: filters.date.range.from || DEFAULT_DATE.from,
        to: filters.date.range.to || DEFAULT_DATE.to
      };
    } else if (filters.date === null) {
      // Reset date filter when explicitly set to null
      newFilterState.date = DEFAULT_DATE;
    } else if (filters.date) {
      // Handle date filter from chatbot format
      newFilterState.date = {
        active: true,
        type: filters.date.filterType || 'completed',
        from: filters.date.range?.from || DEFAULT_DATE.from,
        to: filters.date.range?.to || DEFAULT_DATE.to
      };
    } else {
      // Preserve existing date state if not explicitly provided
      newFilterState.date = { ...this.state.filterState.date };
    }

    // Handle BPM filter - only update if explicitly provided
    if (filters.bpm) {
      newFilterState.bpm = {
        active: true,
        filterType: filters.bpm.filterType || 'exact',
        exact: filters.bpm.exact,
        range: {
          min: filters.bpm.range?.min || '',
          max: filters.bpm.range?.max || ''
        }
      };
    } else {
      // Preserve existing BPM state if not explicitly provided
      newFilterState.bpm = { ...this.state.filterState.bpm };
    }

    // Handle genres filter
    if (filters.genres?.selected?.length > 0) {
      // console.log('9a. Applying genres filter:', JSON.stringify(filters.genres, null, 2));
      newFilterState.genres = {
        active: true,
        searchOperator: filters.genres.logic.toUpperCase(),
        data: filters.genres.selected.map(genre => ({
          label: genre.name,
          value: genre.name
        })),
        value: 'genre'
      };
      // console.log('9b. Updated genres filter:', JSON.stringify(newFilterState.genres, null, 2));
    } else {
      newFilterState.genres = {
        active: false,
        searchOperator: 'OR',
        data: [],
        value: 'genre'
      };
    }

    // Handle excluded genres filter
    if (filters.genres?.excluded?.length > 0) {
      newFilterState.excludedGenres = {
        active: true,
        searchOperator: filters.genres.logic.toUpperCase(),
        data: filters.genres.excluded.map(genre => ({
          label: genre.name,
          value: genre.name
        })),
        value: 'genre'
      };
    } else {
      newFilterState.excludedGenres = {
        active: false,
        searchOperator: 'OR',
        data: [],
        value: 'genre'
      };
    }

    // Handle creative directors filter
    if (filters.creativeDirectors?.selected?.length > 0) {
      // console.log('   - Processing Creative Directors filter:', JSON.stringify(filters.creativeDirectors, null, 2));
      newFilterState.creativeDirectors = {
        active: true,
        searchOperator: filters.creativeDirectors.logic === 'and' ? 'AND' : 'OR',
        data: filters.creativeDirectors.selected.map(item => ({
          label: item.name || item.label || item,
          value: item.id || item.value || item
        })),
        value: 'creativedirector'
      };
      // console.log('   - Updated Creative Directors filter:', JSON.stringify(newFilterState.creativeDirectors, null, 2));
    }

    // Handle session artists filter
    if (filters.sessionArtists?.selected?.length > 0) {
      // console.log('   - Processing Session Artists filter:', JSON.stringify(filters.sessionArtists, null, 2));
      // console.log('Setting session artists filter with selected items:', JSON.stringify(filters.sessionArtists.selected));
      newFilterState.sessionArtists = {
        active: true,
        searchOperator: filters.sessionArtists.logic === 'and' ? 'AND' : 'OR',
        data: filters.sessionArtists.selected.map(item => ({
          label: item.name || item.label || item,
          value: item.id || item.value || item
        })),
        value: 'sessionartists'  // This matches the collection name
      };
      // console.log('   - Updated Session Artists filter:', JSON.stringify(newFilterState.sessionArtists, null, 2));
      // console.log('Updated session artists filter:', JSON.stringify(newFilterState.sessionArtists, null, 2));
    } else {
      // console.log('No session artists selected in filters');
    }

    // Handle agencies filter
    if (filters.agencies?.selected?.length > 0) {
      // console.log('10c. Applying agencies filter:', JSON.stringify(filters.agencies, null, 2));
      newFilterState.agencies = {
        active: true,
        searchOperator: filters.agencies.logic.toUpperCase(),
        data: filters.agencies.selected.map(agency => ({
          label: agency.name,
          value: agency.name
        })),
        value: 'agency'
      };
      //console.log('10d. Updated agencies filter:', JSON.stringify(newFilterState.agencies, null, 2));
    } else {
      newFilterState.agencies = {
        active: false,
        searchOperator: 'OR',
        data: [],
        value: 'agency'
      };
    }

    // Handle clients filter
    if (filters.clients?.selected?.length > 0) {
      // console.log('10e. Applying clients filter:', JSON.stringify(filters.clients, null, 2));
      newFilterState.clients = {
        active: true,
        searchOperator: filters.clients.logic.toUpperCase(),
        data: filters.clients.selected.map(client => ({
          label: client.name,
          value: client.name
        })),
        value: 'client'
      };
      //console.log('10f. Updated clients filter:', JSON.stringify(newFilterState.clients, null, 2));
    } else {
      newFilterState.clients = {
        active: false,
        searchOperator: 'OR',
        data: [],
        value: 'client'
      };
    }

    // Handle keys filter
    if (filters.keys?.selected?.length > 0) {
      // console.log('10g. Applying keys filter:', JSON.stringify(filters.keys, null, 2));
      newFilterState.keys = {
        active: true,
        searchOperator: filters.keys.logic.toUpperCase(),
        data: filters.keys.selected.map(key => ({
          label: key.name,
          value: key.name
        })),
        value: 'key'
      };
      // console.log('10h. Updated keys filter:', JSON.stringify(newFilterState.keys, null, 2));
    } else {
      newFilterState.keys = {
        active: false,
        searchOperator: 'OR',
        data: [],
        value: 'key'
      };
    }

    // Handle studios filter
    if (filters.studios?.selected?.length > 0) {
      // console.log('13a. Applying studios filter:', JSON.stringify(filters.studios, null, 2));
      newFilterState.studios = {
        active: true,
        searchOperator: filters.studios.logic.toUpperCase(),
        data: filters.studios.selected.map(studio => ({
          label: studio.name,
          value: studio.name
        })),
        value: 'studio'
      };
      //console.log('13b. Updated studios filter:', JSON.stringify(newFilterState.studios, null, 2));
    } else {
      newFilterState.studios = {
        active: false,
        searchOperator: 'OR',
        data: [],
        value: 'studio'
      };
    }

    // Handle producers filter
    if (filters.producers?.selected?.length > 0) {
      // console.log('14a. Applying producers filter:', JSON.stringify(filters.producers, null, 2));
      newFilterState.producers = {
        active: true,
        searchOperator: filters.producers.logic.toUpperCase(),
        data: filters.producers.selected.map(producer => ({
          label: producer.name,
          value: producer.name
        })),
        value: 'producer'
      };
      //console.log('14b. Updated producers filter:', JSON.stringify(newFilterState.producers, null, 2));
    } else {
      newFilterState.producers = {
        active: false,
        searchOperator: 'OR',
        data: [],
        value: 'producer'
      };
    }

    // Handle composers filter
    if (filters.composers?.selected?.length > 0) {
      // console.log('15a. Applying composers filter:', JSON.stringify(filters.composers, null, 2));
      newFilterState.composers = {
        active: true,
        searchOperator: filters.composers.logic.toUpperCase(),
        data: filters.composers.selected.map(composer => ({
          label: composer.name,
          value: composer.name
        })),
        value: 'composer'
      };
      //console.log('15b. Updated composers filter:', JSON.stringify(newFilterState.composers, null, 2));
    } else {
      newFilterState.composers = {
        active: false,
        searchOperator: 'OR',
        data: [],
        value: 'composer'
      };
    }

    // Handle all remaining exclusion filters
    
    // Handle excluded creative directors filter
    if (filters.creativeDirectors?.excluded?.length > 0) {
      newFilterState.excludedCreativeDirectors = {
        active: true,
        searchOperator: filters.creativeDirectors.logic === 'and' ? 'AND' : 'OR',
        data: filters.creativeDirectors.excluded.map(item => ({
          label: item.name || item.label || item,
          value: item.id || item.value || item
        })),
        value: 'creativedirector'
      };
    } else {
      newFilterState.excludedCreativeDirectors = { active: false, searchOperator: 'OR', data: [], value: 'creativedirector' };
    }

    // Handle excluded session artists filter
    if (filters.sessionArtists?.excluded?.length > 0) {
      newFilterState.excludedSessionArtists = {
        active: true,
        searchOperator: filters.sessionArtists.logic === 'and' ? 'AND' : 'OR',
        data: filters.sessionArtists.excluded.map(item => ({
          label: item.name || item.label || item,
          value: item.id || item.value || item
        })),
        value: 'sessionartists'
      };
    } else {
      newFilterState.excludedSessionArtists = { active: false, searchOperator: 'OR', data: [], value: 'sessionartists' };
    }

    // Handle excluded agencies filter
    if (filters.agencies?.excluded?.length > 0) {
      newFilterState.excludedAgencies = {
        active: true,
        searchOperator: filters.agencies.logic.toUpperCase(),
        data: filters.agencies.excluded.map(agency => ({
          label: agency.name,
          value: agency.name
        })),
        value: 'agency'
      };
    } else {
      newFilterState.excludedAgencies = { active: false, searchOperator: 'OR', data: [], value: 'agency' };
    }

    // Handle excluded clients filter
    if (filters.clients?.excluded?.length > 0) {
      newFilterState.excludedClients = {
        active: true,
        searchOperator: filters.clients.logic.toUpperCase(),
        data: filters.clients.excluded.map(client => ({
          label: client.name,
          value: client.name
        })),
        value: 'client'
      };
    } else {
      newFilterState.excludedClients = { active: false, searchOperator: 'OR', data: [], value: 'client' };
    }

    // Handle excluded keys filter
    if (filters.keys?.excluded?.length > 0) {
      newFilterState.excludedKeys = {
        active: true,
        searchOperator: filters.keys.logic.toUpperCase(),
        data: filters.keys.excluded.map(key => ({
          label: key.name,
          value: key.name
        })),
        value: 'key'
      };
    } else {
      newFilterState.excludedKeys = { active: false, searchOperator: 'OR', data: [], value: 'key' };
    }

    // Handle excluded studios filter
    if (filters.studios?.excluded?.length > 0) {
      newFilterState.excludedStudios = {
        active: true,
        searchOperator: filters.studios.logic.toUpperCase(),
        data: filters.studios.excluded.map(studio => ({
          label: studio.name,
          value: studio.name
        })),
        value: 'studio'
      };
    } else {
      newFilterState.excludedStudios = { active: false, searchOperator: 'OR', data: [], value: 'studio' };
    }

    // Handle excluded producers filter
    if (filters.producers?.excluded?.length > 0) {
      newFilterState.excludedProducers = {
        active: true,
        searchOperator: filters.producers.logic.toUpperCase(),
        data: filters.producers.excluded.map(producer => ({
          label: producer.name,
          value: producer.name
        })),
        value: 'producer'
      };
    } else {
      newFilterState.excludedProducers = { active: false, searchOperator: 'OR', data: [], value: 'producer' };
    }

    // Handle excluded composers filter
    if (filters.composers?.excluded?.length > 0) {
      newFilterState.excludedComposers = {
        active: true,
        searchOperator: filters.composers.logic.toUpperCase(),
        data: filters.composers.excluded.map(composer => ({
          label: composer.name,
          value: composer.name
        })),
        value: 'composer'
      };
    } else {
      newFilterState.excludedComposers = { active: false, searchOperator: 'OR', data: [], value: 'composer' };
    }

    // FINAL DEBUG: Show what we're about to set as the state
    
    // Check if filters have actually changed
    const hasFilterChanged = JSON.stringify(this.state.filterState) !== JSON.stringify(newFilterState);
    
    // Update the state and searchFilter
    this.setState({ 
      filterState: newFilterState,
      initialFilterState: {
        ...filters,
        expandedSections: {
          trackName: filters.text?.value ? true : false,
          license: filters.license?.value !== undefined,
          tags: filters.tags?.selected?.length > 0,
          tags2: filters.tags2?.selected?.length > 0,
          composers: filters.composers?.selected?.length > 0,
          creativeDirectors: filters.creativeDirectors?.selected?.length > 0,
          sessionArtists: filters.sessionArtists?.selected?.length > 0,
          genres: filters.genres?.selected?.length > 0,
          agencies: filters.agencies?.selected?.length > 0,
          clients: filters.clients?.selected?.length > 0,
          keys: filters.keys?.selected?.length > 0,
          studios: filters.studios?.selected?.length > 0,
          producers: filters.producers?.selected?.length > 0,
          bpm: filters.bpm?.filterType === 'exact' && filters.bpm.exact || // eslint-disable-line no-mixed-operators
               (filters.bpm?.filterType === 'range' && (filters.bpm.range.min || filters.bpm.range.max)), // eslint-disable-line no-mixed-operators
          date: filters.date?.range?.from || filters.date?.range?.to
        }
      }
    }, () => {
      // Update the searchFilter with the new filter state
      this.searchFilter = { ...newFilterState };
      
      // Only reload data if filters have actually changed
      if (hasFilterChanged) {
        // console.log('16. Calling loadData to apply filters');
        this.loadData();
      }
    });
  };

  // Add method to filter by selected playlist
  filterByPlaylist = (playlistId) => {
    if (!playlistId) {
      this.setState({ playlistFilterTrackIds: null, playlistFilterName: '' }, this.loadData);
      return;
    }
    // Use playlistTrackIds from props
    const trackIds = this.props.playlistTrackIds && this.props.playlistTrackIds[playlistId] ? this.props.playlistTrackIds[playlistId] : [];
    // Get playlist name from props.playlists
    const playlist = this.props.playlists ? this.props.playlists.find(p => p.id === playlistId) : null;
    const playlistName = playlist ? playlist.name : '';
    this.setState({ playlistFilterTrackIds: trackIds, playlistFilterName: playlistName }, this.loadData);
  };

  componentDidUpdate(prevProps) {
    if (this.props.selectedPlaylist !== prevProps.selectedPlaylist || this.props.playlistTrackIds !== prevProps.playlistTrackIds) {
      this.filterByPlaylist(this.props.selectedPlaylist);
    }
  }

  render() {
    const { 
      loading, 
      loadingMore,
      columnsVisible, 
      isSidebarOpen, 
      filterState, 
      initialFilterState,
      displayedTracks,
      orderBy: currentOrderBy 
    } = this.state;

    // Handler to remove a filter chip
    const handleRemoveFilter = (chip) => {
      // Helper to remove a filter from a given state object
      const removeFromState = (state) => {
        const newState = JSON.parse(JSON.stringify(state));
        const ensureFilter = (key) => {
          const defaultLogic = key === 'tags2' ? 'or' : 'or';
          if (!newState[key]) newState[key] = { selected: [], excluded: [], logic: defaultLogic };
          if (!Array.isArray(newState[key].selected)) newState[key].selected = [];
          if (!Array.isArray(newState[key].excluded)) newState[key].excluded = [];
          if (!Array.isArray(newState[key].data)) newState[key].data = [];
        };
        const matchChip = (item) => (
          item.id === chip.value || item.name === chip.value || item.value === chip.value
        );
        switch (chip.type) {
          case 'tags':
            ensureFilter('tags');
            if (newState.multi?.data) {
              newState.multi.data = newState.multi.data.filter(t => t.value !== chip.value);
              if (newState.multi.data.length === 0) newState.multi.active = false;
            }
            newState.tags.selected = (newState.tags.selected || []).filter(t => !matchChip(t));
            newState.tags.data = (newState.tags.data || []).filter(t => t.value !== chip.value);
            if (!newState.tags.selected.length) newState.tags.selected = [];
            if (!newState.tags.data.length) newState.tags.data = [];
            break;
          case 'tags2':
            ensureFilter('tags2');
            if (newState.tags2?.data) {
              newState.tags2.data = newState.tags2.data.filter(t => t.value !== chip.value);
              if (newState.tags2.data.length === 0) newState.tags2.active = false;
            }
            newState.tags2.selected = (newState.tags2.selected || []).filter(t => !matchChip(t));
            newState.tags2.data = (newState.tags2.data || []).filter(t => t.value !== chip.value);
            if (!newState.tags2.selected.length) newState.tags2.selected = [];
            if (!newState.tags2.data.length) newState.tags2.data = [];
            break;
          case 'genres':
            ensureFilter('genres');
            if (newState.genres?.data) {
              newState.genres.data = newState.genres.data.filter(g => g.value !== chip.value);
              newState.genres.active = newState.genres.data.length > 0;
            }
            newState.genres.selected = (newState.genres.selected || []).filter(g => !matchChip(g));
            newState.genres.data = (newState.genres.data || []).filter(g => g.value !== chip.value);
            if (!newState.genres.selected.length) newState.genres.selected = [];
            if (!newState.genres.data.length) newState.genres.data = [];
            break;
          case 'excludedTags':
            if (newState.excludedTags?.data) {
              newState.excludedTags.data = newState.excludedTags.data.filter(t => t.value !== chip.value);
              if (newState.excludedTags.data.length === 0) newState.excludedTags.active = false;
            }
            ensureFilter('tags');
            newState.tags.excluded = (newState.tags.excluded || []).filter(t => !matchChip(t));
            break;
          case 'excludedTags2':
            if (newState.excludedTags2?.data) {
              newState.excludedTags2.data = newState.excludedTags2.data.filter(t => t.value !== chip.value);
              if (newState.excludedTags2.data.length === 0) newState.excludedTags2.active = false;
            }
            ensureFilter('tags2');
            newState.tags2.excluded = (newState.tags2.excluded || []).filter(t => !matchChip(t));
            break;
          case 'excludedGenres':
            if (newState.excludedGenres?.data) {
              newState.excludedGenres.data = newState.excludedGenres.data.filter(g => g.value !== chip.value);
              if (newState.excludedGenres.data.length === 0) newState.excludedGenres.active = false;
            }
            ensureFilter('genres');
            newState.genres.excluded = (newState.genres.excluded || []).filter(g => !matchChip(g));
            break;
          case 'excludedKeys':
            if (newState.excludedKeys?.data) {
              newState.excludedKeys.data = newState.excludedKeys.data.filter(k => k.value !== chip.value);
              if (newState.excludedKeys.data.length === 0) newState.excludedKeys.active = false;
            }
            ensureFilter('keys');
            newState.keys.excluded = (newState.keys.excluded || []).filter(k => !matchChip(k));
            break;
          case 'excludedStudios':
            if (newState.excludedStudios?.data) {
              newState.excludedStudios.data = newState.excludedStudios.data.filter(s => s.value !== chip.value);
              if (newState.excludedStudios.data.length === 0) newState.excludedStudios.active = false;
            }
            ensureFilter('studios');
            newState.studios.excluded = (newState.studios.excluded || []).filter(s => !matchChip(s));
            break;
          case 'excludedAgencies':
            if (newState.excludedAgencies?.data) {
              newState.excludedAgencies.data = newState.excludedAgencies.data.filter(a => a.value !== chip.value);
              if (newState.excludedAgencies.data.length === 0) newState.excludedAgencies.active = false;
            }
            ensureFilter('agencies');
            newState.agencies.excluded = (newState.agencies.excluded || []).filter(a => !matchChip(a));
            break;
          case 'excludedClients':
            if (newState.excludedClients?.data) {
              newState.excludedClients.data = newState.excludedClients.data.filter(c => c.value !== chip.value);
              if (newState.excludedClients.data.length === 0) newState.excludedClients.active = false;
            }
            ensureFilter('clients');
            newState.clients.excluded = (newState.clients.excluded || []).filter(c => !matchChip(c));
            break;
          case 'excludedProducers':
            if (newState.excludedProducers?.data) {
              newState.excludedProducers.data = newState.excludedProducers.data.filter(p => p.value !== chip.value);
              if (newState.excludedProducers.data.length === 0) newState.excludedProducers.active = false;
            }
            ensureFilter('producers');
            newState.producers.excluded = (newState.producers.excluded || []).filter(p => !matchChip(p));
            break;
          case 'excludedComposers':
            if (newState.excludedComposers?.data) {
              newState.excludedComposers.data = newState.excludedComposers.data.filter(c => c.value !== chip.value);
              if (newState.excludedComposers.data.length === 0) newState.excludedComposers.active = false;
            }
            ensureFilter('composers');
            newState.composers.excluded = (newState.composers.excluded || []).filter(c => !matchChip(c));
            break;
          case 'excludedCreativeDirectors':
            if (newState.excludedCreativeDirectors?.data) {
              newState.excludedCreativeDirectors.data = newState.excludedCreativeDirectors.data.filter(cd => cd.value !== chip.value);
              if (newState.excludedCreativeDirectors.data.length === 0) newState.excludedCreativeDirectors.active = false;
            }
            ensureFilter('creativeDirectors');
            newState.creativeDirectors.excluded = (newState.creativeDirectors.excluded || []).filter(cd => !matchChip(cd));
            break;
          case 'excludedSessionArtists':
            if (newState.excludedSessionArtists?.data) {
              newState.excludedSessionArtists.data = newState.excludedSessionArtists.data.filter(sa => sa.value !== chip.value);
              if (newState.excludedSessionArtists.data.length === 0) newState.excludedSessionArtists.active = false;
            }
            ensureFilter('sessionArtists');
            newState.sessionArtists.excluded = (newState.sessionArtists.excluded || []).filter(sa => !matchChip(sa));
            break;
          case 'keys':
            ensureFilter('keys');
            if (newState.keys?.data) {
              newState.keys.data = newState.keys.data.filter(k => k.value !== chip.value);
              newState.keys.active = newState.keys.data.length > 0;
            }
            newState.keys.selected = (newState.keys.selected || []).filter(k => !matchChip(k));
            newState.keys.data = (newState.keys.data || []).filter(k => k.value !== chip.value);
            if (!newState.keys.selected.length) newState.keys.selected = [];
            if (!newState.keys.data.length) newState.keys.data = [];
            break;
          case 'bpm':
            if (!newState.bpm) newState.bpm = { filterType: 'exact', exact: null, range: { min: '', max: '' } };
            newState.bpm.exact = null;
            newState.bpm.range = { min: '', max: '' };
            break;
          case 'studios':
            ensureFilter('studios');
            if (newState.studios?.data) {
              newState.studios.data = newState.studios.data.filter(s => s.value !== chip.value);
              newState.studios.active = newState.studios.data.length > 0;
            }
            newState.studios.selected = (newState.studios.selected || []).filter(s => !matchChip(s));
            newState.studios.data = (newState.studios.data || []).filter(s => s.value !== chip.value);
            if (!newState.studios.selected.length) newState.studios.selected = [];
            if (!newState.studios.data.length) newState.studios.data = [];
            break;
          case 'agencies':
            ensureFilter('agencies');
            if (newState.agencies?.data) {
              newState.agencies.data = newState.agencies.data.filter(a => a.value !== chip.value);
              newState.agencies.active = newState.agencies.data.length > 0;
            }
            newState.agencies.selected = (newState.agencies.selected || []).filter(a => !matchChip(a));
            newState.agencies.data = (newState.agencies.data || []).filter(a => a.value !== chip.value);
            if (!newState.agencies.selected.length) newState.agencies.selected = [];
            if (!newState.agencies.data.length) newState.agencies.data = [];
            break;
          case 'clients':
            ensureFilter('clients');
            if (newState.clients?.data) {
              newState.clients.data = newState.clients.data.filter(c => c.value !== chip.value);
              newState.clients.active = newState.clients.data.length > 0;
            }
            newState.clients.selected = (newState.clients.selected || []).filter(c => !matchChip(c));
            newState.clients.data = (newState.clients.data || []).filter(c => c.value !== chip.value);
            if (!newState.clients.selected.length) newState.clients.selected = [];
            if (!newState.clients.data.length) newState.clients.data = [];
            break;
          case 'producers':
            ensureFilter('producers');
            if (newState.producers?.data) {
              newState.producers.data = newState.producers.data.filter(p => p.value !== chip.value);
              newState.producers.active = newState.producers.data.length > 0;
            }
            newState.producers.selected = (newState.producers.selected || []).filter(p => !matchChip(p));
            newState.producers.data = (newState.producers.data || []).filter(p => p.value !== chip.value);
            if (!newState.producers.selected.length) newState.producers.selected = [];
            if (!newState.producers.data.length) newState.producers.data = [];
            break;
          case 'composers':
            ensureFilter('composers');
            if (newState.composers?.data) {
              newState.composers.data = newState.composers.data.filter(c => c.value !== chip.value);
              newState.composers.active = newState.composers.data.length > 0;
            }
            newState.composers.selected = (newState.composers.selected || []).filter(c => !matchChip(c));
            newState.composers.data = (newState.composers.data || []).filter(c => c.value !== chip.value);
            if (!newState.composers.selected.length) newState.composers.selected = [];
            if (!newState.composers.data.length) newState.composers.data = [];
            break;
          case 'creativeDirectors':
            ensureFilter('creativeDirectors');
            if (newState.creativeDirectors?.data) {
              newState.creativeDirectors.data = newState.creativeDirectors.data.filter(cd => cd.value !== chip.value);
              newState.creativeDirectors.active = newState.creativeDirectors.data.length > 0;
            }
            newState.creativeDirectors.selected = (newState.creativeDirectors.selected || []).filter(cd => !matchChip(cd));
            newState.creativeDirectors.data = (newState.creativeDirectors.data || []).filter(cd => cd.value !== chip.value);
            if (!newState.creativeDirectors.selected.length) newState.creativeDirectors.selected = [];
            if (!newState.creativeDirectors.data.length) newState.creativeDirectors.data = [];
            break;
          case 'sessionArtists':
            ensureFilter('sessionArtists');
            if (newState.sessionArtists?.data) {
              newState.sessionArtists.data = newState.sessionArtists.data.filter(sa => sa.value !== chip.value);
              newState.sessionArtists.active = newState.sessionArtists.data.length > 0;
            }
            newState.sessionArtists.selected = (newState.sessionArtists.selected || []).filter(sa => !matchChip(sa));
            newState.sessionArtists.data = (newState.sessionArtists.data || []).filter(sa => sa.value !== chip.value);
            if (!newState.sessionArtists.selected.length) newState.sessionArtists.selected = [];
            if (!newState.sessionArtists.data.length) newState.sessionArtists.data = [];
            break;
          case 'license':
            newState.license = { value: false, active: false };
            break;
          case 'date':
            if (!newState.date) newState.date = { filterType: 'completed', range: { from: null, to: null } };
            newState.date = { ...newState.date, from: null, to: null, active: false };
            if (newState.date?.range) {
              newState.date.range = { from: null, to: null };
            }
            break;
          case 'text':
            newState.text = { ...newState.text, value: '', active: false };
            break;
          default:
            break;
        }
        return newState;
      };
      const newFilterState = removeFromState(this.state.filterState);
      const newInitialFilterState = removeFromState(this.state.initialFilterState);
      this.setState({ filterState: newFilterState, initialFilterState: newInitialFilterState }, () => {
        this.loadData();
      });
    };

    // Handler to toggle a filter chip between include/exclude state
    const handleToggleFilter = (chip) => {
      const newFilterState = JSON.parse(JSON.stringify(this.state.filterState));
      const newInitialFilterState = JSON.parse(JSON.stringify(this.state.initialFilterState));
      
      if (chip.type === 'excludedTags') {
        // Moving from excluded to included
        if (newFilterState.excludedTags?.data) {
          newFilterState.excludedTags.data = newFilterState.excludedTags.data.filter(t => t.value !== chip.value);
          if (newFilterState.excludedTags.data.length === 0) newFilterState.excludedTags.active = false;
        }
        // Add to included tags
        if (!newFilterState.multi) newFilterState.multi = { active: false, data: [], searchOperator: 'OR', value: 'tags' };
        if (!newFilterState.multi.data) newFilterState.multi.data = [];
        newFilterState.multi.data.push({ label: chip.label, value: chip.value });
        newFilterState.multi.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.tags) newInitialFilterState.tags = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.tags.selected) newInitialFilterState.tags.selected = [];
        if (!newInitialFilterState.tags.excluded) newInitialFilterState.tags.excluded = [];
        // Remove from excluded
        newInitialFilterState.tags.excluded = newInitialFilterState.tags.excluded.filter(t => (t.id || t.value) !== chip.value);
        // Add to included
        newInitialFilterState.tags.selected.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'excludedTags2') {
        // Moving from excluded to included
        if (newFilterState.excludedTags2?.data) {
          newFilterState.excludedTags2.data = newFilterState.excludedTags2.data.filter(t => t.value !== chip.value);
          if (newFilterState.excludedTags2.data.length === 0) newFilterState.excludedTags2.active = false;
        }
        // Add to included tags2
        if (!newFilterState.tags2) newFilterState.tags2 = { active: false, data: [], searchOperator: 'OR' };
        if (!newFilterState.tags2.data) newFilterState.tags2.data = [];
        newFilterState.tags2.data.push({ label: chip.label, value: chip.value });
        newFilterState.tags2.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.tags2) newInitialFilterState.tags2 = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.tags2.selected) newInitialFilterState.tags2.selected = [];
        if (!newInitialFilterState.tags2.excluded) newInitialFilterState.tags2.excluded = [];
        // Remove from excluded
        newInitialFilterState.tags2.excluded = newInitialFilterState.tags2.excluded.filter(t => (t.id || t.value) !== chip.value);
        // Add to included
        newInitialFilterState.tags2.selected.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'tags') {
        // Moving from included to excluded
        if (newFilterState.multi?.data) {
          newFilterState.multi.data = newFilterState.multi.data.filter(t => t.value !== chip.value);
          if (newFilterState.multi.data.length === 0) newFilterState.multi.active = false;
        }
        // Add to excluded tags
        if (!newFilterState.excludedTags) newFilterState.excludedTags = { active: false, data: [], searchOperator: 'OR', value: 'tags' };
        if (!newFilterState.excludedTags.data) newFilterState.excludedTags.data = [];
        newFilterState.excludedTags.data.push({ label: chip.label, value: chip.value });
        newFilterState.excludedTags.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.tags) newInitialFilterState.tags = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.tags.selected) newInitialFilterState.tags.selected = [];
        if (!newInitialFilterState.tags.excluded) newInitialFilterState.tags.excluded = [];
        // Remove from included
        newInitialFilterState.tags.selected = newInitialFilterState.tags.selected.filter(t => (t.id || t.value) !== chip.value);
        // Add to excluded
        newInitialFilterState.tags.excluded.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'tags2') {
        // Moving from included to excluded
        if (newFilterState.tags2?.data) {
          newFilterState.tags2.data = newFilterState.tags2.data.filter(t => t.value !== chip.value);
          if (newFilterState.tags2.data.length === 0) newFilterState.tags2.active = false;
        }
        // Add to excluded tags2
        if (!newFilterState.excludedTags2) newFilterState.excludedTags2 = { active: false, data: [], searchOperator: 'OR', value: 'tags' };
        if (!newFilterState.excludedTags2.data) newFilterState.excludedTags2.data = [];
        newFilterState.excludedTags2.data.push({ label: chip.label, value: chip.value });
        newFilterState.excludedTags2.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.tags2) newInitialFilterState.tags2 = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.tags2.selected) newInitialFilterState.tags2.selected = [];
        if (!newInitialFilterState.tags2.excluded) newInitialFilterState.tags2.excluded = [];
        // Remove from included
        newInitialFilterState.tags2.selected = newInitialFilterState.tags2.selected.filter(t => (t.id || t.value) !== chip.value);
        // Add to excluded
        newInitialFilterState.tags2.excluded.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'excludedGenres') {
        // Moving from excluded to included
        if (newFilterState.excludedGenres?.data) {
          newFilterState.excludedGenres.data = newFilterState.excludedGenres.data.filter(g => g.value !== chip.value);
          if (newFilterState.excludedGenres.data.length === 0) newFilterState.excludedGenres.active = false;
        }
        // Add to included genres
        if (!newFilterState.genres) newFilterState.genres = { active: false, data: [], searchOperator: 'OR' };
        if (!newFilterState.genres.data) newFilterState.genres.data = [];
        newFilterState.genres.data.push({ label: chip.label, value: chip.value });
        newFilterState.genres.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.genres) newInitialFilterState.genres = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.genres.selected) newInitialFilterState.genres.selected = [];
        if (!newInitialFilterState.genres.excluded) newInitialFilterState.genres.excluded = [];
        // Remove from excluded - use both id and value to ensure removal
        newInitialFilterState.genres.excluded = newInitialFilterState.genres.excluded.filter(g => 
          (g.id !== chip.value) && (g.value !== chip.value) && (g.name !== chip.label) && (g.label !== chip.label)
        );
        // Add to included
        newInitialFilterState.genres.selected.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'genres') {
        // Moving from included to excluded
        if (newFilterState.genres?.data) {
          newFilterState.genres.data = newFilterState.genres.data.filter(g => g.value !== chip.value);
          if (newFilterState.genres.data.length === 0) newFilterState.genres.active = false;
        }
        // Add to excluded genres
        if (!newFilterState.excludedGenres) newFilterState.excludedGenres = { active: false, data: [], searchOperator: 'OR', value: 'genre' };
        if (!newFilterState.excludedGenres.data) newFilterState.excludedGenres.data = [];
        newFilterState.excludedGenres.data.push({ label: chip.label, value: chip.value });
        newFilterState.excludedGenres.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.genres) newInitialFilterState.genres = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.genres.selected) newInitialFilterState.genres.selected = [];
        if (!newInitialFilterState.genres.excluded) newInitialFilterState.genres.excluded = [];
        // Remove from included
        newInitialFilterState.genres.selected = newInitialFilterState.genres.selected.filter(g => (g.id || g.value) !== chip.value);
        // Add to excluded
        newInitialFilterState.genres.excluded.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'excludedKeys') {
        // Moving from excluded to included
        if (newFilterState.excludedKeys?.data) {
          newFilterState.excludedKeys.data = newFilterState.excludedKeys.data.filter(k => k.value !== chip.value);
          if (newFilterState.excludedKeys.data.length === 0) newFilterState.excludedKeys.active = false;
        }
        // Add to included keys
        if (!newFilterState.keys) newFilterState.keys = { active: false, data: [], searchOperator: 'OR' };
        if (!newFilterState.keys.data) newFilterState.keys.data = [];
        newFilterState.keys.data.push({ label: chip.label, value: chip.value });
        newFilterState.keys.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.keys) newInitialFilterState.keys = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.keys.selected) newInitialFilterState.keys.selected = [];
        if (!newInitialFilterState.keys.excluded) newInitialFilterState.keys.excluded = [];
        // Remove from excluded - use both id and value to ensure removal
        newInitialFilterState.keys.excluded = newInitialFilterState.keys.excluded.filter(k => 
          (k.id !== chip.value) && (k.value !== chip.value) && (k.name !== chip.label) && (k.label !== chip.label)
        );
        // Add to included
        newInitialFilterState.keys.selected.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'keys') {
        // Moving from included to excluded
        if (newFilterState.keys?.data) {
          newFilterState.keys.data = newFilterState.keys.data.filter(k => k.value !== chip.value);
          if (newFilterState.keys.data.length === 0) newFilterState.keys.active = false;
        }
        // Add to excluded keys
        if (!newFilterState.excludedKeys) newFilterState.excludedKeys = { active: false, data: [], searchOperator: 'OR', value: 'key' };
        if (!newFilterState.excludedKeys.data) newFilterState.excludedKeys.data = [];
        newFilterState.excludedKeys.data.push({ label: chip.label, value: chip.value });
        newFilterState.excludedKeys.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.keys) newInitialFilterState.keys = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.keys.selected) newInitialFilterState.keys.selected = [];
        if (!newInitialFilterState.keys.excluded) newInitialFilterState.keys.excluded = [];
        // Remove from included
        newInitialFilterState.keys.selected = newInitialFilterState.keys.selected.filter(k => (k.id || k.value) !== chip.value);
        // Add to excluded
        newInitialFilterState.keys.excluded.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'excludedStudios') {
        // Moving from excluded to included
        if (newFilterState.excludedStudios?.data) {
          newFilterState.excludedStudios.data = newFilterState.excludedStudios.data.filter(s => s.value !== chip.value);
          if (newFilterState.excludedStudios.data.length === 0) newFilterState.excludedStudios.active = false;
        }
        // Add to included studios
        if (!newFilterState.studios) newFilterState.studios = { active: false, data: [], searchOperator: 'OR' };
        if (!newFilterState.studios.data) newFilterState.studios.data = [];
        newFilterState.studios.data.push({ label: chip.label, value: chip.value });
        newFilterState.studios.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.studios) newInitialFilterState.studios = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.studios.selected) newInitialFilterState.studios.selected = [];
        if (!newInitialFilterState.studios.excluded) newInitialFilterState.studios.excluded = [];
        // Remove from excluded - use both id and value to ensure removal
        newInitialFilterState.studios.excluded = newInitialFilterState.studios.excluded.filter(s => 
          (s.id !== chip.value) && (s.value !== chip.value) && (s.name !== chip.label) && (s.label !== chip.label)
        );
        // Add to included
        newInitialFilterState.studios.selected.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'studios') {
        // Moving from included to excluded
        if (newFilterState.studios?.data) {
          newFilterState.studios.data = newFilterState.studios.data.filter(s => s.value !== chip.value);
          if (newFilterState.studios.data.length === 0) newFilterState.studios.active = false;
        }
        // Add to excluded studios
        if (!newFilterState.excludedStudios) newFilterState.excludedStudios = { active: false, data: [], searchOperator: 'OR', value: 'studio' };
        if (!newFilterState.excludedStudios.data) newFilterState.excludedStudios.data = [];
        newFilterState.excludedStudios.data.push({ label: chip.label, value: chip.value });
        newFilterState.excludedStudios.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.studios) newInitialFilterState.studios = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.studios.selected) newInitialFilterState.studios.selected = [];
        if (!newInitialFilterState.studios.excluded) newInitialFilterState.studios.excluded = [];
        // Remove from included
        newInitialFilterState.studios.selected = newInitialFilterState.studios.selected.filter(s => (s.id || s.value) !== chip.value);
        // Add to excluded
        newInitialFilterState.studios.excluded.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'excludedAgencies') {
        // Moving from excluded to included
        if (newFilterState.excludedAgencies?.data) {
          newFilterState.excludedAgencies.data = newFilterState.excludedAgencies.data.filter(a => a.value !== chip.value);
          if (newFilterState.excludedAgencies.data.length === 0) newFilterState.excludedAgencies.active = false;
        }
        // Add to included agencies
        if (!newFilterState.agencies) newFilterState.agencies = { active: false, data: [], searchOperator: 'OR' };
        if (!newFilterState.agencies.data) newFilterState.agencies.data = [];
        newFilterState.agencies.data.push({ label: chip.label, value: chip.value });
        newFilterState.agencies.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.agencies) newInitialFilterState.agencies = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.agencies.selected) newInitialFilterState.agencies.selected = [];
        if (!newInitialFilterState.agencies.excluded) newInitialFilterState.agencies.excluded = [];
        // Remove from excluded - use both id and value to ensure removal
        newInitialFilterState.agencies.excluded = newInitialFilterState.agencies.excluded.filter(a => 
          (a.id !== chip.value) && (a.value !== chip.value) && (a.name !== chip.label) && (a.label !== chip.label)
        );
        // Add to included
        newInitialFilterState.agencies.selected.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'agencies') {
        // Moving from included to excluded
        if (newFilterState.agencies?.data) {
          newFilterState.agencies.data = newFilterState.agencies.data.filter(a => a.value !== chip.value);
          if (newFilterState.agencies.data.length === 0) newFilterState.agencies.active = false;
        }
        // Add to excluded agencies
        if (!newFilterState.excludedAgencies) newFilterState.excludedAgencies = { active: false, data: [], searchOperator: 'OR', value: 'agency' };
        if (!newFilterState.excludedAgencies.data) newFilterState.excludedAgencies.data = [];
        newFilterState.excludedAgencies.data.push({ label: chip.label, value: chip.value });
        newFilterState.excludedAgencies.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.agencies) newInitialFilterState.agencies = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.agencies.selected) newInitialFilterState.agencies.selected = [];
        if (!newInitialFilterState.agencies.excluded) newInitialFilterState.agencies.excluded = [];
        // Remove from included
        newInitialFilterState.agencies.selected = newInitialFilterState.agencies.selected.filter(a => (a.id || a.value) !== chip.value);
        // Add to excluded
        newInitialFilterState.agencies.excluded.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'excludedClients') {
        // Moving from excluded to included
        if (newFilterState.excludedClients?.data) {
          newFilterState.excludedClients.data = newFilterState.excludedClients.data.filter(c => c.value !== chip.value);
          if (newFilterState.excludedClients.data.length === 0) newFilterState.excludedClients.active = false;
        }
        // Add to included clients
        if (!newFilterState.clients) newFilterState.clients = { active: false, data: [], searchOperator: 'OR' };
        if (!newFilterState.clients.data) newFilterState.clients.data = [];
        newFilterState.clients.data.push({ label: chip.label, value: chip.value });
        newFilterState.clients.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.clients) newInitialFilterState.clients = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.clients.selected) newInitialFilterState.clients.selected = [];
        if (!newInitialFilterState.clients.excluded) newInitialFilterState.clients.excluded = [];
        // Remove from excluded - use both id and value to ensure removal
        newInitialFilterState.clients.excluded = newInitialFilterState.clients.excluded.filter(c => 
          (c.id !== chip.value) && (c.value !== chip.value) && (c.name !== chip.label) && (c.label !== chip.label)
        );
        // Add to included
        newInitialFilterState.clients.selected.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'clients') {
        // Moving from included to excluded
        if (newFilterState.clients?.data) {
          newFilterState.clients.data = newFilterState.clients.data.filter(c => c.value !== chip.value);
          if (newFilterState.clients.data.length === 0) newFilterState.clients.active = false;
        }
        // Add to excluded clients
        if (!newFilterState.excludedClients) newFilterState.excludedClients = { active: false, data: [], searchOperator: 'OR', value: 'client' };
        if (!newFilterState.excludedClients.data) newFilterState.excludedClients.data = [];
        newFilterState.excludedClients.data.push({ label: chip.label, value: chip.value });
        newFilterState.excludedClients.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.clients) newInitialFilterState.clients = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.clients.selected) newInitialFilterState.clients.selected = [];
        if (!newInitialFilterState.clients.excluded) newInitialFilterState.clients.excluded = [];
        // Remove from included
        newInitialFilterState.clients.selected = newInitialFilterState.clients.selected.filter(c => (c.id || c.value) !== chip.value);
        // Add to excluded
        newInitialFilterState.clients.excluded.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'excludedProducers') {
        // Moving from excluded to included
        if (newFilterState.excludedProducers?.data) {
          newFilterState.excludedProducers.data = newFilterState.excludedProducers.data.filter(p => p.value !== chip.value);
          if (newFilterState.excludedProducers.data.length === 0) newFilterState.excludedProducers.active = false;
        }
        // Add to included producers
        if (!newFilterState.producers) newFilterState.producers = { active: false, data: [], searchOperator: 'OR' };
        if (!newFilterState.producers.data) newFilterState.producers.data = [];
        newFilterState.producers.data.push({ label: chip.label, value: chip.value });
        newFilterState.producers.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.producers) newInitialFilterState.producers = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.producers.selected) newInitialFilterState.producers.selected = [];
        if (!newInitialFilterState.producers.excluded) newInitialFilterState.producers.excluded = [];
        // Remove from excluded - use both id and value to ensure removal
        newInitialFilterState.producers.excluded = newInitialFilterState.producers.excluded.filter(p => 
          (p.id !== chip.value) && (p.value !== chip.value) && (p.name !== chip.label) && (p.label !== chip.label)
        );
        // Add to included
        newInitialFilterState.producers.selected.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'producers') {
        // Moving from included to excluded
        if (newFilterState.producers?.data) {
          newFilterState.producers.data = newFilterState.producers.data.filter(p => p.value !== chip.value);
          if (newFilterState.producers.data.length === 0) newFilterState.producers.active = false;
        }
        // Add to excluded producers
        if (!newFilterState.excludedProducers) newFilterState.excludedProducers = { active: false, data: [], searchOperator: 'OR', value: 'producer' };
        if (!newFilterState.excludedProducers.data) newFilterState.excludedProducers.data = [];
        newFilterState.excludedProducers.data.push({ label: chip.label, value: chip.value });
        newFilterState.excludedProducers.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.producers) newInitialFilterState.producers = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.producers.selected) newInitialFilterState.producers.selected = [];
        if (!newInitialFilterState.producers.excluded) newInitialFilterState.producers.excluded = [];
        // Remove from included
        newInitialFilterState.producers.selected = newInitialFilterState.producers.selected.filter(p => (p.id || p.value) !== chip.value);
        // Add to excluded
        newInitialFilterState.producers.excluded.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'excludedComposers') {
        // Moving from excluded to included
        if (newFilterState.excludedComposers?.data) {
          newFilterState.excludedComposers.data = newFilterState.excludedComposers.data.filter(c => c.value !== chip.value);
          if (newFilterState.excludedComposers.data.length === 0) newFilterState.excludedComposers.active = false;
        }
        // Add to included composers
        if (!newFilterState.composers) newFilterState.composers = { active: false, data: [], searchOperator: 'OR' };
        if (!newFilterState.composers.data) newFilterState.composers.data = [];
        newFilterState.composers.data.push({ label: chip.label, value: chip.value });
        newFilterState.composers.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.composers) newInitialFilterState.composers = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.composers.selected) newInitialFilterState.composers.selected = [];
        if (!newInitialFilterState.composers.excluded) newInitialFilterState.composers.excluded = [];
        // Remove from excluded - use both id and value to ensure removal
        newInitialFilterState.composers.excluded = newInitialFilterState.composers.excluded.filter(c => 
          (c.id !== chip.value) && (c.value !== chip.value) && (c.name !== chip.label) && (c.label !== chip.label)
        );
        // Add to included
        newInitialFilterState.composers.selected.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'composers') {
        // Moving from included to excluded
        if (newFilterState.composers?.data) {
          newFilterState.composers.data = newFilterState.composers.data.filter(c => c.value !== chip.value);
          if (newFilterState.composers.data.length === 0) newFilterState.composers.active = false;
        }
        // Add to excluded composers
        if (!newFilterState.excludedComposers) newFilterState.excludedComposers = { active: false, data: [], searchOperator: 'OR', value: 'composer' };
        if (!newFilterState.excludedComposers.data) newFilterState.excludedComposers.data = [];
        newFilterState.excludedComposers.data.push({ label: chip.label, value: chip.value });
        newFilterState.excludedComposers.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.composers) newInitialFilterState.composers = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.composers.selected) newInitialFilterState.composers.selected = [];
        if (!newInitialFilterState.composers.excluded) newInitialFilterState.composers.excluded = [];
        // Remove from included
        newInitialFilterState.composers.selected = newInitialFilterState.composers.selected.filter(c => (c.id || c.value) !== chip.value);
        // Add to excluded
        newInitialFilterState.composers.excluded.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'excludedCreativeDirectors') {
        // Moving from excluded to included
        if (newFilterState.excludedCreativeDirectors?.data) {
          newFilterState.excludedCreativeDirectors.data = newFilterState.excludedCreativeDirectors.data.filter(cd => cd.value !== chip.value);
          if (newFilterState.excludedCreativeDirectors.data.length === 0) newFilterState.excludedCreativeDirectors.active = false;
        }
        // Add to included creative directors
        if (!newFilterState.creativeDirectors) newFilterState.creativeDirectors = { active: false, data: [], searchOperator: 'OR' };
        if (!newFilterState.creativeDirectors.data) newFilterState.creativeDirectors.data = [];
        newFilterState.creativeDirectors.data.push({ label: chip.label, value: chip.value });
        newFilterState.creativeDirectors.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.creativeDirectors) newInitialFilterState.creativeDirectors = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.creativeDirectors.selected) newInitialFilterState.creativeDirectors.selected = [];
        if (!newInitialFilterState.creativeDirectors.excluded) newInitialFilterState.creativeDirectors.excluded = [];
        // Remove from excluded - use both id and value to ensure removal
        newInitialFilterState.creativeDirectors.excluded = newInitialFilterState.creativeDirectors.excluded.filter(cd => 
          (cd.id !== chip.value) && (cd.value !== chip.value) && (cd.name !== chip.label) && (cd.label !== chip.label)
        );
        // Add to included
        newInitialFilterState.creativeDirectors.selected.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'creativeDirectors') {
        // Moving from included to excluded
        if (newFilterState.creativeDirectors?.data) {
          newFilterState.creativeDirectors.data = newFilterState.creativeDirectors.data.filter(cd => cd.value !== chip.value);
          if (newFilterState.creativeDirectors.data.length === 0) newFilterState.creativeDirectors.active = false;
        }
        // Add to excluded creative directors
        if (!newFilterState.excludedCreativeDirectors) newFilterState.excludedCreativeDirectors = { active: false, data: [], searchOperator: 'OR', value: 'creativeDirector' };
        if (!newFilterState.excludedCreativeDirectors.data) newFilterState.excludedCreativeDirectors.data = [];
        newFilterState.excludedCreativeDirectors.data.push({ label: chip.label, value: chip.value });
        newFilterState.excludedCreativeDirectors.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.creativeDirectors) newInitialFilterState.creativeDirectors = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.creativeDirectors.selected) newInitialFilterState.creativeDirectors.selected = [];
        if (!newInitialFilterState.creativeDirectors.excluded) newInitialFilterState.creativeDirectors.excluded = [];
        // Remove from included
        newInitialFilterState.creativeDirectors.selected = newInitialFilterState.creativeDirectors.selected.filter(cd => (cd.id || cd.value) !== chip.value);
        // Add to excluded
        newInitialFilterState.creativeDirectors.excluded.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'excludedSessionArtists') {
        // Moving from excluded to included
        if (newFilterState.excludedSessionArtists?.data) {
          newFilterState.excludedSessionArtists.data = newFilterState.excludedSessionArtists.data.filter(sa => sa.value !== chip.value);
          if (newFilterState.excludedSessionArtists.data.length === 0) newFilterState.excludedSessionArtists.active = false;
        }
        // Add to included session artists
        if (!newFilterState.sessionArtists) newFilterState.sessionArtists = { active: false, data: [], searchOperator: 'OR' };
        if (!newFilterState.sessionArtists.data) newFilterState.sessionArtists.data = [];
        newFilterState.sessionArtists.data.push({ label: chip.label, value: chip.value });
        newFilterState.sessionArtists.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.sessionArtists) newInitialFilterState.sessionArtists = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.sessionArtists.selected) newInitialFilterState.sessionArtists.selected = [];
        if (!newInitialFilterState.sessionArtists.excluded) newInitialFilterState.sessionArtists.excluded = [];
        // Remove from excluded - use both id and value to ensure removal
        newInitialFilterState.sessionArtists.excluded = newInitialFilterState.sessionArtists.excluded.filter(sa => 
          (sa.id !== chip.value) && (sa.value !== chip.value) && (sa.name !== chip.label) && (sa.label !== chip.label)
        );
        // Add to included
        newInitialFilterState.sessionArtists.selected.push({ id: chip.value, name: chip.label, label: chip.label });
      } else if (chip.type === 'sessionArtists') {
        // Moving from included to excluded
        if (newFilterState.sessionArtists?.data) {
          newFilterState.sessionArtists.data = newFilterState.sessionArtists.data.filter(sa => sa.value !== chip.value);
          if (newFilterState.sessionArtists.data.length === 0) newFilterState.sessionArtists.active = false;
        }
        // Add to excluded session artists
        if (!newFilterState.excludedSessionArtists) newFilterState.excludedSessionArtists = { active: false, data: [], searchOperator: 'OR', value: 'sessionArtist' };
        if (!newFilterState.excludedSessionArtists.data) newFilterState.excludedSessionArtists.data = [];
        newFilterState.excludedSessionArtists.data.push({ label: chip.label, value: chip.value });
        newFilterState.excludedSessionArtists.active = true;
        
        // Update initialFilterState structure for TestMUITags
        if (!newInitialFilterState.sessionArtists) newInitialFilterState.sessionArtists = { selected: [], excluded: [], logic: 'or' };
        if (!newInitialFilterState.sessionArtists.selected) newInitialFilterState.sessionArtists.selected = [];
        if (!newInitialFilterState.sessionArtists.excluded) newInitialFilterState.sessionArtists.excluded = [];
        // Remove from included
        newInitialFilterState.sessionArtists.selected = newInitialFilterState.sessionArtists.selected.filter(sa => (sa.id || sa.value) !== chip.value);
        // Add to excluded
        newInitialFilterState.sessionArtists.excluded.push({ id: chip.value, name: chip.label, label: chip.label });
      }
      
      
      this.setState({ filterState: newFilterState, initialFilterState: newInitialFilterState }, () => {
        this.loadData();
      });
    };

    // Handler to clear all filters (reset to default/empty)
    const handleClearAllFilters = () => {
      // Create empty filters that explicitly clear all excluded properties
      const emptyFilters = {
        tags: { selected: [], excluded: [], logic: 'or', data: [] },
        multi: { active: false, data: [] },
        genres: { selected: [], excluded: [], logic: 'or', data: [] },
        keys: { selected: [], excluded: [], logic: 'or', data: [] },
        bpm: { filterType: 'exact', exact: null, range: { min: '', max: '' }, active: false },
        date: { filterType: 'completed', range: { from: null, to: null }, active: false },
        composers: { selected: [], excluded: [], logic: 'or', data: [] },
        creativeDirectors: { selected: [], excluded: [], logic: 'or', data: [] },
        sessionArtists: { selected: [], excluded: [], logic: 'or', data: [] },
        agencies: { selected: [], excluded: [], logic: 'or', data: [] },
        clients: { selected: [], excluded: [], logic: 'or', data: [] },
        studios: { selected: [], excluded: [], logic: 'or', data: [] },
        producers: { selected: [], excluded: [], logic: 'or', data: [] },
        license: { value: undefined, active: false },
        text: { value: '', active: false },
        tags2: { selected: [], excluded: [], logic: 'or', data: [] },
        // Add excluded filter properties explicitly set to inactive
        excludedTags: { active: false, data: [] },
        excludedTags2: { active: false, data: [] },
        excludedGenres: { active: false, data: [] },
        excludedKeys: { active: false, data: [] },
        excludedStudios: { active: false, data: [] },
        excludedAgencies: { active: false, data: [] },
        excludedClients: { active: false, data: [] },
        excludedProducers: { active: false, data: [] },
        excludedComposers: { active: false, data: [] },
        excludedCreativeDirectors: { active: false, data: [] },
        excludedSessionArtists: { active: false, data: [] }
      };
      
      // Reset mnemonic override when clearing all filters
      console.log('🎵 Clearing all filters via button - resetting mnemonic override to default state');
      
      // Set state FIRST to ensure TestMUITags gets the empty state before the store is cleared
      this.setState({ 
        filterState: emptyFilters, 
        initialFilterState: emptyFilters,
        showMnemonicsOverride: false, // Reset to default filtering behavior
        hasMnemonicMatches: false, // Reset mnemonic matches state
        showLicensedOverride: false, // Reset to default filtering behavior
        hasLicensedMatches: false // Reset licensed matches state
      }, () => {
        // Clear excluded chips from the store AFTER state update
        useExcludedChipsStore.getState().clearAllExcludedChips();
      });
    };

    const { toast } = this.state;

    if (!columnsVisible) return <Loader />;

    return (
      <div className="flex flex-col items-start md:flex-row">
        {/* Pending Licenses Modal - shown when client org has unclaimed licenses */}
        <PendingLicensesModal firebase={this.props.firebase} />

        {/* Pending License Requests Modal - shown when artist org has incoming requests */}
        <PendingLicenseRequestsModal firebase={this.props.firebase} />

        {/* Expired Licenses Modal - shown when client org has expired/revoked licenses */}
        <ExpiredLicensesModal firebase={this.props.firebase} />

        {/* Expiring Licenses Modal - shown when a license is approaching expiry within the org's lead-time window */}
        <ExpiringLicensesModal firebase={this.props.firebase} />

        {/* Exclusive License Notification Modal - shown when artist org has exclusive licenses that ended */}
        <ExclusiveLicenseNotificationModal firebase={this.props.firebase} />

        {/* License Claimed Modal - shown when a pending license the artist issued has been claimed */}
        <LicenseClaimedModal firebase={this.props.firebase} />

        {/* Toast Notification */}
        {toast.show && (
          <div className="toast-container">
            <div className="alert" style={{ backgroundColor: toast.type === 'error' ? 'var(--button-danger)' : 'var(--button-success)', color: '#fff', border: 'none' }}>
              <strong>{toast.message}</strong>
            </div>
          </div>
        )}
        {/* Mobile backdrop */}
        {isSidebarOpen && <div className={styles.railBackdrop} onClick={this.toggleSidebar} />}

        {/* Filter Rail */}
        <div
          className={`${styles.filterRail} ${isSidebarOpen ? styles.filterRailExpanded : styles.filterRailCollapsed}`}
          onClick={() => { if (!isSidebarOpen) this.toggleSidebar(); }}
        >
          {/* Toggle button pinned at top */}
          <div style={{
            display: 'flex', justifyContent: isSidebarOpen ? 'flex-end' : 'center',
            alignItems: 'center', padding: '8px 8px 4px',
            flexShrink: 0,
          }}>
            <button
              className={styles.railToggle}
              onClick={(e) => { e.stopPropagation(); this.toggleSidebar(); }}
              title={isSidebarOpen ? 'Collapse filters' : 'Expand filters'}
              style={{ margin: 0 }}
            >
              <i className={`fas ${isSidebarOpen ? 'fa-chevron-left' : 'fa-chevron-right'}`}></i>
            </button>
          </div>

          {/* Filter content — always rendered, CSS clips when collapsed */}
          <div className={styles.railContent}>
            <TestMUITags
              firebase={this.props.firebase}
              onFiltersChange={this.handleMultiFilterChange}
              initialFilterState={initialFilterState}
              testDirectFilters={this.testDirectFilters}
              onMnemonicReset={this.resetMnemonicsOverride}
              onLicensedReset={this.resetLicensedOverride}
              mode={this.props.mode}
            />
          </div>
        </div>

        {/* Main Content Area */}
        <div
          className="transition-all duration-300 main-content"
          style={{
            overflow: 'hidden',
            paddingLeft: '16px',
            paddingRight: '15px',
            display: 'flex',
            flexDirection: 'column',
            height: '89vh',
            minHeight: 0,
            flex: 1,
            minWidth: 0,
          }}
        >
          <div className="pl-2 pt-8 mb-2">
            {/* Mobile filter button */}
            <button
              className={styles.mobileFilterBtn}
              onClick={this.toggleSidebar}
              style={{
                padding: '6px 12px', borderRadius: '6px', marginBottom: '8px',
                border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)',
                color: '#fff', fontSize: '0.8rem', cursor: 'pointer',
                alignItems: 'center', gap: '6px',
              }}
            >
              <i className="fas fa-filter" style={{ marginRight: '6px', color: 'var(--accent-1-color)' }}></i>
              Filters
            </button>
            <PlaylistsToggle
              tracks={displayedTracks}
              extraControls={
                <GroupBySelector
                  viewMode={this.state.viewMode}
                  groupByField={this.state.groupByField}
                  onChange={(val) => {
                    if (val === 'none') {
                      this.setState({ viewMode: 'flat', groupByField: 'client' });
                    } else {
                      this.setState({ viewMode: 'grouped', groupByField: val });
                    }
                  }}
                />
              }
            >
              <h1 className="text-uppercase text-2xl font-bold text-gray-800 mb-0" style={{ marginBottom: 0 }}>
                {this.props.mode === 'public' ? 'Public Library' : 'Library'}
              </h1>
            </PlaylistsToggle>
          </div>
          {/* Playlist row will appear below if open (handled inside PlaylistsToggle) */}
          {/* ...rest of the content (playlist filter indicator, table, etc.)... */}
          {/* Playlist filter indicator */}
          {(this.props.selectedPlaylist || this.props.loadingPlaylists) && (
            <div style={{ color: 'var(--accent-1-color)', fontWeight: 'bold', marginBottom: 8, paddingLeft: '8px', display: 'flex', alignItems: 'center' }}>
              {this.props.loadingPlaylists && !this.props.selectedPlaylist ? (
                'Loading playlists...'
              ) : (
                <>
                  <span style={{ fontSize: '1.25rem', fontWeight: 300, marginRight: 8 }}>
                    {this.state.playlistFilterName}
                    {this.props.selectedPlaylist && this.props.playlistTrackIds && Array.isArray(this.props.playlistTrackIds[this.props.selectedPlaylist])
                      ? ` (${this.props.playlistTrackIds[this.props.selectedPlaylist].length} track${this.props.playlistTrackIds[this.props.selectedPlaylist].length === 1 ? '' : 's'})`
                      : ''}
                  </span>
                  <button
                    aria-label="Clear selected playlist"
                    onClick={() => {
                      this.props.selectPlaylist && this.props.selectPlaylist(null);
                      this.filterByPlaylist(null);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#fff',
                      fontSize: 20,
                      marginLeft: 8,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      padding: 0,
                      height: 32
                    }}
                    title="Clear selected playlist"
                  >
                    <span style={{ fontSize: 24, lineHeight: 1 }}>×</span>
                  </button>
                </>
              )}
            </div>
          )}
          {/* Active filter chips row */}
          <ActiveFiltersBar
            filterState={filterState}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
            onToggleFilter={handleToggleFilter}
          />
          {/* Loader or Table */}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {loading ? (
              <Loader />
            ) : (
              <div 
                className="table-scroll-container"
                style={{
                  height: '100%',
                  position: 'relative',
                  width: '100%',
                  overflow: 'hidden'
                }}
              >
                <AudioList
                  columnsVisible={columnsVisible}
                  deleteTrack={this.deleteTrack}
                  editTrack={this.editTrack}
                  tracks={displayedTracks}
                  allFilteredTracks={this.state.tracks}
                  orderBy={this.orderBy}
                  currentOrderBy={currentOrderBy}
                  updateTrackLicense={this.updateTrackLicense}
                  updateTrackFields={this.updateTrackFields}
                  onScroll={this.handleScroll}
                  selectedPlaylist={this.props.selectedPlaylist}
                  columnFilterChanged={this.columnFilterChanged}
                  selectedColumns={this.state.selectedColumns}
                  setSelectedColumns={this.setSelectedColumns}
                  onSaveColumnSelection={this.handleSaveColumnSelection}
                  onRecallColumnSelection={this.handleRecallColumnSelection}
                  firebase={this.props.firebase}
                  showMnemonicsOverride={this.state.showMnemonicsOverride}
                  onShowMnemonics={this.showMnemonics}
                  hasMnemonicMatches={this.hasMnemonicMatches()}
                  showLicensedOverride={this.state.showLicensedOverride}
                  onShowLicensed={this.showLicensed}
                  hasLicensedMatches={this.hasLicensedMatches()}
                  mode={this.props.mode}
                  viewMode={this.state.viewMode}
                  groupByField={this.state.groupByField}
                />
                {loadingMore && (
                  <div className="text-center py-4">
                    <Loader />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Chatbot feature */}
        <Chatbot onApplyFilters={this.filterChanged} useSimplePrompts={false} />
      </div>
    );
  }

  setSelectedColumns = (arr) => {
    this.setState({
      selectedColumns: arr,
      columnsVisible: this.columnsArrayToVisible(arr)
    });
  };
}

const AudioList = ({
  tracks,
  allFilteredTracks,
  deleteTrack,
  editTrack,
  columnsVisible,
  orderBy,
  currentOrderBy,
  updateTrackLicense,
  updateTrackFields,
  onScroll,
  selectedPlaylist,
  columnFilterChanged,
  selectedColumns,
  setSelectedColumns,
  onSaveColumnSelection,
  onRecallColumnSelection,
  firebase,
  showMnemonicsOverride,
  onShowMnemonics,
  hasMnemonicMatches,
  showLicensedOverride,
  onShowLicensed,
  hasLicensedMatches,
  mode,
  viewMode,
  groupByField
}) => {
  const isPublicMode = mode === 'public';
  const [activeTrack, setActiveTrack] = useState(null);
  const [lastActiveTrack, setLastActiveTrack] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null); // single expanded group key (accordion)
  const [groupChunkSize, setGroupChunkSize] = useState(50); // chunk pagination within expanded group
  const [groupSortAsc, setGroupSortAsc] = useState(true); // group sort direction
  const [groupStickyTop, setGroupStickyTop] = useState(58); // dynamic sticky top offset
  const groupEndSentinelRef = useRef(null);
  const groupHeaderRef = useRef(null);

  // Reset expanded group when groupByField or viewMode changes
  useEffect(() => {
    setExpandedGroup(null);
    setGroupChunkSize(50);
    setGroupSortAsc(true);
  }, [groupByField, viewMode]);

  // Scroll listener to push sticky group header up with the last track
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !expandedGroup) return;

    setGroupStickyTop(58);

    const handleScroll = () => {
      const sentinel = groupEndSentinelRef.current;
      const header = groupHeaderRef.current;
      if (!sentinel || !header) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const sentinelRect = sentinel.getBoundingClientRect();
      const headerHeight = header.offsetHeight;
      const normalTop = 58; // default sticky offset below table header

      // How far the sentinel is from where the bottom of the header would be
      const sentinelRelative = sentinelRect.top - containerRect.top - normalTop;

      if (sentinelRelative < headerHeight) {
        // Sentinel is close — push header up so its bottom meets the sentinel
        const newTop = Math.round(normalTop - (headerHeight - sentinelRelative));
        setGroupStickyTop(prev => prev !== newTop ? newTop : prev);
      } else {
        setGroupStickyTop(prev => prev !== normalTop ? normalTop : prev);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [expandedGroup, groupChunkSize]);
  const GROUP_CHUNK = 50;

  // Compute grouped data from all filtered tracks (metadata only, no DOM cost)
  const groupedData = useMemo(() => {
    if (viewMode !== 'grouped') return null;
    const source = allFilteredTracks || tracks;
    const groupMap = new Map();
    source.forEach(track => {
      const key = getGroupKey(track, groupByField);
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key).push(track);
    });
    // Sort groups alphabetically, keeping 'Uncategorized' at the end
    const sorted = new Map(
      [...groupMap.entries()].sort((a, b) => {
        if (a[0] === 'Uncategorized') return 1;
        if (b[0] === 'Uncategorized') return -1;
        return groupSortAsc ? a[0].localeCompare(b[0]) : b[0].localeCompare(a[0]);
      })
    );
    return sorted;
  }, [viewMode, allFilteredTracks, tracks, groupByField, groupSortAsc]);

  // In grouped mode, only render tracks from the expanded group (chunked).
  // In flat mode, use paginated displayedTracks as before.
  const effectiveTracks = useMemo(() => {
    if (viewMode !== 'grouped' || !groupedData || !expandedGroup) return tracks;
    const groupTracks = groupedData.get(expandedGroup) || [];
    return groupTracks.slice(0, groupChunkSize);
  }, [viewMode, groupedData, expandedGroup, groupChunkSize, tracks]);
  const activeRowRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const scrollLeftRef = useRef(0);
  const { focusedPlaylists, addTrackToPlaylist, playlistTrackIds, removeTrackFromPlaylist } = usePlaylists();
  // Ref to store the next track to autoplay after removal
  const nextTrackToAutoplayRef = useRef(null);
  // Setter to allow TableRows to set the next track to autoplay
  const setNextTrackToAutoplayRef = (trackId) => {
    nextTrackToAutoplayRef.current = trackId;
  };
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);

  // Grouped view helpers — accordion: only one group open at a time
  const toggleGroup = useCallback((groupKey) => {
    setExpandedGroup(prev => {
      if (prev === groupKey) return null; // collapse if already open
      setGroupChunkSize(GROUP_CHUNK); // reset chunk size for new group
      return groupKey;
    });
  }, []);

  // Add function to focus the track table
  const focusTrackTable = useCallback(() => {
    if (scrollContainerRef.current) {
      // Focus the table without scrolling it into view
      scrollContainerRef.current.focus({ preventScroll: true });
      // Ensure the table is ready for keyboard navigation
      if (!activeTrack && effectiveTracks.length > 0) {
        // Set lastActiveTrack but don't set activeTrack to prevent auto-play
        setLastActiveTrack(effectiveTracks[0].id);
      }
    }
  }, [activeTrack, effectiveTracks]);

  // Save scroll position in a ref before sorting
  const handleOrderBy = (data) => {
    if (scrollContainerRef.current) {
      scrollLeftRef.current = scrollContainerRef.current.scrollLeft;
    }
    orderBy(data);
  };

  // Restore scroll position after tracks change, with a delay
  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = scrollLeftRef.current;
        }
      }, 50);
    }
  }, [tracks]);

  // Add click handler for the main content area
  useEffect(() => {
    const handleClick = (event) => {
      // Check if the click is outside of filter elements
      const isFilterElement = event.target.closest('.MuiAutocomplete-root') || 
                             event.target.closest('.MuiFormControl-root') ||
                             event.target.closest('.MuiButton-root') ||
                             event.target.closest('.MuiPaper-root') ||
                             event.target.closest('.MuiPopover-root') ||
                             event.target.closest('.MuiDialog-root') ||
                             event.target.closest('.column-visibility-popover') ||
                             event.target.closest('.column-visibility-autocomplete');
      
      // Only focus the table if clicking on the table container itself
      const isTableContainer = event.target.closest('.table-scroll');
      
      if (!isFilterElement && isTableContainer) {
        // Focus the table and ensure it's ready for keyboard navigation
        focusTrackTable();
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [focusTrackTable, isColumnSelectorOpen]);

  // Add scroll event handler
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && onScroll) {
      container.addEventListener('scroll', onScroll);
      return () => {
        container.removeEventListener('scroll', onScroll);
      };
    }
  }, [onScroll]);

  // Add function to ensure active row is visible
  const ensureActiveRowVisible = useCallback(() => {
    if (activeRowRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const row = activeRowRef.current;
      
      const containerRect = container.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      
      // Calculate the middle points
      const containerMiddle = containerRect.top + (containerRect.height / 2);
      const rowMiddle = rowRect.top + (rowRect.height / 2);
      
      // Calculate different thresholds for top and bottom
      const topThreshold = containerRect.height / 4; // 25% of container height
      const bottomThreshold = containerRect.height / 6; // Smaller threshold means earlier scrolling
      
      // Calculate the distance of the row's middle point from the container's middle
      const distanceFromMiddle = rowMiddle - containerMiddle;
      
      // Log positions for debugging
      //console.log('Container middle:', containerMiddle);
      //console.log('Row middle:', rowMiddle);
      //console.log('Distance from middle:', distanceFromMiddle);
      //console.log('Top threshold:', topThreshold);
      //console.log('Bottom threshold:', bottomThreshold);
      
      // Use different thresholds based on scroll direction
      const threshold = distanceFromMiddle > 0 ? bottomThreshold : topThreshold;
      
      if (Math.abs(distanceFromMiddle) > threshold) {
        // Calculate scroll amount to maintain the appropriate threshold
        const scrollAmount = distanceFromMiddle - (distanceFromMiddle > 0 ? threshold : -threshold);
        //console.log('Scrolling by:', scrollAmount);
        
        container.scrollBy({
          top: scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  }, []);

  const noDataMessage = (
    <tr>
      <td
        colSpan={columnsVisible.colsSelected}
        className="p-5 text-center text-large"
      >
        No Tracks Found
      </td>
    </tr>
  );

  // Use organization-level role instead of global auth role
  const currentOrgRole = useOrganizationStore((state) => state.currentOrgRole);
  const isSuperAdmin = useOrganizationStore((state) => state.isSuperAdmin);
  const role = isSuperAdmin ? 'ADMIN' : (currentOrgRole || 'USER');

  // Handle keyboard navigation
  const handleGlobalKeyPress = useCallback((event) => {
    // Check if any dropdown is focused
    const activeElement = document.activeElement;
    const isDropdownFocused = activeElement?.classList?.contains('MuiAutocomplete-input') ||
                             activeElement?.closest('.MuiAutocomplete-root') ||
                             activeElement?.closest('.MuiDialog-root');
    // If a dropdown is focused, don't handle keyboard events
    if (isDropdownFocused) return;

    // Prevent spacebar from triggering playback if playlist name input is focused
    if (
      activeElement &&
      activeElement.tagName === 'INPUT' &&
      activeElement.type === 'text' &&
      (activeElement.placeholder === 'Playlist name' || activeElement.classList.contains('playlist-name-input'))
    ) {
      // Let the spacebar work as normal in the playlist name input
      return;
    }

    if (!effectiveTracks || !effectiveTracks.length) return;

    // Prevent horizontal scrolling with arrow keys
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      return;
    }

    const currentIndex = effectiveTracks.findIndex(track => track && track.id === activeTrack);
    // If no active track but we have a last active track, use that as reference point
    const referenceIndex = currentIndex === -1 && lastActiveTrack ?
      effectiveTracks.findIndex(track => track && track.id === lastActiveTrack) :
      currentIndex;

    // In grouped mode, effectiveTracks only contains the expanded group's tracks,
    // so no need to skip collapsed tracks — all tracks in the list are visible.
    const nextIndex = referenceIndex === -1 ? 0 : Math.min(referenceIndex + 1, effectiveTracks.length - 1);
    const prevIndex = referenceIndex <= 0 ? 0 : referenceIndex - 1;

    switch (event.key) {
      case ' ':
        // Skip spacebar handling if in input/textarea
        if (
          document.activeElement.tagName === 'INPUT' ||
          document.activeElement.tagName === 'TEXTAREA' ||
          document.activeElement.isContentEditable
        ) {
          return;
        }
        event.preventDefault();
        if (activeTrack) {
          // When pausing, store the current track
          setLastActiveTrack(activeTrack);
          setActiveTrack(null);
        } else {
          // When resuming, use the last active track if available, otherwise use first track
          if (lastActiveTrack && effectiveTracks.some(t => t.id === lastActiveTrack)) {
            setActiveTrack(lastActiveTrack);
          } else if (effectiveTracks.length > 0) {
            // If no last active track or it's not in current tracks, use first track
            const firstTrack = effectiveTracks[0];
            if (firstTrack && firstTrack.id) {
              setActiveTrack(firstTrack.id);
              setLastActiveTrack(firstTrack.id);
            }
          }
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        // Only move up if we're not at the first item
        if (referenceIndex > 0) {
          const prevTrack = effectiveTracks[prevIndex];
          if (prevTrack && prevTrack.id) {
            setActiveTrack(prevTrack.id);
            setLastActiveTrack(prevTrack.id);
            requestAnimationFrame(ensureActiveRowVisible);
          }
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        const nextTrack = effectiveTracks[nextIndex];
        if (nextTrack && nextTrack.id) {
          setActiveTrack(nextTrack.id);
          setLastActiveTrack(nextTrack.id);
          requestAnimationFrame(ensureActiveRowVisible);
        }
        break;
      case 'Enter':
      case 'Return': {
        if (!activeTrack || !focusedPlaylists || focusedPlaylists.length === 0) return;
        // Check if already in all focused playlists
        const alreadyInAll = focusedPlaylists.every(pid => (playlistTrackIds[pid] || []).includes(activeTrack));
        if (alreadyInAll) return;
        event.preventDefault();
        Promise.all(
          focusedPlaylists
            .filter(pid => !(playlistTrackIds[pid] || []).includes(activeTrack))
            .map(pid => addTrackToPlaylist(pid, activeTrack))
        );
        break;
      }
      case 'Backspace':
      case 'Delete': {
        // Skip if user is typing in an input/textarea
        if (
          document.activeElement.tagName === 'INPUT' ||
          document.activeElement.tagName === 'TEXTAREA' ||
          document.activeElement.isContentEditable
        ) {
          return;
        }
        // Only apply this logic if filtering by playlist
        if (
          activeTrack &&
          selectedPlaylist &&
          playlistTrackIds &&
          Array.isArray(playlistTrackIds[selectedPlaylist]) &&
          playlistTrackIds[selectedPlaylist].includes(activeTrack)
        ) {
          event.preventDefault();
          //console.log('--- Backspace/Delete Handler Start ---');
          //console.log('Active Track:', activeTrack);
          //console.log('Selected Playlist:', selectedPlaylist);
          //console.log('Current visible tracks:', effectiveTracks.map(t => t.id));
          // Find the index of the active track in the visible table
          const currentIndex = effectiveTracks.findIndex(t => t.id === activeTrack);
          //console.log('Current Track Index in visible tracks:', currentIndex);
          let nextTrackId = null;
          if (currentIndex !== -1) {
            // Prefer next track, otherwise previous if at end
            if (currentIndex < effectiveTracks.length - 1) {
              nextTrackId = effectiveTracks[currentIndex + 1].id;
              //console.log('Next visible track ID:', nextTrackId);
            } else if (currentIndex > 0) {
              nextTrackId = effectiveTracks[currentIndex - 1].id;
              //console.log('Previous visible track ID:', nextTrackId);
            }
            if (nextTrackId && nextTrackId !== activeTrack) {
              nextTrackToAutoplayRef.current = nextTrackId;
              //console.log('Stored Next Track in Ref:', nextTrackToAutoplayRef.current);
            }
          } else {
            //console.log('Active track not found in visible tracks');
          }
          //console.log('Removing track from playlist:', activeTrack);
          removeTrackFromPlaylist(selectedPlaylist, activeTrack);
          //console.log('--- Backspace/Delete Handler End ---');
          break;
        }
        break;
      }
      default:
        break;
    }
  }, [effectiveTracks, activeTrack, lastActiveTrack, ensureActiveRowVisible, focusedPlaylists, addTrackToPlaylist, playlistTrackIds, removeTrackFromPlaylist, selectedPlaylist]);

  // Add keyboard event listener
  useEffect(() => {
    window.addEventListener('keydown', handleGlobalKeyPress);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyPress);
    };
  }, [handleGlobalKeyPress]);

  // useEffect to trigger autoplay after removal
  useEffect(() => {
    //console.log('--- Tracks Update Effect Start ---');
    //console.log('Current tracks length:', effectiveTracks.length);
    if (nextTrackToAutoplayRef.current) {
      const nextId = nextTrackToAutoplayRef.current;
      //console.log('Next track to autoplay:', nextId);
      const trackExists = effectiveTracks.some(t => t.id === nextId);
      //console.log('Next track exists in current tracks:', trackExists);
      if (trackExists) {
        //console.log('Setting active track to:', nextId);
        setActiveTrack(nextId);
        setLastActiveTrack(nextId);
      } else {
        //console.log('Next track not found in current tracks list');
      }
      //console.log('Clearing nextTrackToAutoplayRef');
      nextTrackToAutoplayRef.current = null;
    } else {
      //console.log('No next track to autoplay (ref is null)');
    }
    //console.log('--- Tracks Update Effect End ---');
  }, [effectiveTracks]);

  return (
    <div
      ref={scrollContainerRef}
      className="table-scroll"
      style={{
        height: '100%',
        overflowY: 'auto',
        position: 'relative',
        outline: 'none' // Remove focus outline
      }}
      tabIndex={-1} // Make div focusable
      onClick={(e) => {
        e.stopPropagation();
        if (
          isColumnSelectorOpen ||
          e.target.closest('.column-visibility-popover') ||
          e.target.closest('.column-visibility-autocomplete')
        ) {
          return;
        }
        focusTrackTable();
      }}
    >
      <Table className="table-tracks" cellPadding="true">
        <thead>
          <tr>
            {columnsVisible.trackName && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="trackName"
                type="string"
                label="Track Name"
              />
            )}
            {/* Column selector in rightmost cell */}
            <th className="text-right" style={{ minWidth: 'auto', width: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px' }}>
                <ColumnVisibilitySelector
                  columnFilterChanged={columnFilterChanged}
                  isOpen={isColumnSelectorOpen}
                  setIsOpen={setIsColumnSelectorOpen}
                  selectedColumns={selectedColumns}
                  setSelectedColumns={setSelectedColumns}
                  onSave={onSaveColumnSelection}
                  onRecall={onRecallColumnSelection}
                  mode={mode}
                />
              </div>
            </th>
            {columnsVisible.length && (
              <th style={{ minWidth: 60, maxWidth: 100, width: 100, textAlign: 'center', verticalAlign: 'middle' }}>
                <h4 className="mb-0">Length</h4>
              </th>
            )}
            {/* License Status and Public columns - hidden in public mode */}
            {!isPublicMode && columnsVisible.licenseStatus && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="licenseStatus"
                type="licenseStatus"
                label="License Status"
                align="center"
              />
            )}
            {!isPublicMode && columnsVisible.isPublic && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="isPublic"
                type="boolean"
                label="Public"
                align="center"
              />
            )}
            {/* Request and Organization columns - shown only in public mode */}
            {isPublicMode && (
              <th style={{ minWidth: 90, maxWidth: 120, width: 100, textAlign: 'center', verticalAlign: 'middle' }}>
                <h4 className="mb-0">Request</h4>
              </th>
            )}
            {isPublicMode && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="sourceOrgName"
                type="string"
                label="Organization"
              />
            )}
            {columnsVisible.composers && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="composer"
                type="objectArray"
                label="Composers"
              />
            )}
            {columnsVisible.genre && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="genre"
                type="object"
                label="Genre"
              />
            )}
            {columnsVisible.bpm && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="bpm"
                type="object"
                label="BPM"
              />
            )}
            {columnsVisible.key && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="key"
                type="object"
                label="Key"
              />
            )}
            {columnsVisible.jobTitle && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="jobTitle"
                type="string"
                label="Job Title"
              />
            )}
            {columnsVisible.producer && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="producer"
                type="objectArray"
                label="Producer"
              />
            )}
            {columnsVisible.artist && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="artist"
                type="object"
                label="Artist"
              />
            )}
            {columnsVisible.album && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="album"
                type="object"
                label="Album"
              />
            )}
            {columnsVisible.recordLabel && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="recordLabel"
                type="object"
                label="Record Label"
              />
            )}
            {columnsVisible.publisher && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="publisher"
                type="object"
                label="Publisher"
              />
            )}
            {columnsVisible.agency && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="agency"
                type="object"
                label="Agency"
              />
            )}
            {columnsVisible.client && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="client"
                type="object"
                label="Client"
              />
            )}
            {columnsVisible.creativeDirector && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="creativedirector"
                type="objectArray"
                label="Creative Directors"
              />
            )}
            {columnsVisible.dateCompleted && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="dateCompleted"
                type="string"
                label="Date Completed"
              />
            )}
            {columnsVisible.dateExpires && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="dateExpires"
                type="string"
                label="Date Expires"
              />
            )}
            {columnsVisible.flightingPlatform && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="flightingPlatform"
                type="objectArray"
                label="Flighting Platform"
              />
            )}
            {columnsVisible.flightingCode && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="flightingCode"
                type="objectArray"
                label="Flighting Code"
              />
            )}
            {columnsVisible.studio && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="studio"
                type="object"
                label="Studio"
              />
            )}
            {columnsVisible.backupLocation && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="backupLocation"
                type="object"
                label="Backup Location"
              />
            )}
            {columnsVisible.sessionArtists && (
              <TableHeader
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="sessionArtists"
                type="objectArray"
                label="Session Artists"
              />
            )}
            {columnsVisible.trackRating && (
              <TableHeader
                align="right"
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="trackRating"
                type="string"
                label="Track Rating"
              />
            )}
            {columnsVisible.flag && (
              <TableHeader
                align="left"
                orderBy={handleOrderBy}
                sortState={currentOrderBy}
                keyName="needsAttention"
                type="boolean"
                label="Flag"
              />
            )}
          </tr>
        </thead>
        <tbody>
        {viewMode === 'grouped' && groupedData ? (
            <>
              <tr className="group-header-row">
                <td
                  className="group-header-cell"
                  style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 10,
                    padding: '6px 12px',
                    border: 'none',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    backgroundColor: 'inherit',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                      {groupedData.size} group{groupedData.size !== 1 ? 's' : ''}
                    </span>
                    <button
                      onClick={() => setGroupSortAsc(prev => !prev)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontSize: '0.75rem',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'color 0.2s',
                      }}
                      title={groupSortAsc ? 'Sort groups Z-A' : 'Sort groups A-Z'}
                    >
                      <i className={`fas fa-sort-alpha-${groupSortAsc ? 'down' : 'up'}`} style={{ fontSize: '0.7rem' }}></i>
                      {groupSortAsc ? 'A-Z' : 'Z-A'}
                    </button>
                  </div>
                </td>
                {(columnsVisible.colsSelected || 20) > 1 && (
                  <td
                    colSpan={(columnsVisible.colsSelected || 20) - 1}
                    style={{ border: 'none', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', padding: 0 }}
                  />
                )}
              </tr>
              {Array.from(groupedData.entries()).map(([groupKey, groupTracks], groupIndex) => {
                const isExpanded = expandedGroup === groupKey;
                const totalInGroup = groupTracks.length;
                const displayedInGroup = isExpanded ? groupTracks.slice(0, groupChunkSize) : [];
                const hasMoreInGroup = isExpanded && groupChunkSize < totalInGroup;

                return (
                  <React.Fragment key={`group-${groupKey}`}>
                    {/* Group header row */}
                    <tr
                      ref={isExpanded ? groupHeaderRef : undefined}
                      className={`group-header-row${isExpanded ? ' group-header-expanded' : ''}`}
                      onClick={() => toggleGroup(groupKey)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: isExpanded ? '#6b6bff' : 'transparent',
                        borderTop: groupIndex > 0 ? '2px solid rgba(255, 255, 255, 0.15)' : 'none',
                        opacity: (expandedGroup && !isExpanded) ? 0.2 : 1,
                        transition: 'opacity 0.2s ease',
                        ...(isExpanded ? {
                          position: 'sticky',
                          top: groupStickyTop,
                          zIndex: 20,
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                        } : {}),
                      }}
                    >
                      <td
                        className="group-header-cell"
                        style={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 10,
                          padding: '16px 12px',
                          fontWeight: 600,
                          fontSize: '1.1rem',
                          color: '#fff',
                          border: 'none',
                          borderLeft: isExpanded ? '3px solid var(--accent-1-color)' : '3px solid transparent',
                          backgroundColor: 'inherit',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap' }}>
                          <i
                            className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}
                            style={{ fontSize: '0.7rem', width: '12px', color: 'var(--accent-1-color)' }}
                          ></i>
                          <span>{groupKey}</span>
                          <span style={{
                            fontSize: '0.75rem',
                            color: 'rgba(255, 255, 255, 0.5)',
                            fontWeight: 400,
                          }}>
                            {totalInGroup} track{totalInGroup !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </td>
                      {(columnsVisible.colsSelected || 20) > 1 && (
                        <td
                          colSpan={(columnsVisible.colsSelected || 20) - 1}
                          style={{ border: 'none', padding: 0 }}
                        />
                      )}
                    </tr>
                    {/* Track rows for expanded group only */}
                    {isExpanded && (
                      <TableRows
                        tracks={displayedInGroup}
                        columnsVisible={columnsVisible}
                        editTrack={editTrack}
                        deleteTrack={deleteTrack}
                        role={role}
                        updateTrackLicense={updateTrackLicense}
                        updateTrackFields={updateTrackFields}
                        activeTrack={activeTrack}
                        setActiveTrack={setActiveTrack}
                        activeRowRef={activeRowRef}
                        selectedPlaylist={selectedPlaylist}
                        setNextTrackToAutoplayRef={setNextTrackToAutoplayRef}
                        firebase={firebase}
                        mode={mode}
                        grouped
                      />
                    )}
                    {/* Load more button within group */}
                    {hasMoreInGroup && (
                      <tr>
                        <td
                          colSpan={columnsVisible.colsSelected || 20}
                          style={{ border: 'none', padding: '8px 12px', textAlign: 'center' }}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setGroupChunkSize(prev => prev + GROUP_CHUNK);
                            }}
                            style={{
                              background: 'rgba(255, 255, 255, 0.1)',
                              color: 'rgba(255, 255, 255, 0.7)',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px 16px',
                              fontSize: '0.8rem',
                              cursor: 'pointer',
                            }}
                          >
                            Load more ({totalInGroup - groupChunkSize} remaining)
                          </button>
                        </td>
                      </tr>
                    )}
                    {/* Sentinel row to detect end of group for sticky header */}
                    {isExpanded && (
                      <tr ref={groupEndSentinelRef} style={{ height: 0, padding: 0, border: 'none' }}>
                        <td style={{ height: 0, padding: 0, border: 'none' }}></td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              <HiddenTracksButtonRow
                showMnemonicsOverride={showMnemonicsOverride}
                onShowMnemonics={onShowMnemonics}
                hasMnemonicMatches={hasMnemonicMatches}
                showLicensedOverride={showLicensedOverride}
                onShowLicensed={onShowLicensed}
                hasLicensedMatches={hasLicensedMatches}
                columnsVisible={columnsVisible}
              />
            </>
          ) : effectiveTracks.length > 0 ? (
            <>
              <TableRows
                tracks={effectiveTracks}
                columnsVisible={columnsVisible}
                editTrack={editTrack}
                deleteTrack={deleteTrack}
                role={role}
                updateTrackLicense={updateTrackLicense}
                updateTrackFields={updateTrackFields}
                activeTrack={activeTrack}
                setActiveTrack={setActiveTrack}
                activeRowRef={activeRowRef}
                selectedPlaylist={selectedPlaylist}
                setNextTrackToAutoplayRef={setNextTrackToAutoplayRef}
                firebase={firebase}
                mode={mode}
              />
              <HiddenTracksButtonRow
                showMnemonicsOverride={showMnemonicsOverride}
                onShowMnemonics={onShowMnemonics}
                hasMnemonicMatches={hasMnemonicMatches}
                showLicensedOverride={showLicensedOverride}
                onShowLicensed={onShowLicensed}
                hasLicensedMatches={hasLicensedMatches}
                columnsVisible={columnsVisible}
              />
            </>
          ) : (
            <>
              {noDataMessage}
              <HiddenTracksButtonRow
                showMnemonicsOverride={showMnemonicsOverride}
                onShowMnemonics={onShowMnemonics}
                hasMnemonicMatches={hasMnemonicMatches}
                showLicensedOverride={showLicensedOverride}
                onShowLicensed={onShowLicensed}
                hasLicensedMatches={hasLicensedMatches}
                columnsVisible={columnsVisible}
              />
            </>
          )}
        </tbody>
      </Table>
    </div>
  );
};

const TableHeader = ({ orderBy, sortState, keyName, type, label, align }) => {
  const alignClass = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  const isActive = sortState.column === keyName;
  const sortIcon = isActive ? (sortState.ascending ? '↓' : '↑') : '↕';
  
  const changeOrderBy = (event) => {
    event.stopPropagation();
    orderBy({ column: keyName, type });
  };

  return (
    <th className={alignClass}>
      <h4 onClick={changeOrderBy} className="mb-0">
        {label} {sortIcon}
      </h4>
    </th>
  );
};

// Combined component for hidden tracks notification row (mnemonics + licensed)
const HiddenTracksButtonRow = ({
  showMnemonicsOverride, onShowMnemonics, hasMnemonicMatches,
  showLicensedOverride, onShowLicensed, hasLicensedMatches,
  columnsVisible
}) => {
  const { excludeMnemonics, excludeLicensed } = getDisplayPreferences();
  const showMnemonicBtn = !showMnemonicsOverride && excludeMnemonics && hasMnemonicMatches;
  const showLicensedBtn = !showLicensedOverride && excludeLicensed && hasLicensedMatches;

  if (!showMnemonicBtn && !showLicensedBtn) return null;

  const visibleColumnCount = columnsVisible ?
    Object.values(columnsVisible).filter(Boolean).length : 12;

  return (
    <tr>
      <td
        colSpan={visibleColumnCount}
        className="py-3"
        style={{
          borderTop: '2px solid #e9ecef',
          backgroundColor: '#f8f9fa',
          fontFamily: 'inherit'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0 0.5rem' }}>
          <span className="text-muted" style={{ fontSize: '0.8rem' }}>
            One or more tracks are currently hidden. Click to include them in results.
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {showMnemonicBtn && (
              <button
                type="button"
                onClick={onShowMnemonics}
                style={{
                  background: 'var(--button-success)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.25rem 0.75rem',
                  height: '30px',
                  whiteSpace: 'nowrap',
                }}
              >
                Show Mnemonics
              </button>
            )}
            {showLicensedBtn && (
              <button
                type="button"
                onClick={onShowLicensed}
                style={{
                  background: 'var(--button-info)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.25rem 0.75rem',
                  height: '30px',
                  whiteSpace: 'nowrap',
                }}
              >
                Show Licensed
              </button>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

function FindAudioWithPlaylistContext(props) {
  const { selectedPlaylist, playlistTrackIds, playlists, selectPlaylist, loading } = usePlaylists();
  // Use organization-level role instead of global auth role
  const currentOrgRole = useOrganizationStore((state) => state.currentOrgRole);
  const isSuperAdmin = useOrganizationStore((state) => state.isSuperAdmin);
  // Super admins get ADMIN role, otherwise use org role (default to USER if not set)
  const role = isSuperAdmin ? 'ADMIN' : (currentOrgRole || 'USER');
  return <FindAudio {...props} role={role} selectedPlaylist={selectedPlaylist} playlistTrackIds={playlistTrackIds} playlists={playlists} selectPlaylist={selectPlaylist} loadingPlaylists={loading} />;
}

const condition = (authUser) => !!authUser;
export default withAuthorization(condition)(withFirebase(FindAudioWithPlaylistContext));

// Utility to extract a simple filter list from filterState
function extractSimpleFilterList(filterState) {
  const filters = [];
  if (!filterState) return filters;
  // Multi (tags)
  if (filterState.multi?.active && Array.isArray(filterState.multi.data)) {
    filterState.multi.data.forEach(tag => {
      if (tag.label) filters.push(`Tag: ${tag.label}`);
    });
  }
  // Genres
  if (filterState.genres?.active && Array.isArray(filterState.genres.data)) {
    filterState.genres.data.forEach(genre => {
      if (genre.label) filters.push(`Genre: ${genre.label}`);
    });
  }
  // --- Add logic for tags and genres ---
  if (filterState.multi?.searchOperator) {
    filters.push(`Tag logic: ${filterState.multi.searchOperator}`);
  }
  if (filterState.genres?.searchOperator) {
    filters.push(`Genre logic: ${filterState.genres.searchOperator}`);
  }
  // BPM
  if (filterState.bpm?.active) {
    if (filterState.bpm.filterType === 'range' && (filterState.bpm.range?.min || filterState.bpm.range?.max)) {
      const min = filterState.bpm.range?.min || '';
      const max = filterState.bpm.range?.max || '';
      if (min && max) {
        filters.push(`BPM: ${min}-${max}`);
      } else if (min) {
        filters.push(`BPM: ${min}+`);
      } else if (max) {
        filters.push(`BPM: <${max}`);
      }
    } else if (filterState.bpm.exact) {
      filters.push(`BPM: ${filterState.bpm.exact}`);
    }
  }
  // Keys
  if (filterState.keys?.active && Array.isArray(filterState.keys.data)) {
    filterState.keys.data.forEach(key => {
      if (key.label) filters.push(`Key: ${key.label}`);
    });
  }
  // Studios
  if (filterState.studios?.active && Array.isArray(filterState.studios.data)) {
    filterState.studios.data.forEach(studio => {
      if (studio.label) filters.push(`Studio: ${studio.label}`);
    });
  }
  // Agencies
  if (filterState.agencies?.active && Array.isArray(filterState.agencies.data)) {
    filterState.agencies.data.forEach(agency => {
      if (agency.label) filters.push(`Agency: ${agency.label}`);
    });
  }
  // Clients
  if (filterState.clients?.active && Array.isArray(filterState.clients.data)) {
    filterState.clients.data.forEach(client => {
      if (client.label) filters.push(`Client: ${client.label}`);
    });
  }
  // Producers
  if (filterState.producers?.active && Array.isArray(filterState.producers.data)) {
    filterState.producers.data.forEach(producer => {
      if (producer.label) filters.push(`Producer: ${producer.label}`);
    });
  }
  // Composers
  if (filterState.composers?.active && Array.isArray(filterState.composers.data)) {
    filterState.composers.data.forEach(composer => {
      if (composer.label) filters.push(`Composer: ${composer.label}`);
    });
  }
  // License
  if (filterState.license?.active && filterState.license.value !== undefined) {
    filters.push(`License: ${filterState.license.value ? 'Licensed' : 'Unlicensed'}`);
  }
  // Date
  if (filterState.date?.active && filterState.date.from && filterState.date.to) {
    const from = new Date(filterState.date.from).toLocaleDateString();
    const to = new Date(filterState.date.to).toLocaleDateString();
    filters.push(`Date: ${from} to ${to}`);
  }
  // Text search
  if (filterState.text?.active && filterState.text.value) {
    filters.push(`Text: ${filterState.text.value}`);
  }
  return filters;
}
